import React, { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import styled from 'styled-components';
import HeroImage from './HeroImage';
import { HeroData } from '../../../models/HeroData';
import DraftState from '../../../models/DraftState';
import { useDraft } from '../contexts/DraftContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import MergeReplaceDialog from './MergeReplaceDialog';
import ShareLinkManager from '../../../components/ShareLinkManager';
import PracticeMatchup from './PracticeMatchup';
import AllMatchupsView from './AllMatchupsView';
import firebaseService from '../services/FirebaseService';
import supabaseService from '../../../services/SupabaseService';
import UserTierService from '../../../models/UserTier';
import game from '../../../games';
import { captureBoardCanvas, saveCanvasAsJpeg, stackCanvasesVertically, buildExportFileName, waitForNextFrame, waitForImagesLoaded, forceEagerImageLoad, measureContentWidth, measureUnwrappedWidth, wait } from '../../../utils/exportBoardAsJpeg';

// Local string hero ids → OpenDota numeric ids (moved to games/dota in Phase 2.1)
const HERO_ID_MAP = game.heroes.idMap;

// Top hero-pool row: 15 boxes shown by default (manual picking baseline).
// Opp Pool import grows the underlying array beyond this only when a team
// actually has more than 15 resolvable watch-out heroes.
const FOREHEAD_MIN_SLOTS = 15;
const FOREHEAD_EXPAND_STEP = 5;

// Color palette for different match pulls
const MATCH_COLORS = [
  { r: 76, g: 175, b: 80 },   // Green
  { r: 255, g: 152, b: 0 },   // Orange
  { r: 156, g: 39, b: 176 },  // Purple
  { r: 33, g: 150, b: 243 },  // Blue
  { r: 244, g: 67, b: 54 },   // Red
  { r: 0, g: 188, b: 212 },   // Cyan
  { r: 255, g: 235, b: 59 },  // Yellow
  { r: 233, g: 30, b: 99 },   // Pink
];

const getMatchColor = (colorIndex) => {
  const color = MATCH_COLORS[colorIndex % MATCH_COLORS.length];
  return color;
};

const DraftBoardContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #0b0d14;
  background-image: linear-gradient(to bottom, #161926, #0b0d14);
  padding: 12px;
  border-radius: 12px;
  width: 280px;
  min-width: 280px;
  max-width: 280px;
  flex-shrink: 0;
  margin: 0;
  position: relative;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  border: 3px solid transparent;

  /* Gradient highlight - green for normal, red for swapped - starts from left, dissipates toward middle */
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 50%;
    background: ${props => props.$swapSides ? `linear-gradient(
      to right,
      rgba(255, 70, 70, 0.45) 0%,
      rgba(255, 90, 90, 0.28) 25%,
      rgba(255, 70, 70, 0.15) 50%,
      transparent 100%
    )` : `linear-gradient(
      to right,
      rgba(76, 175, 80, 0.2) 0%,
      rgba(139, 195, 74, 0.12) 25%,
      rgba(76, 175, 80, 0.06) 50%,
      transparent 100%
    )`};
    border-radius: 12px 0 0 12px;
    pointer-events: none;
    z-index: 2;
  }

  /* Neon glow for scenarios with practice matchup data */
  ${props => props.$hasPracticeMatchup && !props.$fromMatchPull && `
    border: 3px solid rgba(0, 229, 255, 0.7);
    animation: practiceMatchupGlow 3s ease-in-out infinite;
  `}

  /* Manual neon highlight (user-toggled) */
  ${props => props.$neonHighlight && `
    border: 3px solid rgba(255, 0, 229, 0.85);
    animation: manualNeonGlow 2.4s ease-in-out infinite;
  `}

  @keyframes manualNeonGlow {
    0%, 100% {
      border-color: rgba(255, 0, 229, 0.7);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5),
                  0 0 18px rgba(255, 0, 229, 0.5),
                  0 0 36px rgba(255, 0, 229, 0.2);
    }
    50% {
      border-color: rgba(255, 0, 229, 1);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5),
                  0 0 30px rgba(255, 0, 229, 0.8),
                  0 0 60px rgba(255, 0, 229, 0.35);
    }
  }

  @keyframes practiceMatchupGlow {
    0%, 100% {
      border-color: rgba(0, 229, 255, 0.6);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5),
                  0 0 15px rgba(0, 229, 255, 0.4),
                  0 0 30px rgba(0, 229, 255, 0.15);
    }
    50% {
      border-color: rgba(0, 229, 255, 1);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5),
                  0 0 25px rgba(0, 229, 255, 0.7),
                  0 0 50px rgba(0, 229, 255, 0.3);
    }
  }

  /* Breathing effect for match-pulled scenarios with unique colors */
  ${props => {
    if (props.$fromMatchPull && typeof props.$matchColorIndex === 'number') {
      const color = getMatchColor(props.$matchColorIndex);
      return `
        border: 3px solid rgba(${color.r}, ${color.g}, ${color.b}, 0.6);
        animation: breathingGlow-${props.$matchColorIndex % MATCH_COLORS.length} 3s ease-in-out infinite;
      `;
    }
    return '';
  }}

  /* Generate keyframes for each color */
  ${MATCH_COLORS.map((color, index) => `
    @keyframes breathingGlow-${index} {
      0%, 100% {
        border-color: rgba(${color.r}, ${color.g}, ${color.b}, 0.6);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5),
                    0 0 15px rgba(${color.r}, ${color.g}, ${color.b}, 0.4);
      }
      50% {
        border-color: rgba(${color.r}, ${color.g}, ${color.b}, 1);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5),
                    0 0 25px rgba(${color.r}, ${color.g}, ${color.b}, 0.6);
      }
    }
  `).join('\n')}

  /* Grey out base when dragging (not the drag image) */
  ${props => props.$isDragging && `
    opacity: 0.3;
    filter: grayscale(0.5);
  `}

  /* Add margin when this scenario is the drop target to create a gap */
  ${props => props.$isOver && `
    margin-left: 40px;
  `}

  /* Ripple animation when dropped */
  ${props => props.$justDropped && `
    animation: dropRipple 0.6s ease-out;
  `}

  @keyframes dropRipple {
    0% {
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5),
                  0 0 0 0 rgba(33, 150, 243, 0.7);
    }
    50% {
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5),
                  0 0 0 10px rgba(33, 150, 243, 0.3);
    }
    100% {
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5),
                  0 0 0 20px rgba(33, 150, 243, 0);
    }
  }

  @media (max-width: 768px) {
    width: 220px;
    min-width: 220px;
    max-width: 220px;
    padding: 8px 6px;
  }

  @media (max-width: 480px) {
    width: 200px;
    min-width: 200px;
    max-width: 200px;
    padding: 6px 4px;
  }

  @media (max-width: 600px) and (orientation: portrait) {
    zoom: 0.72;
  }
`;

const DragHandle = styled.div.attrs(props => ({
  'data-drag-handle': true,
  'draggable': true
}))`
  width: 100%;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  margin-bottom: 8px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.05);
  transition: background 0.2s;
  user-select: none;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  &:active {
    cursor: grabbing;
  }

  &::before {
    content: '⋮⋮⋮';
    color: rgba(255, 255, 255, 0.3);
    font-size: 16px;
    letter-spacing: 2px;
    pointer-events: none;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #333;
`;

const TeamsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 10px;
  align-items: center;
`;

const TeamHeader = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: ${props => props.$team === 'Radiant' ? '#92ff92' : '#ff6b6b'};
  text-align: center;
  width: 50%;
  text-shadow: 0 0 8px ${props => props.$team === 'Radiant' ? 'rgba(146, 255, 146, 0.5)' : 'rgba(255, 107, 107, 0.5)'};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 0;
`;

const ClearButton = styled.button`
  background-color: #d32f2f;
  background-image: linear-gradient(to bottom, #d32f2f, #b71c1c);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  
  &:hover {
    background-image: linear-gradient(to bottom, #e33c3c, #c82121);
    transform: translateY(-1px);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.4);
  }
  
  &:active {
    transform: translateY(1px);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
  }
`;

const PicksContainer = styled.div`
  display: flex;
  justify-content: center;
  position: relative;
  gap: 16px;
  background: linear-gradient(to bottom, rgba(20, 22, 34, 0.8), rgba(14, 16, 26, 0.8));
  padding: 12px 0;
  border-radius: 8px;
`;

const TeamColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${props => {
    // When not swapped: Radiant (left) aligns right, Dire (right) aligns left
    // When swapped: Dire (left) aligns right, Radiant (right) aligns left
    const isRadiant = props.$team === 'Radiant';
    const isSwapped = props.$swapped || false;

    // Radiant is normally on left, Dire on right
    // When swapped, positions flip
    const isOnLeft = isSwapped ? !isRadiant : isRadiant;

    return isOnLeft ? 'flex-end' : 'flex-start';
  }};
  gap: 12px;
`;

// Empty spacer to maintain alignment between columns
const EmptySpacer = styled.div`
  height: ${props => getBoxSize(props.$position)};
  width: ${props => {
    if (DraftState.getLargeBoxSequences().includes(props.$position.toString())) {
      return '120px';
    }
    return '80px';
  }};
  visibility: hidden;
`;

// Box sizes based on their importance in the draft
const getBoxSize = (position) => {
  // Use the same large box sequences from the DraftState model
  if (DraftState.getLargeBoxSequences().includes(position.toString())) {
    return '65px';  // Height for large boxes - matches overlay dimensions
  }
  return '50px';  // Height for standard boxes
};

const PickBox = styled.div.attrs(props => ({
  'data-position': props.$position,
  'data-team': props.$team
}))`
  border: 1px solid #444;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${props => getBoxSize(props.$position)};
  width: ${props => {
    // Width determination based on position, using DraftState
    if (DraftState.getLargeBoxSequences().includes(props.$position.toString())) {
      return '120px';  // Width for large boxes - reduced from 135px
    }
    return '80px';  // Width for standard boxes - reduced from 90px
  }};
  position: relative;
  z-index: 3; /* Above green gradient overlay */
  cursor: ${props => props.$isSimulatorView ? 'default' : 'pointer'};
  transition: border-color 0.15s ease, filter 0.15s ease;
  background-color: #111215;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  overflow: visible; /* Allow connector handle to overflow outside box edge */
  padding: 0; /* Remove padding to allow images to fill the box */

  &:hover {
    ${props => !props.$isSimulatorView && `
      border-color: ${props.$team === 'Radiant' ? '#92ff92' : '#ff6b6b'};
      filter: brightness(1.15);
    `}
  }

  &.selected {
    border-color: ${props => props.$team === 'Radiant' ? '#92ff92' : '#ff6b6b'};
    border-width: 2px;
    box-shadow: 0 0 10px ${props => props.$team === 'Radiant' ? 'rgba(146, 255, 146, 0.5)' : 'rgba(255, 107, 107, 0.5)'};
  }

  &.multi-selected {
    border-color: #2196F3;
    border-width: 3px;
    box-shadow: 0 0 15px rgba(33, 150, 243, 0.8);
    background-color: rgba(33, 150, 243, 0.1);
  }

  &.filled {
    background-color: ${props => props.$team === 'Radiant' ? 'rgba(146, 255, 146, 0.1)' : 'rgba(255, 107, 107, 0.1)'};
  }

  @media (max-height: 600px) and (orientation: landscape) {
    width: 37px !important;
    height: 33px !important;
    min-width: unset !important;
    border-radius: 4px;
  }
`;

const PositionLabel = styled.div`
  position: absolute;
  top: 4px;
  left: 4px;
  font-size: 12px;
  font-weight: bold;
  color: ${props => props.$isBan ? '#ff6b6b' : '#6bff74'};
  background-color: rgba(0, 0, 0, 0.5);
  padding: 2px 5px;
  border-radius: 4px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
  z-index: 2; /* Ensure it's above the hero image */

  @media (max-height: 600px) and (orientation: landscape) {
    font-size: 9px;
    padding: 1px 3px;
    top: 2px;
    left: 2px;
  }
`;

const HeroContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 0; /* Ensure it's below any overlays like the ban X */
  overflow: hidden;
  border-radius: inherit; /* Clip hero image to PickBox rounded corners */

  /* Ensure HeroImage fills the container */
  > div {
    width: 100%;
    height: 100%;
  }
`;

const BanIndicator = styled.div`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: rgba(211, 47, 47, 0.8);
  color: white;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2; /* Ensure it's above the hero image */
`;

// Container for multiple stacked badges
const MatchBadgesContainer = styled.div`
  position: absolute;
  top: 4px;
  right: 4px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  z-index: 3;
`;

// Breathing border overlay for matched heroes
// Purple: hero appears in both picks AND bans
// Green: hero appears ONLY in picks
// Red: hero appears ONLY in bans
const MatchedHeroBorder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: 4px solid ${props => {
    if (props.$borderType === 'purple') return 'rgba(156, 39, 176, 1)';
    if (props.$borderType === 'green') return 'rgba(76, 175, 80, 1)';
    if (props.$borderType === 'red') return 'rgba(255, 107, 107, 1)';
    return 'rgba(156, 39, 176, 1)'; // fallback to purple
  }};
  border-radius: 8px;
  pointer-events: none;
  animation: ${props => {
    if (props.$borderType === 'purple') return 'breathingPurpleBorder';
    if (props.$borderType === 'green') return 'breathingGreenBorder';
    if (props.$borderType === 'red') return 'breathingRedBorder';
    return 'breathingPurpleBorder';
  }} 2s ease-in-out infinite;
  z-index: 10;
  opacity: 1 !important;

  @keyframes breathingPurpleBorder {
    0%, 100% {
      border-color: rgba(156, 39, 176, 1);
      box-shadow: 0 0 15px rgba(156, 39, 176, 0.8);
    }
    50% {
      border-color: rgba(186, 104, 200, 1);
      box-shadow: 0 0 25px rgba(186, 104, 200, 1);
    }
  }

  @keyframes breathingGreenBorder {
    0%, 100% {
      border-color: rgba(76, 175, 80, 1);
      box-shadow: 0 0 15px rgba(76, 175, 80, 0.8);
    }
    50% {
      border-color: rgba(102, 187, 106, 1);
      box-shadow: 0 0 25px rgba(102, 187, 106, 1);
    }
  }

  @keyframes breathingRedBorder {
    0%, 100% {
      border-color: rgba(255, 107, 107, 1);
      box-shadow: 0 0 15px rgba(255, 107, 107, 0.8);
    }
    50% {
      border-color: rgba(255, 82, 82, 1);
      box-shadow: 0 0 25px rgba(255, 82, 82, 1);
    }
  }
`;

const MatchBadge = styled.div`
  min-width: 24px;
  height: 18px;
  padding: 2px 6px;
  border-radius: 4px;
  background: ${props => props.$isSamePosition
    ? 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)' // Green for same position
    : 'linear-gradient(135deg, #ff6b6b 0%, #ff5252 100%)'}; // Red for different position
  color: white;
  font-size: 10px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${props => props.$isSamePosition
    ? '0 2px 6px rgba(76, 175, 80, 0.6), 0 0 10px rgba(76, 175, 80, 0.4)'
    : '0 2px 6px rgba(255, 107, 107, 0.6), 0 0 10px rgba(255, 107, 107, 0.4)'};
  animation: ${props => props.$isSamePosition ? 'matchGlowGreen' : 'matchGlowRed'} 2s ease-in-out infinite;
  line-height: 1;

  @keyframes matchGlowGreen {
    0%, 100% {
      box-shadow: 0 2px 6px rgba(76, 175, 80, 0.6),
                  0 0 10px rgba(76, 175, 80, 0.4);
    }
    50% {
      box-shadow: 0 2px 10px rgba(76, 175, 80, 0.8),
                  0 0 16px rgba(76, 175, 80, 0.6);
    }
  }

  @keyframes matchGlowRed {
    0%, 100% {
      box-shadow: 0 2px 6px rgba(255, 107, 107, 0.6),
                  0 0 10px rgba(255, 107, 107, 0.4);
    }
    50% {
      box-shadow: 0 2px 10px rgba(255, 107, 107, 0.8),
                  0 0 16px rgba(255, 107, 107, 0.6);
    }
  }
`;

const BanCross = styled.div`
  position: absolute;
  bottom: 4px;
  left: 4px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ff6b6b;
  font-weight: bold;
  font-size: 18px;
  pointer-events: none;
  text-shadow: 0 0 4px rgba(0, 0, 0, 0.9);
  z-index: 2;

  &::before {
    content: '';
    position: absolute;
    width: 16px;
    height: 16px;
    background-color: rgba(0, 0, 0, 0.3);
    border-radius: 50%;
    z-index: -1;
  }

  @media (max-height: 600px) and (orientation: landscape) {
    font-size: 14px;
    width: 14px;
    height: 14px;
    bottom: 2px;
    left: 2px;
  }
`;

const LivePickTimeBadge = styled.div`
  position: absolute;
  bottom: 3px;
  right: 3px;
  background: rgba(0, 0, 0, 0.72);
  color: #ff6a00;
  font-size: 13px;
  font-weight: 700;
  font-family: 'Century Gothic', 'Futura', 'Trebuchet MS', sans-serif;
  line-height: 1;
  padding: 2px 4px;
  border-radius: 3px;
  pointer-events: none;
  z-index: 4;
  letter-spacing: 0.02em;
  text-shadow: 0 0 8px #ff6a00, 0 0 16px rgba(255,106,0,0.6);
`;

const HeroSelectPopup = styled.div`
  position: fixed;
  z-index: 1000;
  width: 90vw;
  max-width: 500px;
  max-height: 80vh;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  background-color: rgba(14, 16, 22, 0.97);
  border: 1px solid #444;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.9);
  display: flex;
  flex-direction: column;
  animation: popupFadeIn 0.25s cubic-bezier(0.23, 1, 0.32, 1);
  
  @keyframes popupFadeIn {
    from {
      opacity: 0;
      transform: translate(-50%, -50%) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
  }
`;

const SearchInput = styled.input`
  padding: 14px 16px;
  background-color: rgba(10, 12, 16, 0.7);
  color: white;
  border: none;
  border-bottom: 1px solid #555;
  margin-bottom: 0;
  font-size: 15px;
  width: 100%;
  box-sizing: border-box;
  position: sticky;
  top: 0;
  z-index: 3;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    background-color: rgba(15, 18, 24, 0.9);
    border-bottom-color: #4CAF50;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.5);
  font-size: 16px;
  pointer-events: none;
`;

const SelectBoxPlaceholder = styled.div.attrs(props => ({
  shouldShow: props.show
}))`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  color: #aaa;
  font-size: 14px;
  line-height: 1.5;
  max-width: 300px;
  padding: 24px;
  background-color: rgba(0, 0, 0, 0.4);
  border-radius: 8px;
  pointer-events: none;
  
  .instruction {
    font-weight: bold;
    color: white;
    margin-bottom: 10px;
  }
  
  .arrow {
    font-size: 24px;
    margin: 5px 0;
  }
  
  display: ${props => props.shouldShow ? 'block' : 'none'};
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 100px));
  gap: 10px;
  padding: 16px;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 400px;
  flex: 1;
  justify-content: center;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: #555;
    border-radius: 4px;
    
    &:hover {
      background-color: #666;
    }
  }
  
  &::-webkit-scrollbar-track {
    background-color: rgba(0, 0, 0, 0.3);
    border-radius: 4px;
  }
`;

const HeroOption = styled.div`
  cursor: pointer;
  padding: 6px 4px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: all 0.2s;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  }
  
  &:active {
    transform: translateY(1px);
  }
  
  .hero-name {
    font-size: 11px;
    margin-top: 6px;
    text-align: center;
    color: #fff;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
    font-weight: 500;
  }
`;

const PopupControls = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px;
  border-top: 1px solid #444;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(5px);
`;

const RemoveButton = styled.button`
  background-color: #333;
  background-image: linear-gradient(to bottom, #444, #333);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px 14px;
  cursor: pointer;
  font-size: 12px;
  font-weight: bold;
  transition: all 0.2s;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
  
  &:hover {
    background-image: linear-gradient(to bottom, #555, #444);
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.5);
  }
  
  &:active {
    transform: translateY(1px);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
    background-image: linear-gradient(to bottom, #333, #2a2a2a);
  }
`;

const BanBox = styled(PickBox)`
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.25);
    pointer-events: none;
    border-radius: 7px;
    z-index: 1; /* Ensure the overlay sits above the image */
  }

  &.filled::after {
    background-color: rgba(0, 0, 0, 0.35);
  }

  @media (max-height: 600px) and (orientation: landscape) {
    width: 26px !important;
  }
`;

// Edge handle for initiating a counter-relation connector drag
const ConnectorHandle = styled.div`
  position: absolute;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.95);
  border: 1.5px solid #00b4d8;
  box-shadow: 0 1px 3px rgba(0,0,0,0.5);
  cursor: crosshair;
  z-index: 10;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.12s, transform 0.12s;
  user-select: none;

  ${PickBox}:hover &, &.dragging {
    opacity: 1;
    pointer-events: auto;
  }

  &:hover {
    transform: scale(1.35);
    background: #00e0ff;
  }

  &.top    { top: -6px;    left: 50%; transform: translateX(-50%); }
  &.bottom { bottom: -6px; left: 50%; transform: translateX(-50%); }
  &.left   { left: -7px;   top: 50%;  transform: translateY(-50%); }
  &.right  { right: -7px;  top: 50%;  transform: translateY(-50%); }

  ${PickBox}:hover &.top,    &.top.dragging    { transform: translateX(-50%) scale(1); }
  ${PickBox}:hover &.bottom, &.bottom.dragging { transform: translateX(-50%) scale(1); }
  ${PickBox}:hover &.left,   &.left.dragging   { transform: translateY(-50%) scale(1); }
  ${PickBox}:hover &.right,  &.right.dragging  { transform: translateY(-50%) scale(1); }

  &.top:hover    { transform: translateX(-50%) scale(1.35); }
  &.bottom:hover { transform: translateX(-50%) scale(1.35); }
  &.left:hover   { transform: translateY(-50%) scale(1.35); }
  &.right:hover  { transform: translateY(-50%) scale(1.35); }

  @media (max-height: 600px) and (orientation: landscape) {
    display: none;
  }
`;

// SVG overlay covering PicksContainer; draws counter-relation arrows.
const ConnectorSvg = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 5;
  overflow: visible;

  .connector-path {
    fill: none;
    stroke: #00ff88;
    stroke-width: 4;
    stroke-dasharray: 8 6;
    stroke-linecap: round;
    pointer-events: stroke;
    cursor: pointer;
    animation: connectorBreathing 3s ease-in-out infinite;
    transition: stroke-width 0.15s;
  }
  .connector-path:hover {
    stroke-width: 5.5;
  }
  .connector-path.drag-preview {
    stroke: #00b4d8;
    stroke-dasharray: 5 4;
    pointer-events: none;
    animation: none;
    filter: drop-shadow(0 0 4px rgba(0,180,216,0.7));
  }

  @keyframes connectorBreathing {
    0%, 100% {
      stroke: rgba(0, 255, 136, 0.85);
      filter: drop-shadow(0 0 3px rgba(0, 255, 136, 0.6));
    }
    50% {
      stroke: rgba(120, 255, 180, 1);
      filter: drop-shadow(0 0 10px rgba(0, 255, 136, 1)) drop-shadow(0 0 18px rgba(0, 255, 136, 0.5));
    }
  }
`;

const LegendContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  padding: 8px 12px;
  background-color: rgba(0, 0, 0, 0.4);
  border-radius: 6px;
  border: 1px solid #333;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #ddd;
`;

const LegendBanIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.3);
  color: #ff6b6b;
  font-weight: bold;
  font-size: 14px;
`;

const AddScenarioButton = styled.button`
  background-color: #2196F3;
  background-image: linear-gradient(to bottom, #2196F3, #1976D2);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 120px;
  height: 36px;
  font-size: 14px;
  
  &:hover {
    background-image: linear-gradient(to bottom, #42A5F5, #1E88E5);
    transform: translateY(-1px);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.4);
  }
  
  &:active {
    transform: translateY(1px);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
  }
  
  @media (max-width: 768px) {
    padding: 6px 10px;
    min-width: 100px;
    height: 32px;
    font-size: 12px;
  }

  @media (max-width: 600px) {
    height: 28px;
    font-size: 11px;
    padding: 0 10px;
    min-width: unset;
  }

  svg {
    margin-right: 6px;
  }
`;

const NewDraftButton = styled.button`
  background-color: #1976D2;
  background-image: linear-gradient(to bottom, #1976D2, #1565C0);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 120px;
  height: 36px;
  font-size: 14px;
  
  &:hover {
    background-image: linear-gradient(to bottom, #1E88E5, #1976D2);
    transform: translateY(-1px);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.4);
  }
  
  &:active {
    transform: translateY(1px);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
  }
  
  @media (max-width: 768px) {
    padding: 6px 10px;
    min-width: 100px;
    height: 32px;
    font-size: 12px;
  }

  @media (max-width: 600px) {
    height: 28px;
    font-size: 11px;
    padding: 0 10px;
    min-width: unset;
  }

  @media (max-height: 600px) and (orientation: landscape) {
    height: 26px;
    font-size: 10px;
    padding: 0 8px;
    min-width: unset;
  }

  svg {
    margin-right: 6px;
  }
`;

// Create a new ScenarioContainer for horizontal scrolling
const ScenarioContainer = styled.div`
  display: flex;
  overflow-x: auto;
  gap: 20px;
  padding: 10px 20px;
  width: 100%;
  min-width: 100vw;
  scrollbar-width: thin;
  scrollbar-color: #444 #222;
  justify-content: safe center;
  
  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #222;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: #444;
    border-radius: 4px;
    
    &:hover {
      background-color: #555;
    }
  }
`;

// Add a DeleteScenarioButton component
const DeleteScenarioButton = styled.button`
  background-color: transparent;
  color: #ff4d4d;
  border: 1px solid #ff4d4d;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 11px;
  font-weight: bold;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  min-width: 60px;
  justify-content: center;
  
  &:hover {
    background-color: rgba(255, 77, 77, 0.1);
  }
  
  svg {
    margin-right: 4px;
  }
`;

// Add a PracticeMatchupButton component
const PracticeMatchupButton = styled.button`
  background-color: transparent;
  color: #00e5ff;
  border: 1px solid #00e5ff;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 11px;
  cursor: pointer;
  min-width: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-shadow: 0 0 8px rgba(0, 229, 255, 0.5), inset 0 0 6px rgba(0, 229, 255, 0.1);
  text-shadow: 0 0 8px rgba(0, 229, 255, 0.8);

  &:hover {
    background-color: rgba(0, 229, 255, 0.1);
    border-color: #40f0ff;
    color: #40f0ff;
    box-shadow: 0 0 14px rgba(0, 229, 255, 0.8), inset 0 0 10px rgba(0, 229, 255, 0.15);
    text-shadow: 0 0 12px rgba(0, 229, 255, 1);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    margin-right: 4px;
  }

  @media (max-height: 600px) and (orientation: landscape) {
    display: none;
  }
`;

// See Matchups button - matches Practice Matchup style with chart colors
const SeeMatchupsButton = styled.button`
  background-color: transparent;
  color: #ffd700;
  border: 1px solid #ffd700;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 14px;
  cursor: pointer;
  min-width: 120px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  font-weight: bold;

  &:hover {
    background-color: rgba(255, 215, 0, 0.1);
    border-color: #ffed4e;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 600px) {
    height: 28px;
    font-size: 11px;
    padding: 0 10px;
    min-width: unset;
  }

  @media (max-height: 600px) and (orientation: landscape) {
    display: none;
  }
`;

// Share Draft button - purple to stand out
const ShareDraftButton = styled.button`
  background-color: #9c27b0;
  background-image: linear-gradient(to bottom, #9c27b0, #7b1fa2);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 120px;
  height: 36px;
  font-size: 14px;

  &:hover {
    background-image: linear-gradient(to bottom, #ab47bc, #8e24aa);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(156, 39, 176, 0.4);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  }

  @media (max-width: 600px) {
    height: 28px;
    font-size: 11px;
    padding: 0 10px;
    min-width: unset;
  }

  @media (max-height: 600px) and (orientation: landscape) {
    height: 26px;
    font-size: 10px;
    padding: 0 8px;
    min-width: unset;
  }
`;

// Export JPEG button - teal to stand out from the other actions
const ExportJpegButton = styled.button`
  background-color: #00897b;
  background-image: linear-gradient(to bottom, #00897b, #00695c);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 120px;
  height: 36px;
  font-size: 14px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background-image: linear-gradient(to bottom, #26a69a, #00897b);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 137, 123, 0.4);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  }

  @media (max-width: 600px) {
    height: 28px;
    font-size: 11px;
    padding: 0 10px;
    min-width: unset;
  }

  @media (max-height: 600px) and (orientation: landscape) {
    height: 26px;
    font-size: 10px;
    padding: 0 8px;
    min-width: unset;
  }
`;

// Record Viewer button - blue/cyan theme
const RecordViewerButton = styled.button`
  background-color: #0288d1;
  background-image: linear-gradient(to bottom, #0288d1, #01579b);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 120px;
  height: 36px;
  font-size: 14px;

  &:hover {
    background-image: linear-gradient(to bottom, #039be5, #0277bd);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(2, 136, 209, 0.4);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  }

  @media (max-width: 600px) {
    height: 28px;
    font-size: 11px;
    padding: 0 10px;
    min-width: unset;
  }

  @media (max-height: 600px) and (orientation: landscape) {
    height: 26px;
    font-size: 10px;
    padding: 0 8px;
    min-width: unset;
  }
`;

const OppPoolButton = styled.button`
  background-color: #b45309;
  background-image: linear-gradient(to bottom, #d97706, #b45309);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 120px;
  height: 36px;
  font-size: 14px;
  position: relative;

  &:hover {
    background-image: linear-gradient(to bottom, #f59e0b, #d97706);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(217, 119, 6, 0.5);
  }
  &:active { transform: translateY(0); }

  @media (max-width: 600px) { height: 28px; font-size: 11px; padding: 0 10px; min-width: unset; }
`;

const OppPoolPopover = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 1000;
  background: #1e293b;
  border: 1px solid #475569;
  border-radius: 8px;
  padding: 12px;
  min-width: 220px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.5);
  display: flex;
  flex-direction: column;
  gap: 8px;

  select {
    width: 100%;
    background: #0f172a;
    color: #e2e8f0;
    border: 1px solid #475569;
    border-radius: 5px;
    padding: 6px 8px;
    font-size: 13px;
  }

  button.load-btn {
    background: #d97706;
    color: white;
    border: none;
    border-radius: 5px;
    padding: 7px 0;
    font-weight: 700;
    font-size: 13px;
    cursor: pointer;
    &:hover { background: #f59e0b; }
    &:disabled { opacity: 0.5; cursor: default; }
  }

  .pool-hint { font-size: 11px; color: #94a3b8; }
`;

// Win-rate color bands: green (strong) / yellow (even) / red (weak). Falls
// back to the previous neutral amber when there's no numeric win rate.
const foreheadLabelColor = (winRate) => {
  if (typeof winRate !== 'number') return '#fbbf24';
  if (winRate >= 60) return '#4ade80';
  if (winRate >= 40) return '#fbbf24';
  return '#f87171';
};

const ForeheadLabel = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0,0,0,0.72);
  color: ${props => foreheadLabelColor(props.$winRate)};
  font-size: 12px;
  font-weight: 700;
  text-align: center;
  padding: 1px 2px;
  line-height: 1.3;
  z-index: 2;
  border-radius: 0 0 4px 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SaveDraftButton = styled.button`
  background-color: #4CAF50;
  background-image: linear-gradient(to bottom, #4CAF50, #388E3C);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 10px 16px;
  cursor: pointer;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 140px;
  height: 42px;
  font-size: 14px;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);

  &:hover {
    background-image: linear-gradient(to bottom, #66BB6A, #4CAF50);
    transform: translateY(-1px);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.4);
  }

  &:active {
    transform: translateY(1px);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
  }

  @media (max-height: 600px) and (orientation: landscape) {
    height: 26px;
    font-size: 10px;
    padding: 0 10px;
    min-width: unset;
  }
`;

const MobileHide = styled.div`
  display: contents;
  @media (max-width: 768px), (max-height: 600px) and (orientation: landscape) {
    display: none;
  }
`;

const SaveToast = styled.div`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  z-index: 9999;
  pointer-events: none;
  background: ${({ $type }) =>
    $type === 'success' ? '#2e7d32' : $type === 'error' ? '#c62828' : '#e65100'};
  box-shadow: 0 4px 12px rgba(0,0,0,0.5);
  animation: fadeInUp 0.2s ease;
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateX(-50%) translateY(8px); }
    to   { opacity: 1; transform: translateX(-50%) translateY(0); }
  }
`;

// Add a DuplicateScenarioButton component
const DuplicateScenarioButton = styled.button`
  background-color: transparent;
  color: #61dafb;
  border: 1px solid #61dafb;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 11px;
  font-weight: bold;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  min-width: 70px;
  justify-content: center;

  &:hover {
    background-color: rgba(97, 218, 251, 0.1);
  }

  svg {
    margin-right: 4px;
  }
`;

// Add a CopyScenarioButton component
const CopyScenarioButton = styled.button`
  background-color: transparent;
  color: #4caf50;
  border: 1px solid #4caf50;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 11px;
  font-weight: bold;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  min-width: 70px;
  justify-content: center;

  &:hover {
    background-color: rgba(76, 175, 80, 0.1);
  }

  svg {
    margin-right: 4px;
  }
`;

const ScenarioTitle = styled.div`
  font-size: 16px;
  font-weight: bold;
  text-align: center;
  margin-bottom: 10px;
  color: #eee;
`;

const ScenarioInput = styled.input`
  background-color: rgba(0, 0, 0, 0.3);
  border: 1px solid #444;
  border-radius: 4px;
  color: white;
  padding: 8px 10px;
  width: 100%;
  margin-bottom: 12px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #61dafb;
    box-shadow: 0 0 0 2px rgba(97, 218, 251, 0.2);
  }
  
  &::placeholder {
    color: #888;
  }
`;

const TopButtonRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  width: 100%;
  margin-bottom: 16px;
  flex-wrap: wrap;
  position: sticky;
  top: 0;
  background: #121212;
  z-index: 100;
  padding: 12px 0;

  /* Embed view: fixed vertical sidebar on the right edge so the board has
     full vertical real estate (no scrolling caused by a top toolbar). */
  ${props => props.$embed && `
    position: fixed;
    top: 50%;
    right: 12px;
    transform: translateY(-50%);
    flex-direction: column;
    width: auto;
    margin-bottom: 0;
    padding: 10px;
    gap: 10px;
    background: rgba(20, 22, 28, 0.95);
    border: 1px solid #2a2f3a;
    border-radius: 10px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
  `}

  @media (max-width: 768px) {
    gap: 4px;
    margin-bottom: 12px;
  }

  @media (max-height: 600px) and (orientation: landscape) {
    gap: 4px;
    padding: 4px 0;
    margin-bottom: 4px;

    span[style] {
      font-size: 12px !important;
    }
  }
`;

const ScenarioBoardsRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: safe center;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 16px 20px;
  box-sizing: border-box;

  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;

  /* Gallery mode: flush to edges, no scroll */
  ${props => props.$gallery && `
    padding: 0;
    overflow: hidden;
    justify-content: center;
  `}

  /* Custom scrollbar for webkit browsers */
  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.5);
  }

  @media (max-width: 768px) {
    gap: 12px;
    padding: 12px 4px;
  }

  @media (max-height: 600px) and (orientation: landscape) {
    display: none;
  }
`;

// ── Mobile landscape carousel ─────────────────────────────────────────────────
const MobileCarouselOuter = styled.div`
  display: none;
  width: 100%;
  flex: 1;

  @media (max-height: 600px) and (orientation: landscape) {
    display: ${props => props.$active ? 'flex' : 'none'};
    flex-direction: column;
  }

  ${PositionLabel} {
    top: auto;
    bottom: 0;
    left: 0;
    background-color: rgba(0, 0, 0, 0.25);
    padding: 1px 4px;
    border-radius: 0;
    border-top-right-radius: 4px;
    font-size: 7px;
  }

  ${BanCross} {
    display: none;
  }
`;

const MobileCarouselScroll = styled.div`
  display: flex;
  overflow-x: scroll;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  width: 100%;
  flex: 1;
  align-items: stretch;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const MobilePage = styled.div`
  width: 100vw;
  min-width: 100vw;
  flex-shrink: 0;
  scroll-snap-align: start;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  grid-auto-flow: column;
  box-sizing: border-box;
`;

const MobileBoardCard = styled.div`
  width: ${props => props.$solo ? '50vw' : 'auto'};
  min-width: ${props => props.$solo ? '50vw' : 'unset'};
  flex-shrink: ${props => props.$solo ? '0' : '1'};
  align-self: ${props => props.$solo ? 'flex-start' : 'auto'};
  scroll-snap-align: ${props => props.$solo ? 'start' : 'none'};
  display: flex;
  flex-direction: column;
  padding: 4px 6px;
  box-sizing: border-box;
  background: linear-gradient(to bottom, #161926, #0b0d14);
  border-radius: 0;
  border: 2px solid ${props => props.$borderColor || 'rgba(160,160,160,0.3)'};
  overflow: hidden;
  gap: 3px;
`;

const MobileBoardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  flex-shrink: 0;
  flex-wrap: wrap;
`;

const MobileDraftGrid = styled.div`
  display: flex;
  flex-direction: row;
  gap: 6px;
  flex: 1;
  align-items: flex-start;
`;

const MobileSideSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;

  @media (max-height: 600px) and (orientation: landscape) {
    gap: 2px;
  }
`;

const MobileSideLabel = styled.div`
  font-size: 11px;
  font-weight: bold;
  text-align: left;
  color: ${props => props.$team === 'Radiant' ? '#92ff92' : '#ff6b6b'};
  text-shadow: 0 0 6px ${props => props.$team === 'Radiant' ? 'rgba(146,255,146,0.5)' : 'rgba(255,107,107,0.5)'};
  margin-bottom: 2px;

  @media (max-height: 600px) and (orientation: landscape) {
    font-size: 9px;
    margin-bottom: 1px;
  }
`;

const MobileHeroRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 3px;
  flex-wrap: nowrap;

  @media (max-height: 600px) and (orientation: landscape) {
    gap: 2px;
  }
`;

const MobileSideDivider = styled.div`
  width: 1px;
  background: rgba(255, 255, 255, 0.12);
  align-self: stretch;
  flex-shrink: 0;
  margin: 0 2px;
`;

// ── Mobile landscape view toggle bar ─────────────────────────────────────────
const MobileLandscapeToggleBar = styled.div`
  display: none;
  @media (max-height: 600px) and (orientation: landscape) {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    padding: 3px 8px;
    background: rgba(0, 0, 0, 0.4);
  }
`;

const MobileLandscapeToggleSwitch = styled.div`
  display: flex;
  border: 2px solid #2196F3;
  border-radius: 999px;
  overflow: hidden;
  background: #181a20;
  height: 22px;
`;

const MobileLandscapeToggleOption = styled.button`
  background: ${props => props.$active ? '#2196F3' : 'transparent'};
  color: ${props => props.$active ? '#fff' : '#aaa'};
  border: none;
  padding: 0 12px;
  cursor: pointer;
  font-size: 10px;
  font-weight: ${props => props.$active ? 'bold' : 'normal'};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;

  &:hover {
    background: ${props => props.$active ? '#42A5F5' : 'rgba(33,150,243,0.15)'};
    color: ${props => props.$active ? '#fff' : '#90CAF9'};
  }

  &:first-child { border-radius: 999px 0 0 999px; }
  &:last-child  { border-radius: 0 999px 999px 0; }
`;

// ── Drafter POV carousel styled components ────────────────────────────────────
const MobileDrafterOuter = styled.div`
  display: none;
  width: 100%;
  flex: 1;

  @media (max-height: 600px) and (orientation: landscape) {
    display: ${props => props.$active ? 'flex' : 'none'};
    flex-direction: column;
  }

  ${PositionLabel} {
    top: auto;
    bottom: 0;
    left: 0;
    background-color: rgba(0, 0, 0, 0.25);
    padding: 1px 4px;
    border-radius: 0;
    border-top-right-radius: 4px;
    font-size: 7px;
  }

  ${BanCross} {
    display: none;
  }
`;

const MobileDrafterCard = styled.div`
  width: 50vw;
  min-width: 50vw;
  flex-shrink: 0;
  scroll-snap-align: start;
  display: flex;
  flex-direction: column;
  padding: 4px 8px;
  box-sizing: border-box;
  background: linear-gradient(to bottom, #161926, #0b0d14);
  border-radius: 0;
  border: 2px solid ${props => props.$borderColor || 'rgba(160,160,160,0.3)'};
  overflow: hidden;
  gap: 3px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: ${props => props.$swapped
      ? 'linear-gradient(to right, rgba(255,70,70,0.28) 0%, rgba(255,70,70,0.10) 50%, transparent 100%)'
      : 'linear-gradient(to right, rgba(76,175,80,0.22) 0%, rgba(76,175,80,0.10) 50%, transparent 100%)'};
    pointer-events: none;
    z-index: 0;
  }
`;

const MobileBansSection = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const MobileBansGroup = styled.div`
  display: flex;
  flex-direction: row;
  gap: 3px;
  align-items: center;

  @media (max-height: 600px) and (orientation: landscape) {
    gap: 2px;
  }
`;

const MobileTeamsLabel = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
  flex-shrink: 0;
`;

const MobilePicksSection = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  gap: 10px;
  flex: 1;
`;

const MobilePickColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
`;

// Copy menu dialog styled components
const CopyMenuOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const CopyMenuDialog = styled.div`
  background: #1a1d29;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  border: 1px solid #444;
  min-width: 300px;
`;

const CopyMenuTitle = styled.h3`
  margin: 0 0 20px 0;
  color: #fff;
  font-size: 18px;
  text-align: center;
`;

const CopyMenuButton = styled.button`
  width: 100%;
  padding: 12px;
  margin-bottom: 12px;
  background: transparent;
  border: 1px solid ${props => props.$color || '#4caf50'};
  color: ${props => props.$color || '#4caf50'};
  border-radius: 6px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.$color ? `${props.$color}22` : 'rgba(76, 175, 80, 0.1)'};
  }

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const CopyMenuCancelButton = styled.button`
  width: 100%;
  padding: 12px;
  margin-top: 16px;
  background: transparent;
  border: 1px solid #888;
  color: #888;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(136, 136, 136, 0.1);
    border-color: #aaa;
    color: #aaa;
  }
`;

// Scenario selection overlay for "From" functionality
const ScenarioSelectionOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  z-index: 999;
  overflow-y: auto;
  padding: 40px 20px;
`;

const ScenarioSelectionHeader = styled.div`
  text-align: center;
  color: #fff;
  margin-bottom: 30px;
`;

const ScenarioSelectionTitle = styled.h2`
  margin: 0 0 10px 0;
  font-size: 24px;
  color: #4caf50;
`;

const ScenarioSelectionSubtitle = styled.p`
  margin: 0 0 20px 0;
  font-size: 16px;
  color: #aaa;
`;

const ScenarioSelectionCloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  color: #aaa;
  font-size: 24px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  padding: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    color: #fff;
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ScenarioSelectionGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  justify-content: center;
  max-width: 1400px;
  margin: 0 auto;
`;

const BanCrossOverlay = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 5;
  background: rgba(0, 0, 0, 0.35);
  border-radius: 8px;
  &::before, &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 10%;
    right: 10%;
    height: 3px;
    background: #ff3b30;
    box-shadow: 0 0 6px rgba(255, 59, 48, 0.9);
  }
  &::before { transform: translateY(-50%) rotate(20deg); }
  &::after  { transform: translateY(-50%) rotate(-20deg); }
`;

const ForeheadRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  column-gap: 14px;
  row-gap: 14px;
  margin-bottom: 12px;
  width: 100%;
  flex-wrap: wrap;
  position: relative;
`;

const ForeheadBracket = styled.div`
  position: absolute;
  height: 16px;
  pointer-events: none;
  border-top: 2px solid #fbbf24;
  border-left: 2px solid #fbbf24;
  border-right: 2px solid #fbbf24;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 5;

  span {
    transform: translateY(-100%);
    background: transparent;
    color: #fbbf24;
    font-size: 22px;
    font-weight: 800;
    line-height: 1.1;
    white-space: nowrap;
    padding: 0 8px;
    letter-spacing: 0.3px;
  }
`;

const InfoInputRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  width: 100%;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    display: none; /* Hide input boxes on mobile */
  }

  @media (max-height: 600px) and (orientation: landscape) {
    display: none;
  }
`;

const InfoInput = styled.input`
  padding: 8px 10px;
  border-radius: 4px;
  border: 1px solid #444;
  background: #181a20;
  color: #fff;
  font-size: 14px;
  min-width: 120px;
  max-width: 180px;
  outline: none;
  transition: border 0.2s;
  box-sizing: border-box;
  
  &:focus {
    border-color: #61dafb;
  }
  
  @media (max-width: 768px) {
    padding: 6px 8px;
    font-size: 13px;
    min-width: 100px;
    max-width: 140px;
  }
`;

const InfoDateInput = styled(InfoInput).attrs({ type: 'date' })`
  min-width: 120px;
  
  @media (max-width: 768px) {
    min-width: 100px;
  }
`;

const ToggleSwitchContainer = styled.div`
  display: flex;
  align-items: center;
  margin-right: 12px;

  @media (max-height: 600px) and (orientation: landscape) {
    margin-right: 4px;
  }
`;

const ToggleSwitch = styled.div`
  display: flex;
  border: 2px solid #1de9b6;
  border-radius: 999px;
  overflow: hidden;
  background: #181a20;
  min-width: 120px;
  height: 36px;

  @media (max-width: 600px) {
    height: 28px;
    min-width: unset;
  }

  @media (max-height: 600px) and (orientation: landscape) {
    height: 26px;
    min-width: unset;
  }
`;

const ToggleOption = styled.button`
  background: ${props => props.$active ? '#1de9b6' : 'transparent'};
  color: ${props => props.$active ? '#000' : '#aaa'};
  border: none;
  padding: 0 14px;
  cursor: pointer;
  font-size: 13px;
  font-weight: ${props => props.$active ? 'bold' : 'normal'};
  transition: all 0.2s ease;
  flex: 1;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;

  &:hover {
    background: ${props => props.$active ? '#26efc2' : 'rgba(29, 233, 182, 0.1)'};
    color: ${props => props.$active ? '#000' : '#1de9b6'};
  }

  &:first-child {
    border-top-left-radius: 999px;
    border-bottom-left-radius: 999px;
  }

  &:last-child {
    border-top-right-radius: 999px;
    border-bottom-right-radius: 999px;
  }

  @media (max-width: 600px) {
    font-size: 11px;
    padding: 0 10px;
  }
`;

// Add drag-and-drop overlay styles
const DragOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(33, 150, 243, 0.08);
  backdrop-filter: blur(2px);
  display: ${props => props.$show ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 9999;
  border: 2px dashed rgba(33, 150, 243, 0.4);
`;

const DragOverlayContent = styled.div`
  background-color: rgba(0, 0, 0, 0.7);
  padding: 24px 32px;
  border-radius: 8px;
  text-align: center;
  color: #2196f3;
  font-size: 18px;
  font-weight: 500;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
`;

const DragOverlayIcon = styled.div`
  font-size: 40px;
  margin-bottom: 8px;
  opacity: 0.8;
`;

const MainContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  padding: 8px;
  box-sizing: border-box;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;

  /* Embed view: reserve right gutter wider than the fixed sidebar.
     Skipped in gallery mode since the sidebar is hidden. */
  ${props => props.$embed && !props.$gallery && `
    padding-right: 200px;
  `}

  /* Gallery mode: no extra padding, no scroll, let zoom fill viewport */
  ${props => props.$gallery && `
    padding: 0;
    min-height: unset;
    overflow: hidden;
  `}

  @media (max-width: 768px) {
    padding: 4px;
  }
`;

// Selection box for multi-select
const SelectionBox = styled.div`
  position: fixed;
  border: 2px solid #2196F3;
  background-color: rgba(33, 150, 243, 0.1);
  pointer-events: none;
  z-index: 9998;
`;

// Canvas ripple overlay for drop animation
const CanvasRipple = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  z-index: 9997;
  opacity: ${props => props.$show ? 1 : 0};
  transition: opacity 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    top: ${props => props.$y}px;
    left: ${props => props.$x}px;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(33, 150, 243, 0.3) 0%, rgba(33, 150, 243, 0.1) 40%, transparent 70%);
    transform: translate(-50%, -50%);
    animation: ${props => props.$show ? 'canvasRipple 1.2s ease-out' : 'none'};
  }

  @keyframes canvasRipple {
    0% {
      width: 0;
      height: 0;
      opacity: 1;
    }
    50% {
      opacity: 0.6;
    }
    100% {
      width: 200vw;
      height: 200vw;
      opacity: 0;
    }
  }
`;

// ---------------------------------------------------------------------------
// Lane annotations (per-scenario): labelled brackets drawn in the side gutters
// next to a range of pick slots (e.g. "MID" spanning slots 14–18). Stored on
// scenario.annotations = [{ id, side:'left'|'right', fromPos, toPos, label }] and
// persisted for free since scenarios are saved as JSON blobs.
// ---------------------------------------------------------------------------

const BRACKET_COLOR = '#ff7a59';
const LANE_GUTTER = 38; // px of interactive gutter on each side of the board
const LANE_FONT = "'Century Gothic', 'Futura', 'Trebuchet MS', sans-serif"; // match box numbers
const BRACE_GAP = 14;   // distance from the box edge to the brace spine
const BRACE_W = 9;      // how far the brace bulges / cusp sticks out
// Approx pixel width of a label's text box (Century Gothic 22px bold ≈ 12px/char).
// Used both to size the label input and to reserve just enough card margin so a
// neighbouring board starts right at the end of the text (no overlap, no big gap).
const laneLabelWidth = (label) => Math.max((label || '').length, 4) * 12 + 14;
// Space from the board's outer edge to where the label text begins
// (brace gap + cusp + small pad).
const LANE_LABEL_OFFSET = BRACE_GAP + 2 * BRACE_W + 6;

const AnnotationLayerWrap = styled.div`
  position: absolute;
  inset: 0;
  overflow: visible;
  z-index: 4;
  pointer-events: none; /* children opt back in */
`;

const GutterZone = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: ${LANE_GUTTER}px;
  /* Sit just OUTSIDE the board edge so the board keeps its width and the
     brackets/labels overflow into the margin. */
  ${p => (p.$side === 'left' ? 'right: 100%;' : 'left: 100%;')}
  pointer-events: auto;
  cursor: crosshair;
  z-index: 5;
  &:hover { background: rgba(255, 122, 89, 0.06); }
`;

const GutterPlus = styled.div`
  position: absolute;
  left: 50%;
  transform: translate(-50%, -50%);
  color: ${BRACKET_COLOR};
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
  pointer-events: none;
  opacity: 0.85;
  text-shadow: 0 0 6px rgba(255, 122, 89, 0.6);
`;

const LaneLabelInput = styled.input`
  position: absolute;
  transform: translateY(-50%);
  background: transparent;
  border: 1px solid transparent;
  border-radius: 3px;
  color: ${BRACKET_COLOR};
  font-family: ${LANE_FONT};
  font-size: 22px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-shadow: 0 0 6px rgba(255, 122, 89, 0.45);
  width: 110px;
  padding: 1px 3px;
  pointer-events: auto;
  z-index: 6;
  cursor: text;
  &:focus { outline: none; background: rgba(0, 0, 0, 0.55); border-color: ${BRACKET_COLOR}; }
  &::placeholder { color: rgba(255, 122, 89, 0.4); font-weight: 600; }
`;

// Invisible drag zone at a brace end — keeps the bracket clean (no visible dots)
// while still letting you drag to resize the span.
const LaneResizeZone = styled.div`
  position: absolute;
  transform: translate(-50%, -50%);
  width: 26px;
  height: 16px;
  cursor: ns-resize;
  pointer-events: auto;
  z-index: 7;
`;

// Small ✕ that appears when a label is focused/hovered — the obvious delete.
const LaneDeleteX = styled.button`
  position: absolute;
  transform: translate(-50%, -50%);
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1px solid ${BRACKET_COLOR};
  background: rgba(0, 0, 0, 0.7);
  color: ${BRACKET_COLOR};
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  pointer-events: auto;
  z-index: 8;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  &:hover { background: ${BRACKET_COLOR}; color: #111; }
`;

/**
 * Lane bracket annotations for one scenario — direct manipulation.
 * Click (or click-drag a range) in a side gutter to create: a single slot makes
 * a text-only label, two or more slots draw a curly brace whose cusp points at
 * the label. Labels edit inline (box-number font); drag a brace end to resize;
 * right-click the label (or clear it) to delete.
 */
function LaneAnnotations({ scenarioId, annotations, boxRefs, onChange, readOnly, toolEnabled = false }) {
  const layerRef = useRef(null);
  const [, force] = useState(0);
  const [hover, setHover] = useState(null); // { side, y }
  const [drag, setDrag] = useState(null);   // { mode, side, startPos, curPos, id }
  const [activeId, setActiveId] = useState(null); // label currently focused (shows ✕)
  const [hoveredId, setHoveredId] = useState(null); // label/✕ hovered (shows ✕)
  const hoverTimer = useRef(null);
  const hoverAnn = (id) => { if (hoverTimer.current) clearTimeout(hoverTimer.current); setHoveredId(id); };
  const leaveAnn = () => { if (hoverTimer.current) clearTimeout(hoverTimer.current); hoverTimer.current = setTimeout(() => setHoveredId(null), 120); };
  const list = annotations || [];
  const sig = JSON.stringify(list);

  // Re-measure from live DOM rects after layout and on resize.
  useLayoutEffect(() => {
    const layer = layerRef.current;
    if (!layer) return undefined;
    const rerender = () => force(n => n + 1);
    const t = setTimeout(rerender, 0);
    const ro = new ResizeObserver(rerender);
    ro.observe(layer);
    window.addEventListener('resize', rerender);
    return () => { clearTimeout(t); ro.disconnect(); window.removeEventListener('resize', rerender); };
  }, [scenarioId, sig]);

  const layer = layerRef.current;
  const lr = layer ? layer.getBoundingClientRect() : null;
  const midX = lr ? lr.left + lr.width / 2 : 0;

  const boxRect = (pos) => {
    const el = boxRefs.current[`${scenarioId}_${pos}`];
    return el ? el.getBoundingClientRect() : null;
  };
  const sideOfRect = (r) => ((r.left + r.width / 2) < midX ? 'left' : 'right');

  // Outer edge of each column (the widest box defines it). Anchoring braces here
  // — rather than to the endpoint boxes — keeps the brace a clean vertical line
  // that clears every box, including the wide hero boxes (120px vs 80px).
  let colLeftEdge = null;
  let colRightEdge = null;
  if (lr) {
    for (let p = 1; p <= 24; p += 1) {
      const r = boxRect(p);
      if (!r) continue;
      if (sideOfRect(r) === 'left') {
        const x = r.left - lr.left;
        colLeftEdge = colLeftEdge === null ? x : Math.min(colLeftEdge, x);
      } else {
        const x = r.right - lr.left;
        colRightEdge = colRightEdge === null ? x : Math.max(colRightEdge, x);
      }
    }
  }

  // Map a clientY to the nearest pick slot within a given side's column.
  const nearestPos = (clientY, side) => {
    let best = null;
    let bestDist = Infinity;
    for (let p = 1; p <= 24; p += 1) {
      const r = boxRect(p);
      if (!r) continue;
      if (sideOfRect(r) !== side) continue;
      if (clientY >= r.top && clientY <= r.bottom) return p;
      const d = Math.abs((r.top + r.bottom) / 2 - clientY);
      if (d < bestDist) { bestDist = d; best = p; }
    }
    return best;
  };

  // Geometry (relative to layer) spanning two slot positions. The vertical span
  // comes from the endpoint boxes; the horizontal anchor is the column's outer
  // edge so the brace never cuts through a wide box in between.
  const measure = (fromPos, toPos) => {
    if (!lr) return null;
    const a = boxRect(fromPos);
    const b = boxRect(toPos);
    if (!a || !b) return null;
    const top = Math.min(a.top, b.top) - lr.top;
    const bottom = Math.max(a.bottom, b.bottom) - lr.top;
    const left = colLeftEdge !== null ? colLeftEdge : Math.min(a.left, b.left) - lr.left;
    const right = colRightEdge !== null ? colRightEdge : Math.max(a.right, b.right) - lr.left;
    return { top, height: bottom - top, left, right };
  };

  // Pointer drag lifecycle (create + resize).
  useEffect(() => {
    if (!drag) return undefined;
    const move = (e) => {
      const pos = nearestPos(e.clientY, drag.side);
      if (pos) setDrag(d => (d && d.curPos !== pos ? { ...d, curPos: pos } : d));
    };
    const up = () => {
      setDrag(d => {
        if (!d) return null;
        const from = Math.min(d.startPos, d.curPos);
        const to = Math.max(d.startPos, d.curPos);
        if (d.mode === 'create') {
          const id = `ann_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
          onChange([...list, { id, side: d.side, fromPos: from, toPos: to, label: '' }]);
          setTimeout(() => {
            const inp = document.querySelector(`[data-lane-input="${id}"]`);
            if (inp) inp.focus();
          }, 30);
        } else if (d.mode === 'resize') {
          onChange(list.map(a => (a.id === d.id ? { ...a, fromPos: from, toPos: to } : a)));
        }
        return null;
      });
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag, sig]);

  const startCreate = (side) => (e) => {
    if (readOnly) return;
    e.preventDefault();
    const pos = nearestPos(e.clientY, side);
    if (!pos) return;
    setDrag({ mode: 'create', side, startPos: pos, curPos: pos });
  };

  const startResize = (ann, end) => (e) => {
    if (readOnly) return;
    e.preventDefault();
    e.stopPropagation();
    const anchor = end === 'top' ? ann.toPos : ann.fromPos; // fixed end
    const moving = end === 'top' ? ann.fromPos : ann.toPos;
    setDrag({ mode: 'resize', side: ann.side, id: ann.id, startPos: anchor, curPos: moving });
  };

  const updateLabel = (id, label) => onChange(list.map(a => (a.id === id ? { ...a, label } : a)));
  const remove = (id) => onChange(list.filter(a => a.id !== id));

  // Curly-brace path: spine sits near the boxes, cusp points toward the label.
  // dir = -1 → cusp points left, +1 → cusp points right.
  const bracePath = (spineX, top, bot, dir) => {
    const mid = (top + bot) / 2;
    const q1 = (top + mid) / 2;
    const q2 = (mid + bot) / 2;
    const w = BRACE_W * dir;
    return [
      `M ${spineX} ${top}`,
      `Q ${spineX + w} ${top} ${spineX + w} ${q1}`,
      `Q ${spineX + w} ${mid} ${spineX + 2 * w} ${mid}`,
      `Q ${spineX + w} ${mid} ${spineX + w} ${q2}`,
      `Q ${spineX + w} ${bot} ${spineX} ${bot}`,
    ].join(' ');
  };

  const rendered = list
    .map(a => ({ a, g: measure(a.fromPos, a.toPos) }))
    .filter(x => x.g);
  const preview = drag
    ? { side: drag.side, g: measure(Math.min(drag.startPos, drag.curPos), Math.max(drag.startPos, drag.curPos)) }
    : null;

  // For a side, the brace spine X and the cusp X (in layer coords).
  const spineOf = (g, side) => (side === 'left' ? g.left - BRACE_GAP : g.right + BRACE_GAP);
  const cuspOf = (spineX, side) => spineX + (side === 'left' ? -2 * BRACE_W : 2 * BRACE_W);

  return (
    <AnnotationLayerWrap ref={layerRef}>
      {/* Interactive gutters (create by click / drag) — only when the bracket
          tool is toggled on, so they don't intercept clicks around the boxes. */}
      {!readOnly && toolEnabled && ['left', 'right'].map(side => (
        <GutterZone
          key={side}
          $side={side}
          onPointerDown={startCreate(side)}
          onMouseMove={(e) => !drag && setHover({ side, y: e.clientY - (lr ? lr.top : 0) })}
          onMouseLeave={() => setHover(h => (h && h.side === side ? null : h))}
        >
          {hover && hover.side === side && !drag && (
            <GutterPlus style={{ top: hover.y }}>+</GutterPlus>
          )}
        </GutterZone>
      ))}

      <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0, overflow: 'visible', pointerEvents: 'none' }}>
        {rendered.map(({ a, g }) => (
          a.fromPos !== a.toPos ? (
            <path
              key={a.id}
              d={bracePath(spineOf(g, a.side), g.top, g.top + g.height, a.side === 'left' ? -1 : 1)}
              fill="none"
              stroke={BRACKET_COLOR}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ filter: 'drop-shadow(0 0 3px rgba(255,122,89,0.5))' }}
            />
          ) : null
        ))}
        {preview && preview.g && Math.min(drag.startPos, drag.curPos) !== Math.max(drag.startPos, drag.curPos) && (
          <path
            d={bracePath(spineOf(preview.g, preview.side), preview.g.top, preview.g.top + preview.g.height, preview.side === 'left' ? -1 : 1)}
            fill="none"
            stroke={BRACKET_COLOR}
            strokeWidth="2.5"
            strokeDasharray="5 4"
            strokeLinecap="round"
            opacity="0.7"
          />
        )}
      </svg>

      {/* Labels + invisible resize zones */}
      {rendered.map(({ a, g }) => {
        const isLeft = a.side === 'left';
        const isBracket = a.fromPos !== a.toPos;
        const cy = g.top + g.height / 2;
        const spineX = spineOf(g, a.side);
        // Where the label anchors: just past the cusp for a brace, or just past
        // the box edge for a text-only label.
        const anchorX = isBracket
          ? cuspOf(spineX, a.side)
          : (isLeft ? g.left - BRACE_GAP : g.right + BRACE_GAP);
        const w = laneLabelWidth(a.label);
        const labelStyle = isLeft
          ? { top: cy, right: `calc(100% - ${anchorX - 5}px)`, width: w, textAlign: 'right' }
          : { top: cy, left: anchorX + 5, width: w, textAlign: 'left' };
        // ✕ sits just past the far (outer) end of the label.
        const xLeft = isLeft ? (anchorX - 5 - w - 4) : (anchorX + 5 + w + 4);
        const showX = !readOnly && (activeId === a.id || hoveredId === a.id);
        return (
          <React.Fragment key={`ui-${a.id}`}>
            <LaneLabelInput
              data-lane-input={a.id}
              value={a.label}
              maxLength={16}
              placeholder="lane"
              readOnly={readOnly}
              onChange={(e) => updateLabel(a.id, e.target.value)}
              onFocus={() => setActiveId(a.id)}
              onMouseEnter={() => hoverAnn(a.id)}
              onMouseLeave={leaveAnn}
              onContextMenu={(e) => { e.preventDefault(); remove(a.id); }}
              onBlur={() => { setActiveId(id => (id === a.id ? null : id)); if (!a.label.trim()) remove(a.id); }}
              style={labelStyle}
              title="Click ✕ to delete"
            />
            {showX && (
              <LaneDeleteX
                title="Delete lane bracket"
                onMouseEnter={() => hoverAnn(a.id)}
                onMouseLeave={leaveAnn}
                onClick={(e) => { e.preventDefault(); remove(a.id); }}
                style={{ top: cy, left: xLeft }}
              >×</LaneDeleteX>
            )}
            {!readOnly && isBracket && (
              <>
                <LaneResizeZone onPointerDown={startResize(a, 'top')} style={{ top: g.top, left: spineX }} />
                <LaneResizeZone onPointerDown={startResize(a, 'bottom')} style={{ top: g.top + g.height, left: spineX }} />
              </>
            )}
          </React.Fragment>
        );
      })}
    </AnnotationLayerWrap>
  );
}

/**
 * DraftBoard Component
 *
 * Displays the draft board with pick boxes for Radiant and Dire
 */

const DraftBoard = ({
  isSharedView = false,
  sharedPermission = null,
  isSimulatorView = false,
  isEmbedView = false, // Hide team/tournament/stage/date inputs, pick-mode toggle, New Draft button
  showButtons = null, // { addScenario, duplicate, delete, clearAll }
  isMarketingDemo = false, // Hide toggles, team/tournament inputs for marketing page
  isDisabledDemo = false, // Disable keyboard interactions when not visible (marketing page)
  maxScenariosDemo = null, // Max scenarios for marketing demo (e.g., 3)
  hideToolbar = false // Hide the action sidebar (undo/redo, Add Scenario, Save Draft, etc.)
}) => {
  // Gallery mode: multiple iframes shown side by side — hide the sidebar and right padding
  const isGalleryView = isEmbedView && new URLSearchParams(window.location.search).get('galleryMode') === '1';
  const {
    scenariosFirstPick,
    scenariosSecondPick,
    pickMode,
    setPickMode,
    addScenario,
    importLiveDraft,
    foreheadPicks,
    setForeheadPicks,
    foreheadConnectors,
    setForeheadConnectors,
    pickTimings,
    setPickTimings,
    duplicateScenario,
    deleteScenario,
    updateScenario,
    reorderScenarios,
    saveDraft,
    newDraft,
    teamName, setTeamName,
    tournament, setTournament,
    stage, setStage,
    userProfile, // Get user tier info
    draftDate, setDraftDate,
    hasUnsavedChanges,
    currentDraftId,
    loadDraft,
    importDraftData,
    mergeDraftData,
    undo,
    redo,
    canUndo,
    canRedo,
    pulledMatchHeroes,
    loadDraftData
  } = useDraft();

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Auto-load draft when opened via ?gsiMatchId=<id> link (from ygbuff)
  useEffect(() => {
    if (!game.features.gsi) return;
    const gsiParam = searchParams.get('gsiMatchId');
    if (!gsiParam) return;
    let active = true;
    (async () => {
      try {
        const draft = await supabaseService.getDraftByGsiMatchId(gsiParam);
        if (!active) return;
        if (draft) {
          loadDraftData(draft);
        }
        // Remove param from URL so refresh doesn't re-load
        setSearchParams({}, { replace: true });
      } catch (e) {
        console.warn('Could not load draft by gsiMatchId:', e);
      }
    })();
    return () => { active = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Use the correct scenarios for the current pick mode
  const scenarios = pickMode === 'first' ? scenariosFirstPick : scenariosSecondPick;

  // Forehead slots — 8 standalone hero slots above the draft boards.
  // State lives on the draft (via DraftContext), so it persists with the
  // saved draft instead of session/localStorage. Reuses the same
  // HeroSelectPopup via the isForeheadMode flag.
  const [isForeheadMode, setIsForeheadMode] = useState(false);

  // Manually-crossed forehead slots (Cmd/Alt+click toggles). Independent of
  // GSI bans; cleared on new match or via the refresh button.
  const [manualCrosses, setManualCrosses] = useState(() => new Set());
  const [foreheadSelection, setForeheadSelection] = useState(() => new Set());
  // Match id for which the user pressed "Refresh" — GSI bans for this match
  // are suppressed until a new match_id arrives.
  const [dismissedMatchId, setDismissedMatchId] = useState(null);
  const dismissedMatchIdRef = useRef(null);
  useEffect(() => { dismissedMatchIdRef.current = dismissedMatchId; }, [dismissedMatchId]);
  const currentMatchIdRef = useRef(null);
  const matchDetectedAtRef = useRef(null); // epoch ms when current match_id was first seen
  const clearForeheadCrosses = useCallback(() => {
    setManualCrosses(new Set());
    setGsiBannedNames(new Set());
    if (currentMatchIdRef.current) {
      setDismissedMatchId(currentMatchIdRef.current);
    }
  }, []);
  const toggleManualCross = (idx) => {
    setManualCrosses(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };
  const lastSeenMatchIdRef = useRef(null);

  // Opponent hero pool (watch-out) loaded from ygbuff
  const [oppPoolOpen, setOppPoolOpen] = useState(false);
  const [oppPoolTeam, setOppPoolTeam] = useState('');
  const [oppPoolTeams, setOppPoolTeams] = useState([]);
  const [oppPoolLoading, setOppPoolLoading] = useState(false);
  const [oppTeamsLoading, setOppTeamsLoading] = useState(false);
  const [oppTeamsError, setOppTeamsError] = useState('');
  const [oppPoolGroups, setOppPoolGroups] = useState([]); // [{player, position, start, end}]
  const [bracketRects, setBracketRects] = useState([]); // [{left, width, top, player, position}]

  const YGBUFF_API = (typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname))
    ? 'http://localhost:6001'
    : 'https://mypersonalgrowth-48cd1.web.app';

  const fetchOppTeams = useCallback(async () => {
    setOppTeamsLoading(true);
    setOppTeamsError('');
    try {
      const r = await fetch(`${YGBUFF_API}/api/intplayer/teams`);
      const teams = await r.json();
      setOppPoolTeams(teams);
      if (teams.length > 0) setOppPoolTeam(teams[0]);
    } catch (err) {
      console.error('[OppPool] fetchOppTeams failed:', err);
      setOppTeamsError(err?.message || String(err) || 'fetch failed');
    } finally {
      setOppTeamsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!oppPoolOpen || oppPoolTeams.length > 0) return;
    fetchOppTeams();
  }, [oppPoolOpen, oppPoolTeams.length, fetchOppTeams]);

  const loadOppPool = async () => {
    if (!oppPoolTeam) return;
    const email = firebaseService.getCurrentUser()?.email;
    if (!email) { alert('Sign in to load opponent pool.'); return; }
    setOppPoolLoading(true);
    try {
      const res = await fetch(`${YGBUFF_API}/api/intplayer/watchout-pool?team=${encodeURIComponent(oppPoolTeam)}&email=${encodeURIComponent(email)}`);
      const pool = await res.json();
      if (!Array.isArray(pool) || pool.length === 0) {
        alert(`No watch-out heroes saved for ${oppPoolTeam}. Cmd+click heroes in the IntPlayer Flex tab first.`);
        return;
      }
      // Group by player; players ordered by role (Carry > Mid > Off > Pos4 > Pos5),
      // heroes within a player sorted by games desc.
      const ROLE_ORDER = { 'Carry': 0, 'Mid': 1, 'Off': 2, 'Pos4': 3, 'Pos5': 4 };
      const roleRank = (p) => (p in ROLE_ORDER ? ROLE_ORDER[p] : 99);
      const byPlayer = new Map();
      for (const item of pool) {
        const key = item.topPlayer || '—';
        if (!byPlayer.has(key)) byPlayer.set(key, []);
        byPlayer.get(key).push(item);
      }
      const playerOrder = [...byPlayer.entries()]
        .map(([player, items]) => {
          items.sort((a, b) => b.topGames - a.topGames);
          return { player, position: items[0]?.topPlayerPosition || '', items, best: items[0]?.topGames || 0 };
        })
        .sort((a, b) => {
          const diff = roleRank(a.position) - roleRank(b.position);
          return diff !== 0 ? diff : b.best - a.best;
        });

      const allH = Object.values(HeroData);
      // 15 boxes is the default/minimum; only grow the row when the pool
      // itself has more resolvable heroes than that (most teams won't).
      const totalResolvable = playerOrder.reduce((sum, p) =>
        sum + p.items.filter(item => allH.some(h => h.name.toLowerCase() === item.heroName.toLowerCase())).length, 0);
      const slotCount = Math.max(FOREHEAD_MIN_SLOTS, totalResolvable);
      const newPicks = Array(slotCount).fill(null);
      const groups = [];
      let slot = 0;
      for (const p of playerOrder) {
        if (slot >= slotCount) break;
        const start = slot;
        for (const item of p.items) {
          if (slot >= slotCount) break;
          const hero = allH.find(h => h.name.toLowerCase() === item.heroName.toLowerCase());
          if (hero) {
            const completeHero = {
              id: hero.id,
              name: hero.name,
              imageUrl: hero.imageUrl || `${game.heroes.imageCdn}${hero.id || hero.name.toLowerCase().replace(/\s/g, '_')}.png`,
              attributes: hero.attributes || {}
            };
            newPicks[slot] = {
              hero: completeHero,
              isBanned: false,
              label: `${item.topGames} ${item.topPlayerWinRate}%`,
              winRate: item.topPlayerWinRate
            };
            slot++;
          }
        }
        if (slot > start) {
          groups.push({ player: p.player, position: p.position, start, end: slot - 1 });
        }
      }
      setForeheadPicks(newPicks);
      setOppPoolGroups(groups);
      setOppPoolOpen(false);
    } catch (e) {
      alert('Failed to load pool: ' + e.message);
    } finally {
      setOppPoolLoading(false);
    }
  };

  // Manual expand/collapse for the hero-pool row, independent of Opp Pool
  // auto-sizing — lets the user hand-pick beyond 15 slots with no pool
  // loaded, or shrink back down. Never removes a filled slot.
  const foreheadMinSlots = Math.max(
    FOREHEAD_MIN_SLOTS,
    oppPoolGroups.length ? Math.max(...oppPoolGroups.map(g => g.end)) + 1 : 0
  );
  const isForeheadExpanded = foreheadPicks.length > foreheadMinSlots;
  const toggleForeheadExpanded = () => {
    if (isForeheadExpanded) {
      let cut = foreheadPicks.length;
      while (cut > foreheadMinSlots && !foreheadPicks[cut - 1]?.hero) cut--;
      setForeheadPicks(foreheadPicks.slice(0, Math.max(cut, foreheadMinSlots)));
    } else {
      setForeheadPicks([...foreheadPicks, ...Array(FOREHEAD_EXPAND_STEP).fill(null)]);
    }
  };

  // GSI-banned hero names — derived from the /draft poll (see below). Suppressed
  // for any match the user dismissed via the Refresh button.
  const [gsiBannedNames, setGsiBannedNames] = useState(new Set());

  // Per-pick timestamps for the live draft (boardPos 1-24 → epochMs when first detected).
  // Used to show time elapsed between consecutive selections.
  const [livePickTimestamps, setLivePickTimestamps] = useState({});
  const livePickOrderRef = useRef([]); // boardPos values in detection order

  // Poll the local GSI server for the live 24-slot draft and upsert a
  // dedicated scenario for the current match. Creates a new scenario when a
  // new match_id is detected; updates picks as Dota emits actions.
  // Skipped in embed view (ygbuff overlay) — GSI is only useful in standalone ygdraftplan.
  // Also skipped entirely for games without GSI (e.g. MLBB has no Game State Integration).
  useEffect(() => {
    if (!game.features.gsi) return;
    if (isEmbedView) return;
    let cancelled = false;
    let lastSig = '';
    const poll = async () => {
      try {
        const res = await fetch('http://localhost:3001/draft', { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const matchId = data?.match_id;
        const actions = Array.isArray(data?.actions) ? data.actions : [];
        currentMatchIdRef.current = matchId || null;
        if (matchId && matchId !== lastSeenMatchIdRef.current) {
          lastSeenMatchIdRef.current = matchId;
          matchDetectedAtRef.current = Date.now(); // record when this match was first seen
          // New match detected — clear all forehead crosses (manual + GSI)
          // and drop any "dismissed for previous match" suppression.
          setManualCrosses(new Set());
          setGsiBannedNames(new Set());
          setDismissedMatchId(null);
          setLivePickTimestamps({});
          livePickOrderRef.current = [];
          setPickTimings([]);
        }

        // Derive GSI ban names from the live action list and apply them,
        // unless the user dismissed bans for this exact match via Refresh.
        if (matchId && matchId !== dismissedMatchIdRef.current) {
          const banNames = new Set(
            actions.filter(a => a && a.is_ban && a.hero_name).map(a => String(a.hero_name).toLowerCase())
          );
          setGsiBannedNames(prev => {
            if (prev.size === banNames.size && [...prev].every(n => banNames.has(n))) return prev;
            return banNames;
          });
        }

        if (!matchId || actions.length === 0) return;
        // Cheap change detection so we don't re-render unnecessarily
        const sig = `${matchId}:${actions.length}:${actions.map(a => a.position + '-' + a.hero_id).join(',')}`;
        if (sig === lastSig) return;
        lastSig = sig;

        // Determine first-pick team from whichever action sits at Python position 1
        const pos1Action = actions.find(a => a && a.position === 1);
        const firstPickTeam = pos1Action?.team || 'radiant';

        // Remap Python server positions → DraftBoard board positions.
        // The Python server already assigns positions 1-24 per the correct protocol
        // (Radiant-first or Dire-first). Both protocols need the same visual correction:
        // the few spots where the protocol order differs from the board's visual slot order.
        // Board left column:  1,2,5,8,10,11,14,15,18,19,21,23  (first-pick team)
        // Board right column: 3,4,6,7,9,12,13,16,17,20,22,24   (second-pick team)
        const posMap = { 2:3, 3:4, 4:2, 5:6, 6:7, 7:5, 21:22, 22:21 };

        const picks = Array(24).fill(null);
        const nowMs = Date.now();
        const newBoardPositions = [];
        for (const a of actions) {
          if (!a || typeof a.position !== 'number' || a.position < 1 || a.position > 24) continue;
          const boardPos = posMap[a.position] ?? a.position;
          picks[boardPos - 1] = {
            hero: {
              id: a.slug || String(a.hero_id),
              name: a.hero_name,
              imageUrl: a.image_url,
              attributes: {},
            },
            isBanned: false,
          };
          if (!livePickOrderRef.current.includes(boardPos)) {
            newBoardPositions.push(boardPos);
          }
        }
        if (newBoardPositions.length > 0) {
          newBoardPositions.forEach(bp => livePickOrderRef.current.push(bp));
          setLivePickTimestamps(prev => {
            const next = { ...prev };
            newBoardPositions.forEach(bp => { next[bp] = nowMs; });
            return next;
          });
          // Append timing entries; delta computed at analytics time from detectedAt
          setPickTimings(prev => {
            const next = [...prev];
            newBoardPositions.forEach(bp => {
              const seqIdx = livePickOrderRef.current.lastIndexOf(bp);
              const action = actions.find(a => (posMap[a.position] ?? a.position) === bp);
              // For seq=1, baseline is match detection time so analytics can compute correct delta
              const baselineAt = seqIdx === 0
                ? (matchDetectedAtRef.current || nowMs)
                : nowMs;
              next.push({
                seq: seqIdx + 1,
                boardPos: bp,
                heroName: action?.hero_name || '',
                detectedAt: nowMs,
                baselineAt: seqIdx === 0 ? baselineAt : undefined,
              });
            });
            return next;
          });
        }
        importLiveDraft(matchId, picks, { title: `Live ${matchId}` });
      } catch (_) {}
    };
    poll();
    const id = setInterval(poll, 1500);
    return () => { cancelled = true; clearInterval(id); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importLiveDraft, setPickTimings]);

  const handleForeheadBoxClick = (slotIndex, e) => {
    if (isDisabledDemo) return;
    // Cmd/Ctrl/Alt+click toggles a manual cross on just that slot
    if (e && (e.metaKey || e.ctrlKey || e.altKey)) {
      e.preventDefault();
      e.stopPropagation();
      toggleManualCross(slotIndex);
      return;
    }
    // Shift+click toggles multi-selection without opening picker
    if (e && e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      setForeheadSelection(prev => {
        const next = new Set(prev);
        if (next.has(slotIndex)) next.delete(slotIndex);
        else next.add(slotIndex);
        return next;
      });
      return;
    }
    // Regular click: clear any forehead multi-selection, open picker
    setForeheadSelection(new Set());
    setIsForeheadMode(true);
    setSelectedBox(slotIndex + 1);
    setShowPopup(true);
    if (selectedPositions.size > 0) setSelectedPositions(new Set());
  };

  const handleForeheadContextMenu = (e, slotIndex) => {
    e.preventDefault();
    e.stopPropagation();
    setForeheadPicks(prev => { const next = [...prev]; next[slotIndex] = null; return next; });
    setForeheadConnectors(prev => prev.filter(c => c.from !== slotIndex && c.to !== slotIndex));
  };

  // UI state
  const [selectedBox, setSelectedBox] = useState(-1);
  const [focusedBox, setFocusedBox] = useState({ scenarioIndex: 0, position: 1 }); // For keyboard navigation
  const [heroSearch, setHeroSearch] = useState('');
  const [showPopup, setShowPopup] = useState(false);


  const [activeScenarioIndex, setActiveScenarioIndex] = useState(0);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [showMergeReplaceDialog, setShowMergeReplaceDialog] = useState(false);
  const [pendingFileImport, setPendingFileImport] = useState(null);
  const popupRef = useRef(null);
  const boxRefs = useRef({});
  const foreheadBoxRefs = useRef({});
  const picksContainerRefs = useRef({});
  const scenarioRefs = useRef({});
  const [isExportingJpeg, setIsExportingJpeg] = useState(false);

  // Counter-relation connector drag state
  // { scenarioId, fromPosition, mouseX, mouseY } — mouse coords are in viewport space
  const [connectorDrag, setConnectorDrag] = useState(null);
  // Bumped to force connector SVG re-measure after layout changes (resize, scroll, swap)
  const [connectorTick, setConnectorTick] = useState(0);
  const scrollContainerRef = useRef(null);

  // Forehead row connector drag state (transient, not persisted)
  const [foreheadConnectorDrag, setForeheadConnectorDrag] = useState(null); // { fromIdx, mouseX, mouseY }
  const foreheadRowRef = useRef(null);

  // Measure pixel positions of each player-group bracket over the forehead
  // boxes. When the row wraps to 2+ lines (more than ~15 boxes fit on one
  // line), a group can straddle the wrap — split it into one bracket rect
  // per contiguous same-row run instead of dropping it, and tag each with
  // its own row index so it renders above ITS row, not always row 0.
  useLayoutEffect(() => {
    if (!oppPoolGroups.length || !foreheadRowRef.current) { setBracketRects([]); return; }
    const measure = () => {
      const rowEl = foreheadRowRef.current;
      if (!rowEl) return;
      const rowBox = rowEl.getBoundingClientRect();
      const rowTops = []; // viewport Y of each distinct visual row, in order seen
      const rowIndexOf = (top) => {
        let idx = rowTops.findIndex((t) => Math.abs(t - top) <= 4);
        if (idx === -1) { rowTops.push(top); idx = rowTops.length - 1; }
        return idx;
      };
      const maxIdx = Math.max(...oppPoolGroups.map((g) => g.end));
      const boxRow = {};
      for (let i = 0; i <= maxIdx; i++) {
        const el = foreheadBoxRefs.current[i];
        if (!el) continue;
        boxRow[i] = rowIndexOf(el.getBoundingClientRect().top);
      }
      const rects = [];
      oppPoolGroups.forEach((g, groupIndex) => {
        let runStart = g.start;
        for (let i = g.start; i <= g.end + 1; i++) {
          const sameRow = i <= g.end && boxRow[i] === boxRow[runStart];
          if (sameRow) continue;
          const a = foreheadBoxRefs.current[runStart];
          const b = foreheadBoxRefs.current[i - 1];
          if (a && b) {
            const ar = a.getBoundingClientRect();
            const br = b.getBoundingClientRect();
            rects.push({
              left: ar.left - rowBox.left,
              width: (br.left + br.width) - ar.left,
              row: boxRow[runStart],
              rowTop: rowTops[boxRow[runStart]] - rowBox.top,
              player: g.player,
              position: g.position,
              start: runStart,
              end: i - 1,
              groupIndex,
              groupStart: g.start,
              groupEnd: g.end,
            });
          }
          runStart = i;
        }
      });
      setBracketRects(rects);
    };
    measure();
    // A plain resize listener misses the case that actually bit us: right
    // after Opp Pool data loads, the row's own wrap layout can still be
    // settling (fonts/scrollbar/sibling reflow) a tick after this effect's
    // first synchronous measure — window never resizes, so that listener
    // never re-fires, and the row-1-vs-row-2 assignment taken during that
    // transient state sticks until some unrelated re-render forces another
    // pass. Observe the row element itself so any actual layout change —
    // including that settle — retriggers measurement.
    const ro = new ResizeObserver(measure);
    ro.observe(foreheadRowRef.current);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [oppPoolGroups, foreheadPicks]);

  const searchInputRef = useRef(null); // Ref for hero search input
  const [focusedHeroIndex, setFocusedHeroIndex] = useState(-1); // -1 means focus is in search input, >= 0 means hero grid
  const heroRefs = useRef({}); // Refs for hero option elements

  // Multi-select state
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState({ x: 0, y: 0 });
  const [selectionEnd, setSelectionEnd] = useState({ x: 0, y: 0 });
  const [selectedPositions, setSelectedPositions] = useState(new Set());
  // Refs for use in event handlers (avoid stale closure on first mousemove after mousedown)
  const isSelectingRef = useRef(false);
  const selectionStartRef = useRef({ x: 0, y: 0 });

  // Clipboard state for copy-paste (now stores array of picks with positions)
  const [clipboard, setClipboard] = useState([]);

  // Drag-and-drop scenario reordering state
  const [draggedScenarioId, setDraggedScenarioId] = useState(null);
  const [dragOverScenarioId, setDragOverScenarioId] = useState(null);
  const [isScenarioDragging, setIsScenarioDragging] = useState(false);
  const [droppedScenarioId, setDroppedScenarioId] = useState(null);
  const [ripplePosition, setRipplePosition] = useState({ x: 0, y: 0, show: false });

  // Duplicate prevention toggle
  const [preventDuplicates, setPreventDuplicates] = useState(true);

  // Mobile landscape view toggle: 'board' (default) | 'drafter'
  const [mobileLandscapeView, setMobileLandscapeView] = useState('board');

  // OpenDota highlight toggle
  const [showOpenDotaHighlights, setShowOpenDotaHighlights] = useState(false);
  // Lane-bracket drawing tool. Default OFF so the side gutters don't intercept
  // clicks/drags around the boxes — toggle on only when you want to draw brackets.
  const [bracketToolEnabled, setBracketToolEnabled] = useState(false);

  // Per-scenario swap sides state (stored in scenario object)
  // Each scenario will have its own swapSides property

  // Practice matchup overlay state
  const [showPracticeMatchup, setShowPracticeMatchup] = useState(false);
  const [practiceScenario, setPracticeScenario] = useState(null);
  const [practicePickMode, setPracticePickMode] = useState(null);
  const [showAllMatchups, setShowAllMatchups] = useState(false);

  // Scaling features dialogs state
  const [showShareDialog, setShowShareDialog] = useState(false);

  // Copy scenario state
  const [copySourceScenario, setCopySourceScenario] = useState(null);
  const [copySourcePickMode, setCopySourcePickMode] = useState(null);
  const [showScenarioSelection, setShowScenarioSelection] = useState(false);
  const [pendingCopyToScenarioId, setPendingCopyToScenarioId] = useState(null);
  const [showBothOverlay, setShowBothOverlay] = useState(false);

  // Save feedback toast
  const [saveToast, setSaveToast] = useState(null); // { message, type: 'success'|'warn'|'error' }

  // Ref for draft content
  const draftContentRef = useRef(null);

  // Handler to save draft to Firebase before sharing
  const handleOpenShareDialog = async () => {
    try {
      // Always save/update draft to Firebase before sharing (even if it already exists)
      // This ensures the latest changes are available via the share link

      // Sanitize scenarios to remove any non-serializable data
      const sanitizeScenarios = (scenarios) => {
        if (!scenarios || !Array.isArray(scenarios)) return [];

        return scenarios.map(scenario => {
          // Sanitize picks array - preserve position indices (don't filter out nulls!)
          // The picks array MUST maintain exactly 24 positions to preserve draft layout
          const sanitizedPicks = (scenario.picks || []).map(pick => {
            if (!pick) return null; // Preserve empty positions as null

            // If pick is an object with hero data, extract the hero object
            if (typeof pick === 'object' && pick !== null) {
              if (pick.hero) {
                // Pick has { hero: {...}, isBanned: ... } structure
                return {
                  hero: pick.hero,
                  isBanned: pick.isBanned || false
                };
              } else if ('id' in pick) {
                // Pick is just a hero object { id: ..., name: ... }
                return { hero: pick, isBanned: false };
              }
            }

            // Otherwise return as-is (should be a number or null)
            return pick;
          });

          const sanitized = {
            id: scenario.id || 0,
            name: scenario.name || '',
            picks: sanitizedPicks,
            fromMatchPull: scenario.fromMatchPull === true,
            matchColorIndex: typeof scenario.matchColorIndex === 'number' ? scenario.matchColorIndex : 0,
            matchId: scenario.matchId || '',
            swapSides: scenario.swapSides === true,
            neonHighlight: scenario.neonHighlight === true,
          };

          // Only include practiceMatchups if it exists (Firebase doesn't allow undefined)
          if (scenario.practiceMatchups) {
            sanitized.practiceMatchups = scenario.practiceMatchups;
          }

          if (Array.isArray(scenario.counterConnectors) && scenario.counterConnectors.length > 0) {
            sanitized.counterConnectors = scenario.counterConnectors
              .filter(c => c && typeof c.from === 'number' && typeof c.to === 'number')
              .map(c => ({ from: c.from, to: c.to }));
          }

          return sanitized;
        });
      };

      const draftData = {
        id: currentDraftId || null, // Use existing draft ID if available
        scenariosFirstPick: sanitizeScenarios(scenariosFirstPick),
        scenariosSecondPick: sanitizeScenarios(scenariosSecondPick),
        teamName: teamName || '',
        tournament: tournament || '',
        stage: stage || '',
        date: draftDate || new Date().toISOString().slice(0, 10),
        title: `${teamName || 'Untitled'} - ${tournament || 'Draft'}`,
        isPublic: true, // Save to main collection for share link access
      };

      console.log('Saving draft to Firebase before sharing:', JSON.stringify(draftData, null, 2));

      await firebaseService.saveDraft(draftData);

      setShowShareDialog(true);
    } catch (error) {
      console.error('Error saving draft to Firebase:', error);
      alert(`Failed to save draft to Firebase: ${error.message}`);
    }
  };

  // Hero filtering logic with improved search (memoized to prevent excessive re-renders)
  const allHeroes = React.useMemo(() => Object.values(HeroData), []);
  const filteredHeroes = React.useMemo(() => heroSearch
    ? allHeroes.filter(hero => {
        const searchTerm = heroSearch.toLowerCase().trim();
        const heroName = hero.name.toLowerCase();
        
        // Direct name match
        if (heroName.includes(searchTerm)) {
          return true;
        }
        
        // Split hero name into words (handle spaces, hyphens, and apostrophes)
        const words = heroName.split(/[\s\-']+/).filter(word => word.length > 0);
        
        // Create abbreviation from first letters
        const abbreviation = words.map(word => word.charAt(0)).join('');
        
        // Check exact abbreviation match (e.g., "aa" -> "Ancient Apparition")
        if (abbreviation === searchTerm) {
          return true;
        }
        
        // Check partial abbreviation match (e.g., "a" matches "Ancient Apparition")
        if (abbreviation.startsWith(searchTerm)) {
          return true;
        }
        
        // Check if search term matches any word start
        if (words.some(word => word.startsWith(searchTerm))) {
          return true;
        }
        
        // Special case: if search is very short, be more restrictive
        if (searchTerm.length === 1) {
          return abbreviation.startsWith(searchTerm) || words.some(word => word.startsWith(searchTerm));
        }
        
        return false;
      }).sort((a, b) => {
        // Sort results by relevance
        const searchTerm = heroSearch.toLowerCase().trim();
        const aName = a.name.toLowerCase();
        const bName = b.name.toLowerCase();
        
        // Exact name starts get priority
        const aStartsWith = aName.startsWith(searchTerm);
        const bStartsWith = bName.startsWith(searchTerm);
        if (aStartsWith && !bStartsWith) return -1;
        if (bStartsWith && !aStartsWith) return 1;
        
        // Then abbreviation matches
        const aWords = aName.split(/[\s\-']+/).filter(word => word.length > 0);
        const bWords = bName.split(/[\s\-']+/).filter(word => word.length > 0);
        const aAbbr = aWords.map(word => word.charAt(0)).join('');
        const bAbbr = bWords.map(word => word.charAt(0)).join('');
        
        const aAbbrMatch = aAbbr === searchTerm;
        const bAbbrMatch = bAbbr === searchTerm;
        if (aAbbrMatch && !bAbbrMatch) return -1;
        if (bAbbrMatch && !aAbbrMatch) return 1;
        
        // Finally alphabetical
        return aName.localeCompare(bName);
      })
    : allHeroes, [heroSearch, allHeroes]);

  // Set active scenario to the first one on pickMode change
  useEffect(() => {
    if (scenarios.length > 0) {
      setActiveScenarioIndex(0);
    }
  }, [pickMode]);

  // Center the first scenario only on pickMode change (not when scenarios are added/duplicated)
  useEffect(() => {
    const centerFirstScenario = () => {
      if (scenarios.length > 0 && scrollContainerRef.current && scenarioRefs.current[scenarios[0].id]) {
        // Use a small delay to ensure the layout is rendered
        setTimeout(() => {
          const firstScenario = scenarioRefs.current[scenarios[0].id];
          const container = scrollContainerRef.current;
          
          if (firstScenario && container) {
            const containerRect = container.getBoundingClientRect();
            const scenarioRect = firstScenario.getBoundingClientRect();
            
            // Calculate the scroll position to center the first scenario
            const scrollLeft = firstScenario.offsetLeft - (container.clientWidth / 2) + (scenarioRect.width / 2);
            
            container.scrollTo({
              left: scrollLeft,
              behavior: 'smooth'
            });
          }
        }, 100);
      }
    };

    centerFirstScenario();
  }, [pickMode]); // Only center when pickMode changes, not when scenarios are added

  // Also center on initial mount
  useEffect(() => {
    const initialCenter = () => {
      if (scenarios.length > 0 && scrollContainerRef.current && scenarioRefs.current[scenarios[0].id]) {
        setTimeout(() => {
          const firstScenario = scenarioRefs.current[scenarios[0].id];
          const container = scrollContainerRef.current;
          
          if (firstScenario && container) {
            const containerRect = container.getBoundingClientRect();
            const scenarioRect = firstScenario.getBoundingClientRect();
            
            // Calculate the scroll position to center the first scenario
            const scrollLeft = firstScenario.offsetLeft - (container.clientWidth / 2) + (scenarioRect.width / 2);
            
            container.scrollTo({
              left: scrollLeft,
              behavior: 'auto' // No animation for initial positioning
            });
          }
        }, 300); // Longer delay for initial mount to ensure layout is complete
      }
    };

    initialCenter();
  }, []); // Empty dependency array for mount only

  // Get the active scenario by index
  const activeScenario = scenarios[activeScenarioIndex] || scenarios[0];

  // Add scenario handler
  const handleAddScenario = React.useCallback(() => {
    // Check if we've reached the max scenarios limit (for marketing demo)
    if (maxScenariosDemo && scenarios.length >= maxScenariosDemo) {
      return; // Don't add if limit reached
    }
    addScenario(pickMode);
  }, [addScenario, pickMode, maxScenariosDemo, scenarios.length]);

  // useEffect to handle pending copy operation when new scenario is added
  useEffect(() => {
    if (pendingCopyToScenarioId && copySourceScenario) {
      const currentScenarios = pickMode === 'first' ? scenariosFirstPick : scenariosSecondPick;
      const newScenario = currentScenarios.find(s => s.id === pendingCopyToScenarioId);

      if (newScenario) {
        // Update the new scenario with the copied data
        const updateData = {
          picks: copySourceScenario.picks ? [...copySourceScenario.picks] : Array(24).fill(null),
          title: copySourceScenario.title ? `${copySourceScenario.title} (copy)` : '',
          practiceMatchups: copySourceScenario.practiceMatchups ? { ...copySourceScenario.practiceMatchups } : {},
          swapSides: copySourceScenario.swapSides || false, // Preserve swap state
        };

        // Preserve match pull properties (border color) when copying
        if (copySourceScenario.fromMatchPull) {
          updateData.fromMatchPull = true;
          updateData.matchColorIndex = copySourceScenario.matchColorIndex;
        }

        updateScenario(pickMode, newScenario.id, updateData);

        // Show the visual drop indicator (ripple effect)
        setDroppedScenarioId(newScenario.id);

        // Scroll to the new scenario
        setTimeout(() => {
          const scenarioEl = scenarioRefs.current[newScenario.id];
          if (scenarioEl && scrollContainerRef.current) {
            scenarioEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'end' });
          }

          // Clear the drop indicator after animation
          setTimeout(() => {
            setDroppedScenarioId(null);
          }, 600);
        }, 100);

        // Clear the pending state
        setPendingCopyToScenarioId(null);
        setCopySourceScenario(null);
      }
    }
  }, [pendingCopyToScenarioId, copySourceScenario, scenariosFirstPick, scenariosSecondPick, pickMode, updateScenario]);

  // Handle swap button for pulled boards - syncs between both pick modes
  const handleSwapPulledBoard = (scenario) => {
    const newSwapState = !(scenario.swapSides || false);

    // If this is a pulled board (has matchColorIndex), sync with matching board in other mode
    if (scenario.fromMatchPull && scenario.matchColorIndex !== undefined) {
      // Update current scenario
      updateScenario(pickMode, scenario.id, { swapSides: newSwapState });

      // Find matching scenario in the other pick mode with same matchColorIndex
      const otherModeScenarios = pickMode === 'first' ? scenariosSecondPick : scenariosFirstPick;
      const matchingScenario = otherModeScenarios.find(
        s => s.fromMatchPull && s.matchColorIndex === scenario.matchColorIndex
      );

      if (matchingScenario) {
        const otherPickMode = pickMode === 'first' ? 'second' : 'first';
        updateScenario(otherPickMode, matchingScenario.id, { swapSides: newSwapState });
      }
    } else {
      // Not a pulled board, just update normally
      updateScenario(pickMode, scenario.id, { swapSides: newSwapState });
    }
  };

  // Update a scenario's picks in the current pick mode
  const updateScenarioPicks = (scenarioId, newPicks) => updateScenario(pickMode, scenarioId, { picks: newPicks });

  // ---------- Counter-relation connectors ----------
  const isReadOnly = isSimulatorView || (isSharedView && sharedPermission !== 'edit') || isDisabledDemo;

  const addCounterConnector = useCallback((scenarioId, fromPos, toPos) => {
    if (fromPos === toPos) return;
    const list = pickMode === 'first' ? scenariosFirstPick : scenariosSecondPick;
    const sc = list.find(s => s.id === scenarioId);
    if (!sc) return;
    const existing = Array.isArray(sc.counterConnectors) ? sc.counterConnectors : [];
    if (existing.some(c => c.from === fromPos && c.to === toPos)) return;
    updateScenario(pickMode, scenarioId, { counterConnectors: [...existing, { from: fromPos, to: toPos }] });
  }, [pickMode, scenariosFirstPick, scenariosSecondPick, updateScenario]);

  const removeCounterConnector = useCallback((scenarioId, index) => {
    const list = pickMode === 'first' ? scenariosFirstPick : scenariosSecondPick;
    const sc = list.find(s => s.id === scenarioId);
    if (!sc || !Array.isArray(sc.counterConnectors)) return;
    const next = sc.counterConnectors.filter((_, i) => i !== index);
    updateScenario(pickMode, scenarioId, { counterConnectors: next });
  }, [pickMode, scenariosFirstPick, scenariosSecondPick, updateScenario]);

  const handleConnectorHandleMouseDown = useCallback((e, scenarioId, position) => {
    if (isReadOnly) return;
    e.stopPropagation();
    e.preventDefault();
    setConnectorDrag({ scenarioId, fromPosition: position, mouseX: e.clientX, mouseY: e.clientY });
  }, [isReadOnly]);

  // Long-press to start a connector drag on touch devices.
  // Press a filled hero ~450ms without moving -> connector drag begins; slide finger to target and release.
  const longPressTimerRef = useRef(null);
  const touchStartRef = useRef(null);

  const cancelLongPress = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    touchStartRef.current = null;
  }, []);

  const handleBoxTouchStart = useCallback((e, scenarioId, position, isFilled) => {
    if (isReadOnly || !isFilled) return;
    if (e.touches.length !== 1) return;
    const t = e.touches[0];
    touchStartRef.current = { scenarioId, position, x: t.clientX, y: t.clientY };
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = setTimeout(() => {
      longPressTimerRef.current = null;
      const start = touchStartRef.current;
      if (!start) return;
      if (window.navigator.vibrate) window.navigator.vibrate(35);
      // Seed mouseX/Y from the source box's bbox center so the arrow starts zero-length.
      // Touch clientX/Y and getBoundingClientRect live in different coord spaces under
      // CSS `zoom: 0.72` on mobile portrait (Safari iOS) — using touch coords here makes
      // the arrow tip jump to a wrong location at the start of the drag.
      const boxEl = boxRefs.current[`${start.scenarioId}_${start.position}`];
      let mx = start.x, my = start.y;
      if (boxEl) {
        const r = boxEl.getBoundingClientRect();
        mx = r.left + r.width / 2;
        my = r.top + r.height / 2;
      }
      setConnectorDrag({ scenarioId: start.scenarioId, fromPosition: start.position, mouseX: mx, mouseY: my });
    }, 450);
  }, [isReadOnly]);

  const handleBoxTouchMove = useCallback((e) => {
    if (!longPressTimerRef.current) return;
    const start = touchStartRef.current;
    if (!start || e.touches.length === 0) return;
    const t = e.touches[0];
    if (Math.abs(t.clientX - start.x) > 10 || Math.abs(t.clientY - start.y) > 10) {
      cancelLongPress();
    }
  }, [cancelLongPress]);

  // Lock page scroll + disable touch-action while a connector drag is active.
  // Prevents Chrome from interpreting touchmove as a scroll on zoomed mobile portrait.
  useEffect(() => {
    if (!connectorDrag) return;
    const prevTouchAction = document.body.style.touchAction;
    const prevOverscroll = document.body.style.overscrollBehavior;
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.touchAction = 'none';
    document.body.style.overscrollBehavior = 'none';
    document.body.style.userSelect = 'none';
    return () => {
      document.body.style.touchAction = prevTouchAction;
      document.body.style.overscrollBehavior = prevOverscroll;
      document.body.style.userSelect = prevUserSelect;
    };
  }, [connectorDrag ? connectorDrag.scenarioId : null]); // eslint-disable-line react-hooks/exhaustive-deps

  // Document-level drag tracking (mouse + touch)
  useEffect(() => {
    if (!connectorDrag) return;

    const tryCommit = (clientX, clientY) => {
      const el = document.elementFromPoint(clientX, clientY);
      const boxEl = el && el.closest && el.closest('[data-connector-target="true"]');
      if (boxEl) {
        const sid = Number(boxEl.getAttribute('data-scenario-id'));
        const pos = Number(boxEl.getAttribute('data-position'));
        const dragSid = Number(connectorDrag.scenarioId);
        if (Number.isFinite(sid) && Number.isFinite(pos) && pos > 0 && sid === dragSid) {
          addCounterConnector(connectorDrag.scenarioId, connectorDrag.fromPosition, pos);
        }
      }
    };

    const onMove = (e) => {
      setConnectorDrag(prev => prev ? { ...prev, mouseX: e.clientX, mouseY: e.clientY } : prev);
    };
    const onUp = (e) => {
      tryCommit(e.clientX, e.clientY);
      setConnectorDrag(null);
    };
    const onTouchMove = (e) => {
      if (e.touches.length === 0) return;
      // Block page scroll while the connector drag is active.
      if (e.cancelable) e.preventDefault();
      const t = e.touches[0];
      // Snap arrow tip to whatever target box is under the finger (via elementFromPoint),
      // using that box's bbox center. Keeps the tip in the same coord space as cRect —
      // raw touch.clientX/Y disagrees with getBoundingClientRect under CSS `zoom` on
      // mobile portrait (Safari iOS), which caused the arrow to drift off the finger.
      const el = document.elementFromPoint(t.clientX, t.clientY);
      const boxEl = el && el.closest && el.closest('[data-connector-target="true"]');
      if (boxEl) {
        const r = boxEl.getBoundingClientRect();
        const cx = r.left + r.width / 2;
        const cy = r.top + r.height / 2;
        setConnectorDrag(prev => prev ? { ...prev, mouseX: cx, mouseY: cy } : prev);
      }
    };
    const onTouchEnd = (e) => {
      const t = e.changedTouches && e.changedTouches[0];
      if (t) tryCommit(t.clientX, t.clientY);
      setConnectorDrag(null);
    };
    const onTouchCancel = () => setConnectorDrag(null);

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd);
    window.addEventListener('touchcancel', onTouchCancel);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', onTouchCancel);
    };
  }, [connectorDrag, addCounterConnector]);

  // Re-measure connectors on window resize / scroll
  useEffect(() => {
    const bump = () => setConnectorTick(t => t + 1);
    window.addEventListener('resize', bump);
    window.addEventListener('scroll', bump, true);
    return () => {
      window.removeEventListener('resize', bump);
      window.removeEventListener('scroll', bump, true);
    };
  }, []);

  // Bump tick after every commit so SVGs recompute against the latest DOM rects.
  // Using useLayoutEffect runs before paint so the user sees the line on the same frame.
  useLayoutEffect(() => {
    setConnectorTick(t => t + 1);
  }, [scenarios, pickMode]);

  // Forehead connector drag tracking
  useEffect(() => {
    if (!foreheadConnectorDrag) return;
    const onMove = (e) => {
      setForeheadConnectorDrag(prev => prev ? { ...prev, mouseX: e.clientX, mouseY: e.clientY } : prev);
    };
    const onUp = (e) => {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const boxEl = el && el.closest && el.closest('[data-forehead-target="true"]');
      if (boxEl) {
        const toIdx = Number(boxEl.getAttribute('data-forehead-idx'));
        if (Number.isFinite(toIdx) && toIdx !== foreheadConnectorDrag.fromIdx) {
          setForeheadConnectors(prev => [...prev, { from: foreheadConnectorDrag.fromIdx, to: toIdx }]);
        }
      }
      setForeheadConnectorDrag(null);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [foreheadConnectorDrag]);

  // Handle box click to show hero selection popup
  const handleBoxClick = (scenarioIndex, position) => {
    // Disable interactions when demo is not visible (marketing page)
    if (isDisabledDemo) {
      return;
    }

    setIsForeheadMode(false);
    setActiveScenarioIndex(scenarioIndex);
    setSelectedBox(position);
    setShowPopup(true);
    // Don't clear heroSearch or input value here - they may have been set by keyboard handler
    // setHeroSearch(''); // REMOVED - let keyboard handler set this if needed
    // searchInputRef.current.value = ''; // REMOVED - let keyboard handler set this if needed
    // Clear multi-selection on normal click
    if (selectedPositions.size > 0) {
      setSelectedPositions(new Set());
    }
  };

  // Handle hero selection from popup
  const handleHeroSelect = useCallback((hero) => {
    if (selectedBox === -1) return;

    // Create a complete hero object with all required properties
    const completeHero = {
      id: hero.id,
      name: hero.name,
      imageUrl: hero.imageUrl || `${game.heroes.imageCdn}${hero.id || hero.name.toLowerCase().replace(/\s/g, '_')}.png`,
      attributes: hero.attributes || {}
    };

    if (isForeheadMode) {
      setForeheadPicks(prev => {
        const next = [...prev];
        // If duplicates are prevented, clear any other forehead slot that has this hero
        if (preventDuplicates) {
          for (let i = 0; i < next.length; i++) {
            if (i !== selectedBox - 1 && next[i]?.hero?.id === completeHero.id) {
              next[i] = null;
            }
          }
        }
        next[selectedBox - 1] = { hero: completeHero, isBanned: false };
        return next;
      });
      setShowPopup(false);
      return;
    }

    if (!activeScenario) return;

    // Create a copy of the current picks array
    const newPicks = activeScenario.picks ?
      [...activeScenario.picks] :
      Array(24).fill(null);

    // Check if this hero already exists somewhere else on the board (only if preventDuplicates is enabled)
    // If it does, remove it from the old position (but not if it's the same position we're updating)
    if (preventDuplicates) {
      for (let i = 0; i < newPicks.length; i++) {
        const existingPick = newPicks[i];
        const existingPosition = i + 1; // Convert to 1-based position

        // Skip if this is the position we're updating
        if (existingPosition === selectedBox) {
          continue;
        }

        // Check for duplicates by ID or name (fallback for any edge cases)
        const isDuplicateById = existingPick?.hero?.id && existingPick.hero.id === completeHero.id;
        const isDuplicateByName = existingPick?.hero?.name && existingPick.hero.name === completeHero.name;

        if (isDuplicateById || isDuplicateByName) {
          newPicks[i] = null;
          break; // Only remove the first occurrence
        }
      }
    }

    // Update the selected position with the new hero
    newPicks[selectedBox - 1] = {
      hero: completeHero,
      isBanned: false
    };

    // Update the scenario with the new picks
    updateScenarioPicks(activeScenario.id, newPicks);
    setShowPopup(false);

    // Set focus back to the box after selection
    setTimeout(() => {
      const scenario = scenarios[activeScenarioIndex];
      if (scenario) {
        const boxKey = `${scenario.id}_${selectedBox}`;
        const boxElement = boxRefs.current[boxKey];
        if (boxElement) {
          boxElement.focus();
        }
      }
    }, 100);
  }, [selectedBox, activeScenario, preventDuplicates, scenarios, activeScenarioIndex, updateScenarioPicks, isForeheadMode]);

  // Handle toggling a hero as banned
  const toggleBan = () => {
    if (selectedBox === -1 || !activeScenario || !activeScenario.picks[selectedBox - 1]) return;
    const newPicks = [...activeScenario.picks];
    newPicks[selectedBox - 1] = {
      ...newPicks[selectedBox - 1],
      isBanned: !newPicks[selectedBox - 1].isBanned
    };
    updateScenarioPicks(activeScenario.id, newPicks);
  };

  // Handle removing a hero
  const removeHero = () => {
    if (selectedBox === -1) return;
    if (isForeheadMode) {
      const idx = selectedBox - 1;
      setForeheadPicks(prev => { const next = [...prev]; next[idx] = null; return next; });
      setForeheadConnectors(prev => prev.filter(c => c.from !== idx && c.to !== idx));
      setShowPopup(false);
      return;
    }
    if (!activeScenario) return;
    const newPicks = [...activeScenario.picks];
    newPicks[selectedBox - 1] = null;
    updateScenarioPicks(activeScenario.id, newPicks);
    setShowPopup(false);
  };

  // Handle right-click on hero box - remove hero immediately (original behavior)
  const handleContextMenu = (e, scenarioId, position) => {
    e.preventDefault();
    e.stopPropagation();

    const scenario = scenarios.find(s => s.id === scenarioId);
    const pick = scenario?.picks?.[position - 1];

    // Remove hero immediately on right-click
    if (pick?.hero) {
      const newPicks = [...scenario.picks];
      newPicks[position - 1] = null;
      updateScenario(pickMode, scenarioId, { picks: newPicks });
    }
  };

  // Note: handleHeroGridKeyDown is now defined inside the useEffect below to avoid stale closure issues

  // Attach keyboard handler for hero grid navigation
  useEffect(() => {
    if (!showPopup) return;

    const handler = (e) => {
      const GRID_COLUMNS = 4;

      // Use callback form to get the latest focusedHeroIndex
      setFocusedHeroIndex(currentIndex => {
        // If not in grid navigation mode, return current
        if (currentIndex < 0) return currentIndex;

        const totalHeroes = filteredHeroes.length;
        let newIndex = currentIndex;

        if (e.key === 'ArrowDown') {
          e.preventDefault();
          const potentialIndex = currentIndex + GRID_COLUMNS;
          if (potentialIndex < totalHeroes) {
            newIndex = potentialIndex;
          }
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          // Move up one row (subtract 4 from index)
          if (currentIndex < GRID_COLUMNS) {
            // At top row, return focus to search input
            if (searchInputRef.current) {
              searchInputRef.current.focus();
            }
            return -1;
          }
          newIndex = currentIndex - GRID_COLUMNS;
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          // Move left (subtract 1), but don't wrap to previous row
          const currentRow = Math.floor(currentIndex / GRID_COLUMNS);
          const potentialIndex = currentIndex - 1;
          const potentialRow = Math.floor(potentialIndex / GRID_COLUMNS);

          if (potentialIndex >= 0 && potentialRow === currentRow) {
            newIndex = potentialIndex;
          }
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          // Move right (add 1), but don't wrap to next row
          const currentRow = Math.floor(currentIndex / GRID_COLUMNS);
          const potentialIndex = currentIndex + 1;
          const potentialRow = Math.floor(potentialIndex / GRID_COLUMNS);

          if (potentialIndex < totalHeroes && potentialRow === currentRow) {
            newIndex = potentialIndex;
          }
        } else if (e.key === 'Enter') {
          e.preventDefault();
          // Select the focused hero
          if (currentIndex >= 0 && currentIndex < totalHeroes) {
            handleHeroSelect(filteredHeroes[currentIndex]);
          }
          return currentIndex;
        } else if (e.key === 'Escape') {
          e.preventDefault();
          setShowPopup(false);
          return -1;
        }

        // Scroll into view if index changed
        if (newIndex !== currentIndex && filteredHeroes[newIndex]) {
          setTimeout(() => {
            const heroElement = heroRefs.current[filteredHeroes[newIndex]?.id];
            if (heroElement) {
              heroElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
          }, 0);
        }

        return newIndex;
      });
    };

    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
    };
  }, [showPopup, filteredHeroes, handleHeroSelect]);

  // Handle Cmd+Click to duplicate scenario and open hero selector
  const handleCmdClick = (e, scenarioId, position, scenarioIndex) => {
    if (!e.ctrlKey && !e.metaKey) return false;

    // Duplicate the scenario
    duplicateScenario(pickMode, scenarioId);

    // After duplication, open hero selector on the new scenario at the same position
    // We need to wait for the state to update
    setTimeout(() => {
      const newScenarioIndex = scenarioIndex + 1; // New scenario is inserted right after
      setActiveScenarioIndex(newScenarioIndex);
      setSelectedBox(position);
      setShowPopup(true);
      setHeroSearch(''); // Clear search to show all heroes
    }, 100);

    console.log(`✨ Scenario duplicated! Hero selector opening at position ${position}`);
    return true;
  };



  // Handle keyboard events in search input
  const handleSearchKeyDown = (e) => {
    // Note: handleHeroSelect is defined later, so we'll reference it directly
    if (e.key === 'Enter') {
      if (focusedHeroIndex >= 0 && focusedHeroIndex < filteredHeroes.length) {
        // Select the focused hero - will be defined later
        const hero = filteredHeroes[focusedHeroIndex];
        // Call handleHeroSelect which is defined below
        setTimeout(() => handleHeroSelect(hero), 0);
      } else if (filteredHeroes.length > 0) {
        // No hero focused, select the first one
        const hero = filteredHeroes[0];
        setTimeout(() => handleHeroSelect(hero), 0);
      }
    } else if (e.key === 'Escape') {
      setShowPopup(false);
      setFocusedHeroIndex(-1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      e.stopPropagation(); // Stop event from bubbling to document handler
      // Move focus from search input to first hero
      if (filteredHeroes.length > 0) {
        console.log('📍 Moving from search to first hero:', {
          index: 0,
          hero: filteredHeroes[0]?.name,
          allHeroes: filteredHeroes.map((h, i) => `${i}:${h.name}`).join(', ')
        });
        setFocusedHeroIndex(0);
        // Blur the search input so it stops intercepting arrow keys
        if (searchInputRef.current) {
          searchInputRef.current.blur();
        }
        // Scroll the first hero into view
        setTimeout(() => {
          const heroElement = heroRefs.current[filteredHeroes[0].id];
          if (heroElement) {
            heroElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          }
        }, 0);
      }
    } else if (e.key === 'ArrowUp') {
      // Already at search input, do nothing (or could close popup)
      e.preventDefault();
      e.stopPropagation(); // Stop event from bubbling to document handler
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      // Allow default behavior for text navigation in input
      return;
    }
  };

  // Clear all picks for a scenario
  const clearScenario = (scenarioId) => {
    updateScenario(pickMode, scenarioId, { picks: Array(24).fill(null) });
  };

  // Save draft to localStorage (include info fields)
  const handleSaveDraft = async () => {
    let toast;
    try {
      const result = await saveDraft({
        teamName,
        tournament,
        stage,
        date: draftDate,
        title: teamName ? `Draft for ${teamName}` : `Draft ${new Date().toLocaleDateString()}`,
        savedAt: new Date().toISOString(),
      });
      if (result?.cloudSaved) {
        toast = { message: 'Saved to cloud', type: 'success' };
      } else if (result?.loggedIn) {
        const detail = result.cloudError ? `: ${result.cloudError}` : '';
        toast = { message: `Cloud save failed${detail}`, type: 'error' };
      } else {
        toast = { message: 'Saved locally (sign in to sync across devices)', type: 'warn' };
      }
    } catch (err) {
      toast = { message: 'Save failed: ' + err.message, type: 'error' };
    }
    setSaveToast(toast);
    setTimeout(() => setSaveToast(null), toast?.type === 'error' ? 8000 : 3500);
  };

  const showToast = (message, type = 'error') => {
    setSaveToast({ message, type });
    setTimeout(() => setSaveToast(null), type === 'error' ? 8000 : 3500);
  };

  // Handle new draft button
  const handleNewDraft = () => {
    const result = newDraft();
    if (result && result.needsConfirmation) {
      setPendingAction({ type: 'newDraft' });
      setShowConfirmation(true);
    }
  };

  // Handle copy scenario button click - directly copy to other mode
  const handleCopyToOtherMode = (scenario, currentPickMode) => {
    const targetPickMode = currentPickMode === 'first' ? 'second' : 'first';
    const currentTargetScenarios = targetPickMode === 'first' ? scenariosFirstPick : scenariosSecondPick;

    // Calculate what the new scenario ID will be
    const maxId = currentTargetScenarios.length > 0 ? Math.max(...currentTargetScenarios.map(s => s.id)) : -1;
    const newScenarioId = maxId + 1;

    // Set pending state - the useEffect will handle the rest when the scenario is added
    setPendingCopyToScenarioId(newScenarioId);
    setCopySourceScenario(scenario);
    setCopySourcePickMode(currentPickMode);

    // Switch to the target pick mode first
    setPickMode(targetPickMode);

    // Add the scenario - this will trigger the useEffect when state updates
    addScenario(targetPickMode);
  };

  const handleExportBothPickModes = async () => {
    if (isExportingJpeg || !scrollContainerRef.current) return;
    const originalMode = pickMode;
    const otherMode = originalMode === 'first' ? 'second' : 'first';
    const fileTagFor = (mode) => (mode === 'first' ? '1st-pick' : '2nd-pick');

    setIsExportingJpeg(true);
    try {
      // Wait for the button-cluster unmount (gated on isExportingJpeg above)
      // to actually commit and paint before measuring anything.
      await waitForNextFrame();

      // The hero-pool row is shared across both pick modes (not per-scenario
      // data) but is re-captured for EACH mode, sized to that mode's own
      // board-strip width — its own natural width is just the viewport, too
      // narrow to fit every box in one row, so it was wrapping.
      // Reusing one capture across both modes (matching whichever mode was
      // captured first) left the OTHER mode's canvas stuck at that same
      // width even when it had far fewer boards, stranding them on the left
      // with a wide dead strip of empty background to the right.
      //
      // Nothing to show if no forehead heroes are picked at all — skip the
      // row entirely rather than exporting empty boxes. When it IS
      // populated, never let the board strip's width squeeze it below what
      // all the boxes need in one line — widen to fit them instead of
      // wrapping to a second row or clipping.
      const hasForeheadPicks = Array.isArray(foreheadPicks) && foreheadPicks.some((pick) => pick?.hero);
      const captureForeheadAt = async (boardsWidth) => {
        if (!foreheadRowRef.current || !hasForeheadPicks) return null;
        const rowEl = foreheadRowRef.current;
        forceEagerImageLoad(rowEl);
        await waitForImagesLoaded(rowEl);
        const unwrappedWidth = measureUnwrappedWidth(rowEl);
        const targetWidth = Math.max(boardsWidth, unwrappedWidth);

        // Forcing the row wider than its natural content re-centers the
        // boxes (ForeheadRow's own `justify-content: center`) at a
        // different x than the narrower on-screen layout bracketRects was
        // measured against — widen for real, re-measure, and patch each
        // bracket's left/width to match before capturing, then revert.
        //
        // Widening also always fits every box on one line (that's the
        // point — see measureUnwrappedWidth above), collapsing whatever was
        // 2+ visual rows on screen. A group that wrapped on screen has
        // MULTIPLE bracket elements (one per row-run, see the measure()
        // effect) — for export only the first survives, repositioned to
        // span the group's full start..end; the rest are hidden rather
        // than left stacked at their stale row-2 position.
        const prevWidth = rowEl.style.width;
        rowEl.style.width = `${targetWidth}px`;
        void rowEl.offsetHeight;
        const bracketEls = Array.from(rowEl.querySelectorAll('[data-bracket-start]'));
        const prevBracketStyles = bracketEls.map((el) => ({
          left: el.style.left,
          width: el.style.width,
          top: el.style.top,
          display: el.style.display,
        }));
        const singleRowTop = `${Math.max(bracketRects.length > 0 ? 52 : 0, foreheadConnectors.length > 0 ? 76 : 0) - 16}px`;
        const seenGroups = new Set();
        if (bracketEls.length) {
          const rowBox = rowEl.getBoundingClientRect();
          bracketEls.forEach((el) => {
            const groupKey = el.getAttribute('data-bracket-group');
            if (seenGroups.has(groupKey)) {
              el.style.display = 'none';
              return;
            }
            seenGroups.add(groupKey);
            const start = Number(el.getAttribute('data-bracket-group-start'));
            const end = Number(el.getAttribute('data-bracket-group-end'));
            const a = foreheadBoxRefs.current[start];
            const b = foreheadBoxRefs.current[end];
            if (!a || !b) return;
            const ar = a.getBoundingClientRect();
            const br = b.getBoundingClientRect();
            el.style.left = `${ar.left - rowBox.left}px`;
            el.style.width = `${(br.left + br.width) - ar.left}px`;
            el.style.top = singleRowTop;
          });
        }

        const canvas = await captureBoardCanvas(rowEl, { width: targetWidth });

        rowEl.style.width = prevWidth;
        bracketEls.forEach((el, i) => {
          el.style.left = prevBracketStyles[i].left;
          el.style.width = prevBracketStyles[i].width;
          el.style.top = prevBracketStyles[i].top;
          el.style.display = prevBracketStyles[i].display;
        });

        return canvas;
      };

      forceEagerImageLoad(scrollContainerRef.current);
      await waitForImagesLoaded(scrollContainerRef.current);
      const firstBoardsWidth = measureContentWidth(scrollContainerRef.current);
      const firstBoardsCanvas = await captureBoardCanvas(scrollContainerRef.current, { forceFullWidth: true });
      const firstForeheadCanvas = await captureForeheadAt(firstBoardsWidth);
      const firstCanvas = firstForeheadCanvas
        ? stackCanvasesVertically([firstForeheadCanvas, firstBoardsCanvas])
        : firstBoardsCanvas;

      setPickMode(otherMode);
      await waitForNextFrame();
      forceEagerImageLoad(scrollContainerRef.current);
      await waitForImagesLoaded(scrollContainerRef.current);
      const secondBoardsWidth = measureContentWidth(scrollContainerRef.current);
      const secondBoardsCanvas = await captureBoardCanvas(scrollContainerRef.current, { forceFullWidth: true });
      const secondForeheadCanvas = await captureForeheadAt(secondBoardsWidth);
      const secondCanvas = secondForeheadCanvas
        ? stackCanvasesVertically([secondForeheadCanvas, secondBoardsCanvas])
        : secondBoardsCanvas;

      // Two separate downloads: stagger them so the browser doesn't treat
      // the second as an unrequested "automatic download" and block it.
      await saveCanvasAsJpeg(firstCanvas, buildExportFileName(teamName, fileTagFor(originalMode)));
      await wait(400);
      await saveCanvasAsJpeg(secondCanvas, buildExportFileName(teamName, fileTagFor(otherMode)));
    } catch (err) {
      console.error('Export JPEG failed', err);
    } finally {
      setPickMode(originalMode);
      setIsExportingJpeg(false);
    }
  };

  // Handle global "Copy From" button click - show overlay
  const handleCopyFromClick = () => {
    setShowScenarioSelection(true);
  };

  // Handle scenario selection from overlay - add as new scenario to current mode
  const handleSelectSourceScenario = (sourceScenario, sourcePickMode) => {
    const currentScenarios = pickMode === 'first' ? scenariosFirstPick : scenariosSecondPick;

    // Calculate what the new scenario ID will be
    const maxId = currentScenarios.length > 0 ? Math.max(...currentScenarios.map(s => s.id)) : -1;
    const newScenarioId = maxId + 1;

    // Set pending state
    setPendingCopyToScenarioId(newScenarioId);
    setCopySourceScenario(sourceScenario);
    setCopySourcePickMode(sourcePickMode);

    // Add the scenario in the current pick mode
    addScenario(pickMode);

    // Close the overlay
    setShowScenarioSelection(false);
  };

  // Handle cancel scenario selection
  const handleCancelScenarioSelection = () => {
    setShowScenarioSelection(false);
  };

  // Handle ESC key to close overlays
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape') {
        if (showScenarioSelection) handleCancelScenarioSelection();
        if (showBothOverlay) setShowBothOverlay(false);
      }
    };

    window.addEventListener('keydown', handleEscKey);
    return () => window.removeEventListener('keydown', handleEscKey);
  }, [showScenarioSelection, showBothOverlay]);

  // Handle confirmation dialog actions
  const handleConfirmSave = async () => {
    try {
      await handleSaveDraft();
      await executePendingAction(true); // Execute with skipConfirmation = true
    } catch (error) {
      console.error('Error saving draft:', error);
    } finally {
      setShowConfirmation(false);
      setPendingAction(null);
    }
  };

  const handleConfirmDiscard = async () => {
    try {
      await executePendingAction(true); // Execute with skipConfirmation = true
    } catch (error) {
      console.error('Error executing action:', error);
    } finally {
      setShowConfirmation(false);
      setPendingAction(null);
    }
  };

  const handleConfirmCancel = () => {
    setShowConfirmation(false);
    setPendingAction(null);
  };

  // Execute the pending action
  const executePendingAction = async (skipConfirmation = false) => {
    if (!pendingAction) return;

    try {
      switch (pendingAction.type) {
        case 'import':
          const importResult = await importDraftData(pendingAction.data, skipConfirmation);
          if (importResult && importResult.id) {
            // Handle successful import (tracking, caching, navigation)
            const tracking = JSON.parse(localStorage.getItem('dota2_draft_tracking') || '{}');
            const draftData = pendingAction.data;
            const file = pendingAction.file;

            const cleanString = (str) => {
              return str ? str.replace(/[^a-z0-9]/gi, '_').replace(/_+/g, '_').replace(/^_|_$/g, '') : '';
            };

            const teamName = cleanString(draftData.teamName) || 'Team';
            const tournamentName = cleanString(draftData.tournament) || 'Tournament';
            const stageName = cleanString(draftData.stage) || 'Stage';
            const dateString = draftData.date || new Date().toISOString().slice(0, 10);

            const displayTitle = `${teamName}_${tournamentName}_${stageName}_${dateString}`;

            tracking[draftData.id] = {
              id: draftData.id,
              filename: file.name,
              title: displayTitle,
              tournament: draftData.tournament || '',
              stage: draftData.stage || '',
              date: draftData.date || draftData.createdAt,
              updatedAt: draftData.updatedAt,
              createdAt: draftData.createdAt,
              scenariosFirstPick: draftData.scenariosFirstPick ? true : false,
              scenariosSecondPick: draftData.scenariosSecondPick ? true : false
            };

            localStorage.setItem('dota2_draft_tracking', JSON.stringify(tracking));

            try {
              localStorage.setItem(`draft_cache_${draftData.id}`, JSON.stringify(draftData));
              console.log('Draft imported successfully:', draftData.id);
            } catch (cacheError) {
              console.warn('Could not cache draft data', cacheError);
            }

            // Draft is already loaded into context, no need to navigate
            console.log('Draft loaded into current view');
          }
          break;
        case 'newDraft':
          newDraft(skipConfirmation);
          break;
        default:
          console.warn('Unknown pending action type:', pendingAction.type);
      }
    } catch (error) {
      console.error('Error executing pending action:', error);
      throw error;
    }
  };

  // Drag and drop functionality (for JSON files)
  const handleDragOver = (e) => {
    // Don't handle file drag if we're dragging a scenario
    if (isScenarioDragging) return;

    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    // Don't handle file drag if we're dragging a scenario
    if (isScenarioDragging) return;

    e.preventDefault();
    e.stopPropagation();
    // Only hide overlay if we're leaving the main container
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = async (e) => {
    // Don't handle file drop if we're dragging a scenario
    if (isScenarioDragging) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    const file = files[0];

    // Check if it's a JSON file
    if (!file.name.toLowerCase().endsWith('.json')) {
      console.warn('Invalid file type. Please drop a JSON file.');
      showToast('Import failed: not a JSON file', 'error');
      return;
    }

    try {
      // Read the file content
      const text = await file.text();
      let draftData;

      try {
        draftData = JSON.parse(text);
      } catch (parseError) {
        console.warn('Invalid JSON file format');
        showToast('Import failed: invalid JSON file', 'error');
        return;
      }

      // Validate that it's a draft file (accept scenario drafts OR forehead-only exports)
      const hasForeheadData = Array.isArray(draftData?.foreheadPicks) || Array.isArray(draftData?.foreheadConnectors);
      const hasScenarios = draftData?.scenariosFirstPick || draftData?.scenariosSecondPick;
      if (!draftData || (!draftData.id && !hasScenarios && !hasForeheadData)) {
        console.warn('This doesn\'t appear to be a valid draft file');
        showToast('Import failed: not a valid draft file', 'error');
        return;
      }

      // Generate an ID if none exists
      if (!draftData.id) {
        draftData.id = `draft_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      }

      // Add missing fields if necessary
      draftData.createdAt = draftData.createdAt || draftData.date || new Date().toISOString();
      draftData.updatedAt = draftData.updatedAt || draftData.createdAt;

      const isForeheadOnly = hasForeheadData && !hasScenarios;

      // Forehead-only files always show the dialog (even on a blank board)
      // Full drafts on a blank board replace without asking
      if (!currentDraftId && !isForeheadOnly) {
        importDraftData(draftData);
        console.log('Imported draft data into new draft');
      } else {
        // Store the pending import and show merge/replace dialog
        setPendingFileImport({ file, draftData, isForeheadOnly });
        setShowMergeReplaceDialog(true);
      }

    } catch (err) {
      console.error('Error importing dropped file:', err);
      showToast('Import failed: ' + err.message, 'error');
    }
  };

  // Merge/Replace dialog handlers for file drop
  const handleMergeDrop = async () => {
    if (!pendingFileImport) return;

    try {
      const { draftData } = pendingFileImport;

      // Merge the imported data with current draft
      mergeDraftData(draftData);

      // Close dialog and clear pending import
      setShowMergeReplaceDialog(false);
      setPendingFileImport(null);

      console.log('Successfully merged draft data');
      showToast('Draft merged', 'success');
    } catch (err) {
      console.error('Error merging draft:', err);
      showToast('Merge failed: ' + err.message, 'error');
    }
  };

  const handleReplaceDrop = async () => {
    if (!pendingFileImport) return;

    try {
      const { file, draftData, isForeheadOnly } = pendingFileImport;

      // Forehead-only: just overwrite the hero-pool slots, keep everything else intact
      if (isForeheadOnly) {
        const picksValid = Array.isArray(draftData.foreheadPicks) && draftData.foreheadPicks.length >= 15;
        if (picksValid) {
          setForeheadPicks([...draftData.foreheadPicks]);
        }
        if (Array.isArray(draftData.foreheadConnectors)) {
          setForeheadConnectors([...draftData.foreheadConnectors]);
        }
        setShowMergeReplaceDialog(false);
        setPendingFileImport(null);
        if (picksValid) {
          showToast('Hero pool imported', 'success');
        } else {
          showToast('Import failed: forehead pick data was incomplete', 'error');
        }
        return;
      }

      // Use the importDraftData function to replace current draft
      const importResult = await importDraftData(draftData, true); // skipConfirmation = true

      if (importResult && importResult.id) {
        // Track the saved draft for future reference
        const tracking = JSON.parse(localStorage.getItem('dota2_draft_tracking') || '{}');

        // Generate display title that matches filename format
        const cleanString = (str) => {
          return str ? str.replace(/[^a-z0-9]/gi, '_').replace(/_+/g, '_').replace(/^_|_$/g, '') : '';
        };

        const teamName = cleanString(draftData.teamName) || 'Team';
        const tournamentName = cleanString(draftData.tournament) || 'Tournament';
        const stageName = cleanString(draftData.stage) || 'Stage';
        const dateString = draftData.date || new Date().toISOString().slice(0, 10);

        const displayTitle = `${teamName}_${tournamentName}_${stageName}_${dateString}`;

        tracking[draftData.id] = {
          id: draftData.id,
          filename: file.name,
          title: displayTitle,
          tournament: draftData.tournament || '',
          stage: draftData.stage || '',
          date: draftData.date || draftData.createdAt,
          updatedAt: draftData.updatedAt,
          createdAt: draftData.createdAt,
          scenariosFirstPick: draftData.scenariosFirstPick ? true : false,
          scenariosSecondPick: draftData.scenariosSecondPick ? true : false
        };

        localStorage.setItem('dota2_draft_tracking', JSON.stringify(tracking));

        // Cache the draft data for future quick loading
        try {
          localStorage.setItem(`draft_cache_${draftData.id}`, JSON.stringify(draftData));
          console.log('Draft cached from drop:', draftData.id);
        } catch (cacheError) {
          console.warn('Could not cache draft data', cacheError);
        }

        // Close dialog and clear pending import
        setShowMergeReplaceDialog(false);
        setPendingFileImport(null);

        // Draft is already loaded into context, no need to navigate
        console.log('Draft replaced successfully:', draftData.id);
        showToast('Draft replaced', 'success');
      } else {
        console.warn('Replace drop: importDraftData did not return a valid result', importResult);
        showToast('Import failed: could not load draft data', 'error');
      }
    } catch (err) {
      console.error('Error replacing draft:', err);
      showToast('Replace failed: ' + err.message, 'error');
    }
  };

  const handleMergeReplaceCancel = () => {
    setShowMergeReplaceDialog(false);
    setPendingFileImport(null);
  };

  // Scenario drag-and-drop handlers
  const handleScenarioDragStart = (e, scenarioId) => {
    // Only allow dragging from the drag handle
    if (!e.target.closest('[data-drag-handle]')) {
      e.preventDefault();
      return;
    }

    setDraggedScenarioId(scenarioId);
    setIsScenarioDragging(true);
    e.dataTransfer.effectAllowed = 'move';

    // Create a custom drag image with highlighted border
    const container = e.target.closest('.draft-board');
    if (container) {
      // Create a wrapper with solid background
      const wrapper = document.createElement('div');
      wrapper.style.position = 'absolute';
      wrapper.style.top = '-10000px';
      wrapper.style.left = '-10000px';
      wrapper.style.width = container.offsetWidth + 'px';
      wrapper.style.padding = '3px';
      wrapper.style.backgroundColor = '#2196F3';  // Blue border background
      wrapper.style.borderRadius = '15px';
      wrapper.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.8), 0 0 20px rgba(33, 150, 243, 0.6)';

      // Clone the container
      const clone = container.cloneNode(true);
      clone.style.border = 'none';
      clone.style.borderRadius = '12px';
      clone.style.backgroundColor = '#0b0d14';
      clone.style.backgroundImage = 'linear-gradient(to bottom, #161926, #0b0d14)';
      clone.style.width = '100%';
      clone.style.boxShadow = 'none';

      wrapper.appendChild(clone);
      document.body.appendChild(wrapper);

      // Set the custom drag image
      e.dataTransfer.setDragImage(wrapper, container.offsetWidth / 2, 20);

      // Clean up after a short delay
      setTimeout(() => {
        if (document.body.contains(wrapper)) {
          document.body.removeChild(wrapper);
        }
      }, 0);
    }
  };

  const handleScenarioDragOver = (e, scenarioId) => {
    // Only handle if we're in scenario dragging mode
    if (!isScenarioDragging) return;

    e.preventDefault();
    e.stopPropagation(); // Prevent bubbling to avoid triggering parent handlers
    e.dataTransfer.dropEffect = 'move';

    if (draggedScenarioId !== null && draggedScenarioId !== scenarioId) {
      // Determine if we should insert before or after based on mouse position
      const targetElement = scenarioRefs.current[scenarioId];
      if (targetElement) {
        const rect = targetElement.getBoundingClientRect();
        const midpoint = rect.left + rect.width / 2;
        const isLeftHalf = e.clientX < midpoint;

        setDragOverScenarioId({ id: scenarioId, insertBefore: isLeftHalf });
      } else {
        setDragOverScenarioId({ id: scenarioId, insertBefore: false });
      }
    }
  };

  const handleScenarioDragLeave = (e, scenarioId) => {
    // Only clear if we're actually leaving this scenario container (not moving to a child)
    if (!e.currentTarget.contains(e.relatedTarget)) {
      // Only clear if this was the scenario we were hovering over
      const currentDragOver = dragOverScenarioId;
      if (currentDragOver && typeof currentDragOver === 'object' && currentDragOver.id === scenarioId) {
        setDragOverScenarioId(null);
      } else if (currentDragOver === scenarioId) {
        setDragOverScenarioId(null);
      }
    }
  };

  const handleScenarioDragEnd = (e) => {
    if (draggedScenarioId !== null && dragOverScenarioId !== null) {
      const targetId = typeof dragOverScenarioId === 'object' ? dragOverScenarioId.id : dragOverScenarioId;

      if (draggedScenarioId !== targetId) {
        // Find the indices of the dragged and target scenarios
        const fromIndex = scenarios.findIndex(s => s.id === draggedScenarioId);
        const targetIndex = scenarios.findIndex(s => s.id === targetId);

        if (fromIndex !== -1 && targetIndex !== -1) {
          let toIndex = targetIndex;

          // Check if we're inserting after (right half)
          if (typeof dragOverScenarioId === 'object' && !dragOverScenarioId.insertBefore) {
            // If target is the last scenario, always place at the end
            if (targetIndex === scenarios.length - 1) {
              toIndex = scenarios.length - 1;
            } else {
              // Insert after the target (adjust for the removal of the dragged item if needed)
              toIndex = fromIndex < targetIndex ? targetIndex : targetIndex + 1;
            }
          } else if (typeof dragOverScenarioId === 'object' && dragOverScenarioId.insertBefore) {
            // Insert before the target
            toIndex = fromIndex < targetIndex ? targetIndex - 1 : targetIndex;
          }

          reorderScenarios(pickMode, fromIndex, toIndex);

          // Trigger ripple animation on the dropped scenario
          setDroppedScenarioId(draggedScenarioId);
          setTimeout(() => setDroppedScenarioId(null), 600);

          // Trigger full-canvas ripple from drop position
          setRipplePosition({ x: e.clientX, y: e.clientY, show: true });
          setTimeout(() => setRipplePosition({ x: 0, y: 0, show: false }), 1200);
        }
      }
    }
    setDraggedScenarioId(null);
    setDragOverScenarioId(null);
    setIsScenarioDragging(false);
  };

  // Prevent default drag behavior on the entire page
  useEffect(() => {
    const preventDefault = (e) => {
      e.preventDefault();
    };

    document.addEventListener('dragover', preventDefault);
    document.addEventListener('drop', preventDefault);

    return () => {
      document.removeEventListener('dragover', preventDefault);
      document.removeEventListener('drop', preventDefault);
    };
  }, []);

  // Multi-select handlers
  const handleMouseDown = (e) => {
    // Don't start selection if we're dragging a scenario or clicking on special elements
    if (isScenarioDragging ||
        e.button !== 0 ||
        e.target.closest('[data-position]') ||
        e.target.closest('[data-drag-handle]')) {
      return;
    }

    isSelectingRef.current = true;
    selectionStartRef.current = { x: e.clientX, y: e.clientY };
    setIsSelecting(true);
    setSelectionStart({ x: e.clientX, y: e.clientY });
    setSelectionEnd({ x: e.clientX, y: e.clientY });
    setSelectedPositions(new Set());
  };

  const handleMouseMove = (e) => {
    // Don't handle selection if we're dragging a scenario
    if (!isSelectingRef.current || isScenarioDragging) return;

    setSelectionEnd({ x: e.clientX, y: e.clientY });

    // Calculate selection box bounds
    const selectionBox = {
      left: Math.min(selectionStartRef.current.x, e.clientX),
      top: Math.min(selectionStartRef.current.y, e.clientY),
      right: Math.max(selectionStartRef.current.x, e.clientX),
      bottom: Math.max(selectionStartRef.current.y, e.clientY)
    };

    // Check collision with all hero boxes
    const newSelectedPositions = new Set();
    Object.entries(boxRefs.current).forEach(([key, boxElement]) => {
      if (!boxElement) return;

      const rect = boxElement.getBoundingClientRect();
      const boxBounds = {
        left: rect.left,
        top: rect.top,
        right: rect.right,
        bottom: rect.bottom
      };

      // Check if selection box intersects with hero box
      const intersects = !(
        selectionBox.right < boxBounds.left ||
        selectionBox.left > boxBounds.right ||
        selectionBox.bottom < boxBounds.top ||
        selectionBox.top > boxBounds.bottom
      );

      if (intersects) {
        newSelectedPositions.add(key);
      }
    });

    setSelectedPositions(newSelectedPositions);

    // Check collision with forehead boxes
    const newForeheadSelection = new Set();
    Object.entries(foreheadBoxRefs.current).forEach(([key, boxElement]) => {
      if (!boxElement) return;
      const rect = boxElement.getBoundingClientRect();
      const intersects = !(
        selectionBox.right < rect.left ||
        selectionBox.left > rect.right ||
        selectionBox.bottom < rect.top ||
        selectionBox.top > rect.bottom
      );
      if (intersects) newForeheadSelection.add(Number(key));
    });
    setForeheadSelection(newForeheadSelection);
  };

  const handleMouseUp = () => {
    isSelectingRef.current = false;
    setIsSelecting(false);
  };

  // Keyboard handler for delete, copy, paste, undo, redo, and navigation
  useEffect(() => {
    // Define the two-column layout as it appears visually (Patch 4.0)
    // Column 0 (Radiant - Left): positions in visual order top to bottom
    const radiantColumn = [...game.board.radiantBans, ...game.board.radiantPicks].sort((a, b) => a - b);
    // Column 1 (Dire - Right): positions in visual order top to bottom
    const direColumn = [...game.board.direBans, ...game.board.direPicks].sort((a, b) => a - b);

    // Horizontal navigation mapping (which positions are side-by-side)
    // Patch 4.0: positions that have horizontal neighbors
    const horizontalPairs = {
      // Left to Right mapping
      2: 3, 5: 4, 8: 9, 11: 12, 14: 13, 15: 16, 18: 17, 19: 20, 21: 22, 23: 24,
      // Right to Left mapping
      3: 2, 4: 5, 9: 8, 12: 11, 13: 14, 16: 15, 17: 18, 20: 19, 22: 21, 24: 23
      // Positions without horizontal neighbors: 1, 10 (left), 6, 7 (right)
    };

    const handleKeyDown = (e) => {
      // Disable all keyboard interactions in simulator view
      if (isSimulatorView) {
        return;
      }

      // Disable keyboard interactions when demo is not visible (marketing page)
      if (isDisabledDemo) {
        return;
      }

      // Don't interfere if user is typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        console.log(`⌨️ Keypress in input field, ignoring: "${e.key}" target:`, e.target);
        return;
      }

      // Debug logging for iPad keyboard detection
      if ((e.key === 'c' || e.key === 'C' || e.key === 'v' || e.key === 'V') && (e.ctrlKey || e.metaKey)) {
        console.log('Keyboard event:', {
          key: e.key,
          ctrlKey: e.ctrlKey,
          metaKey: e.metaKey,
          shiftKey: e.shiftKey,
          code: e.code,
          keyCode: e.keyCode
        });
      }

      // Handle Undo (Ctrl+Z or Cmd+Z)
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();
        undo();
        return;
      }

      // Handle Redo (Ctrl+Y or Cmd+Shift+Z or Cmd+Y)
      if (((e.ctrlKey || e.metaKey) && e.key === 'y') || ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'z')) {
        e.preventDefault();
        e.stopPropagation();
        redo();
        return;
      }

      // Handle Save Draft (Ctrl+S or Cmd+S)
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        console.log('⌨️ Ctrl+S detected in keyboard handler!');
        e.preventDefault();
        e.stopPropagation();
        console.log('⌨️ About to call handleSaveDraft()...');
        handleSaveDraft();
        console.log('⌨️ handleSaveDraft() returned');
        return;
      }

      // Handle New Scenario (Ctrl+N or Cmd+N, also supports Ctrl+Alt+N or Cmd+Option+N)
      if ((e.ctrlKey || e.metaKey) && e.key === 'n' && !e.shiftKey) {
        console.log('🎯 New Scenario shortcut detected!', { ctrlKey: e.ctrlKey, metaKey: e.metaKey, altKey: e.altKey, key: e.key });
        e.preventDefault();
        e.stopPropagation();
        addScenario(pickMode);
        return;
      }

      // Arrow key navigation (when not in popup)
      if (!showPopup && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault();
        e.stopPropagation();

        // Ctrl/Cmd + Shift + Arrow for multi-select (Excel-style)
        if ((e.ctrlKey || e.metaKey) && e.shiftKey) {
          const currentScenario = scenarios[focusedBox.scenarioIndex];
          if (!currentScenario) return;

          const newSelection = new Set(selectedPositions);

          if (e.key === 'ArrowDown') {
            // Select down until next empty cell or end of filled cells (Excel-style)
            const inRadiantCol = radiantColumn.includes(focusedBox.position);
            const column = inRadiantCol ? radiantColumn : direColumn;
            const currentIndex = column.indexOf(focusedBox.position);

            // Find the furthest selected position in this column
            let furthestSelectedIndex = currentIndex;
            for (let i = currentIndex; i < column.length; i++) {
              const key = `${currentScenario.id}_${column[i]}`;
              if (newSelection.has(key)) {
                furthestSelectedIndex = i;
              } else {
                break;
              }
            }

            // Start extending from the furthest selected position
            let endIndex = furthestSelectedIndex;

            // Check if the position right after the selection has a hero
            const nextIndex = furthestSelectedIndex + 1;
            if (nextIndex < column.length) {
              const nextPick = currentScenario.picks?.[column[nextIndex] - 1];
              const nextHasHero = nextPick?.hero != null;

              if (nextHasHero) {
                // Continue selecting filled cells
                for (let i = nextIndex; i < column.length; i++) {
                  const pick = currentScenario.picks?.[column[i] - 1];
                  if (!pick?.hero) {
                    break; // Stop at first empty cell
                  }
                  endIndex = i;
                }
              } else {
                // Next is empty, skip empties and select until next filled
                let foundFilled = false;
                for (let i = nextIndex; i < column.length; i++) {
                  const pick = currentScenario.picks?.[column[i] - 1];
                  if (pick?.hero) {
                    endIndex = i;
                    foundFilled = true;
                    break;
                  }
                }
                // If we didn't find any filled cells, just extend to next empty
                if (!foundFilled && nextIndex < column.length) {
                  endIndex = nextIndex;
                }
              }
            }

            // Select from current to endIndex
            for (let i = currentIndex; i <= endIndex; i++) {
              newSelection.add(`${currentScenario.id}_${column[i]}`);
            }
            setSelectedPositions(newSelection);

            // Move focus to the end of selection
            if (endIndex > currentIndex) {
              setFocusedBox({ scenarioIndex: focusedBox.scenarioIndex, position: column[endIndex] });
            }
            return;
          } else if (e.key === 'ArrowUp') {
            // Select up until next empty cell or start of filled cells (Excel-style)
            const inRadiantCol = radiantColumn.includes(focusedBox.position);
            const column = inRadiantCol ? radiantColumn : direColumn;
            const currentIndex = column.indexOf(focusedBox.position);

            // Find the furthest selected position upward in this column
            let furthestSelectedIndex = currentIndex;
            for (let i = currentIndex; i >= 0; i--) {
              const key = `${currentScenario.id}_${column[i]}`;
              if (newSelection.has(key)) {
                furthestSelectedIndex = i;
              } else {
                break;
              }
            }

            // Start extending from the furthest selected position
            let startIndex = furthestSelectedIndex;

            // Check if the position right before the selection has a hero
            const prevIndex = furthestSelectedIndex - 1;
            if (prevIndex >= 0) {
              const prevPick = currentScenario.picks?.[column[prevIndex] - 1];
              const prevHasHero = prevPick?.hero != null;

              if (prevHasHero) {
                // Continue selecting filled cells upward
                for (let i = prevIndex; i >= 0; i--) {
                  const pick = currentScenario.picks?.[column[i] - 1];
                  if (!pick?.hero) {
                    break; // Stop at first empty cell
                  }
                  startIndex = i;
                }
              } else {
                // Prev is empty, skip empties and select until next filled
                let foundFilled = false;
                for (let i = prevIndex; i >= 0; i--) {
                  const pick = currentScenario.picks?.[column[i] - 1];
                  if (pick?.hero) {
                    startIndex = i;
                    foundFilled = true;
                    break;
                  }
                }
                // If we didn't find any filled cells, just extend to prev empty
                if (!foundFilled && prevIndex >= 0) {
                  startIndex = prevIndex;
                }
              }
            }

            // Select from startIndex to current
            for (let i = startIndex; i <= currentIndex; i++) {
              newSelection.add(`${currentScenario.id}_${column[i]}`);
            }
            setSelectedPositions(newSelection);

            // Move focus to the start of selection
            if (startIndex < currentIndex) {
              setFocusedBox({ scenarioIndex: focusedBox.scenarioIndex, position: column[startIndex] });
            }
            return;
          }
          // Remove left/right multi-select across scenarios
          // (Only allow within same scenario)
        }

        // Ctrl/Cmd + Arrow (without Shift) for scenario/column jump navigation
        if ((e.ctrlKey || e.metaKey) && !e.shiftKey) {
          if (e.key === 'ArrowLeft' && focusedBox.scenarioIndex > 0) {
            // Jump to previous scenario (same position)
            const newScenarioIndex = focusedBox.scenarioIndex - 1;
            setFocusedBox({ scenarioIndex: newScenarioIndex, position: focusedBox.position });
            const scenario = scenarios[newScenarioIndex];
            if (scenario) {
              const boxKey = `${scenario.id}_${focusedBox.position}`;
              const boxElement = boxRefs.current[boxKey];
              if (boxElement) {
                boxElement.focus();
              }
            }
            return;
          } else if (e.key === 'ArrowRight' && focusedBox.scenarioIndex < scenarios.length - 1) {
            // Jump to next scenario (same position)
            const newScenarioIndex = focusedBox.scenarioIndex + 1;
            setFocusedBox({ scenarioIndex: newScenarioIndex, position: focusedBox.position });
            const scenario = scenarios[newScenarioIndex];
            if (scenario) {
              const boxKey = `${scenario.id}_${focusedBox.position}`;
              const boxElement = boxRefs.current[boxKey];
              if (boxElement) {
                boxElement.focus();
              }
            }
            return;
          } else if (e.key === 'ArrowUp') {
            // Jump to first filled box in current column
            const inRadiantCol = radiantColumn.includes(focusedBox.position);
            const column = inRadiantCol ? radiantColumn : direColumn;
            const scenario = scenarios[focusedBox.scenarioIndex];

            if (scenario && scenario.picks) {
              // Find first filled position in column
              let newPosition = null;
              for (const pos of column) {
                const pick = scenario.picks[pos - 1];
                if (pick && pick.hero) {
                  newPosition = pos;
                  break;
                }
              }

              // If no filled position found, go to first position
              if (!newPosition) {
                newPosition = column[0];
              }

              setFocusedBox({ scenarioIndex: focusedBox.scenarioIndex, position: newPosition });
              const boxKey = `${scenario.id}_${newPosition}`;
              const boxElement = boxRefs.current[boxKey];
              if (boxElement) {
                boxElement.focus();
              }
            }
            return;
          } else if (e.key === 'ArrowDown') {
            // Jump to last filled box in current column
            const inRadiantCol = radiantColumn.includes(focusedBox.position);
            const column = inRadiantCol ? radiantColumn : direColumn;
            const scenario = scenarios[focusedBox.scenarioIndex];

            if (scenario && scenario.picks) {
              // Find last filled position in column (search backwards)
              let newPosition = null;
              for (let i = column.length - 1; i >= 0; i--) {
                const pos = column[i];
                const pick = scenario.picks[pos - 1];
                if (pick && pick.hero) {
                  newPosition = pos;
                  break;
                }
              }

              // If no filled position found, go to last position
              if (!newPosition) {
                newPosition = column[column.length - 1];
              }

              setFocusedBox({ scenarioIndex: focusedBox.scenarioIndex, position: newPosition });
              const boxKey = `${scenario.id}_${newPosition}`;
              const boxElement = boxRefs.current[boxKey];
              if (boxElement) {
                boxElement.focus();
              }
            }
            return;
          }
        }

        // Regular arrow key navigation (no Ctrl/Cmd)
        const currentPos = focusedBox.position;
        let newPosition = currentPos;

        // Determine which column we're in
        const inRadiantCol = radiantColumn.includes(currentPos);
        const inDireCol = direColumn.includes(currentPos);

        // Check if current scenario is swapped
        const currentScenario = scenarios[focusedBox.scenarioIndex];
        const isSwapped = currentScenario?.swapSides || false;

        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          // Move up/down within the same column
          const column = inRadiantCol ? radiantColumn : direColumn;
          const currentIndex = column.indexOf(currentPos);

          if (currentIndex !== -1) {
            if (e.key === 'ArrowUp' && currentIndex > 0) {
              newPosition = column[currentIndex - 1];
            } else if (e.key === 'ArrowDown' && currentIndex < column.length - 1) {
              newPosition = column[currentIndex + 1];
            }
          }
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          // Move left/right between columns using horizontal pairs mapping
          // Patch 4.0: Only positions that are actually side-by-side can navigate horizontally

          if (!isSwapped) {
            // Normal layout: Radiant left, Dire right
            if (e.key === 'ArrowLeft' && inDireCol) {
              // Moving from Dire (right) to Radiant (left)
              newPosition = horizontalPairs[currentPos];
            } else if (e.key === 'ArrowRight' && inRadiantCol) {
              // Moving from Radiant (left) to Dire (right)
              newPosition = horizontalPairs[currentPos];
            }
          } else {
            // Swapped layout: Dire left, Radiant right
            if (e.key === 'ArrowLeft' && inRadiantCol) {
              // Moving from Radiant (right) to Dire (left)
              newPosition = horizontalPairs[currentPos];
            } else if (e.key === 'ArrowRight' && inDireCol) {
              // Moving from Dire (left) to Radiant (right)
              newPosition = horizontalPairs[currentPos];
            }
          }

          // If no horizontal neighbor exists, don't change position
          if (!newPosition) {
            newPosition = currentPos;
          }
        }

        // Update focused box if position changed
        if (newPosition !== currentPos) {
          // Handle Shift + Arrow for multi-selection
          if (e.shiftKey) {
            const scenario = scenarios[focusedBox.scenarioIndex];
            if (scenario) {
              const newSelections = new Set(selectedPositions);

              // If this is the first Shift+Arrow (no selections yet), include the current box
              if (newSelections.size === 0) {
                const currentBoxKey = `${scenario.id}_${currentPos}`;
                newSelections.add(currentBoxKey);
              }

              const boxKey = `${scenario.id}_${newPosition}`;

              // Toggle selection - if already selected, deselect; otherwise select
              if (newSelections.has(boxKey)) {
                newSelections.delete(boxKey);
              } else {
                newSelections.add(boxKey);
              }

              setSelectedPositions(newSelections);
            }
          } else {
            // Clear selection when moving without Shift
            if (selectedPositions.size > 0) {
              setSelectedPositions(new Set());
            }
          }

          setFocusedBox({ scenarioIndex: focusedBox.scenarioIndex, position: newPosition });
          // Focus the box element
          const scenario = scenarios[focusedBox.scenarioIndex];
          if (scenario) {
            const boxKey = `${scenario.id}_${newPosition}`;
            const boxElement = boxRefs.current[boxKey];
            if (boxElement) {
              boxElement.focus();
            }
          }
        }
        return;
      }

      // Auto-focus search box when typing alphanumeric characters
      // Works when: popup is already open OR when a box is focused via keyboard navigation
      if (!e.ctrlKey && !e.metaKey && !e.altKey) {
        // Check if it's a single alphanumeric character or space
        const isTypingChar = e.key.length === 1 && /[a-zA-Z0-9 ]/.test(e.key);

        if (isTypingChar) {
          // If popup is already open, just focus and type
          if (showPopup && selectedBox !== -1 && searchInputRef.current) {
            // Check if the search input is already focused
            if (document.activeElement === searchInputRef.current) {
              // Input is focused, let the event pass through naturally
              // Don't return here - let it bubble to the input
              console.log(`🔍 Search input already focused, letting keypress pass through: "${e.key}"`);
            } else {
              // Input is not focused yet, focus it
              // But we need to prevent this event from being processed
              e.preventDefault();
              searchInputRef.current.focus();
              // Manually insert the character since we prevented default
              console.log(`🔍 MANUAL INSERT: Focused search input, inserting character: "${e.key}", prev: "${heroSearch}"`);
              setHeroSearch(prev => {
                const newValue = prev + e.key;
                console.log(`🔍 MANUAL INSERT setState: "${prev}" + "${e.key}" = "${newValue}"`);
                return newValue;
              });
              return;
            }
          }

          // If popup is not open but a box is focused, open the popup first
          if (!showPopup && focusedBox && focusedBox.scenarioIndex >= 0 && focusedBox.position > 0) {
            e.preventDefault();
            e.stopPropagation();

            // Set the search query BEFORE opening popup
            console.log(`🔍 OPENING POPUP: Setting initial search to: "${e.key}"`);
            setHeroSearch(e.key);

            // Open the hero selector popup for the focused box
            handleBoxClick(focusedBox.scenarioIndex, focusedBox.position);

            // Set the input value and focus AFTER popup opens (using RAF for correct timing)
            requestAnimationFrame(() => {
              if (searchInputRef.current) {
                searchInputRef.current.value = e.key;
                searchInputRef.current.focus();
                // Set cursor to end of input
                searchInputRef.current.setSelectionRange(1, 1);
                console.log(`✅ Set initial search value to: "${e.key}"`);
              }
            });

            console.log(`🔍 Opened popup and started search with character: "${e.key}"`);
            return;
          }
        }
      }

      // Handle Enter key - open hero selector on focused box
      if (e.key === 'Enter' && !showPopup) {
        e.preventDefault();
        e.stopPropagation();
        handleBoxClick(focusedBox.scenarioIndex, focusedBox.position);
        return;
      }

      // Handle Backspace/Delete on focused box (when no multi-selection and no forehead selection)
      if ((e.key === 'Backspace' || e.key === 'Delete') && selectedPositions.size === 0 && foreheadSelection.size === 0 && !showPopup) {
        e.preventDefault();
        e.stopPropagation();

        const scenario = scenarios[focusedBox.scenarioIndex];
        if (scenario && scenario.picks && scenario.picks[focusedBox.position - 1]) {
          const newPicks = [...scenario.picks];
          newPicks[focusedBox.position - 1] = null;
          updateScenario(pickMode, scenario.id, { picks: newPicks });
        }
        return;
      }

      // Handle Copy (Ctrl+C or Cmd+C)
      // Support both lowercase 'c' and uppercase 'C' for iPad compatibility
      if ((e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'C') && !showPopup) {
        e.preventDefault();
        e.stopPropagation();

        // If no multi-selection, copy the focused cell
        if (selectedPositions.size === 0 && focusedBox.scenarioIndex >= 0 && focusedBox.position > 0) {
          const scenario = scenarios[focusedBox.scenarioIndex];
          if (scenario) {
            const pick = scenario.picks?.[focusedBox.position - 1];
            setClipboard([{
              pick: pick ? { ...pick } : null,
              position: focusedBox.position,
              offset: 0
            }]);
            if (pick?.hero) {
              console.log(`Copied single hero: ${pick.hero.name} from position ${focusedBox.position}`);
            }
          }
          return;
        }

        // Multi-selection copy (original behavior)
        if (selectedPositions.size === 0) return;

        // Copy all selected positions (including blanks) in visual order
        // First, collect all positions with their visual coordinates
        const positionsWithCoords = [];
        selectedPositions.forEach((key) => {
          const [scenarioId, position] = key.split('_');
          const boxElement = boxRefs.current[key];

          if (boxElement) {
            const rect = boxElement.getBoundingClientRect();
            positionsWithCoords.push({
              key,
              scenarioId,
              position: parseInt(position),
              top: rect.top,
              left: rect.left
            });
          }
        });

        // Sort by visual position: top to bottom, then left to right
        positionsWithCoords.sort((a, b) => {
          const topDiff = a.top - b.top;
          // If tops are within 5px, consider them on the same row
          if (Math.abs(topDiff) < 5) {
            return a.left - b.left; // Sort by left position
          }
          return topDiff; // Sort by top position
        });

        // Now copy in sorted visual order and calculate relative offsets
        const copiedData = [];
        const firstPosition = positionsWithCoords.length > 0 ? positionsWithCoords[0].position : null;

        positionsWithCoords.forEach(({ scenarioId, position }) => {
          const scenario = scenarios.find(s => String(s.id) === String(scenarioId));

          if (scenario) {
            const posIndex = position - 1;
            const pick = scenario.picks ? scenario.picks[posIndex] : null;

            // Store the pick with its absolute position and offset from first position
            copiedData.push({
              pick: pick ? { ...pick } : null,
              position: position,
              offset: position - firstPosition  // Store relative offset
            });
          }
        });

        if (copiedData.length > 0) {
          setClipboard(copiedData);
          const heroCount = copiedData.filter(d => d.pick && d.pick.hero).length;
          const blankCount = copiedData.length - heroCount;
          console.log(`Copied ${heroCount} hero(es)${blankCount > 0 ? ` and ${blankCount} blank(s)` : ''} in visual order:`,
            copiedData.filter(d => d.pick && d.pick.hero).map(d => d.pick.hero.name).join(', '),
            'Pattern:', copiedData.map(d => d.offset).join(', '));
        }
        return;
      }

      // Handle Paste (Ctrl+V or Cmd+V)
      // Support both lowercase 'v' and uppercase 'V' for iPad compatibility
      if ((e.ctrlKey || e.metaKey) && (e.key === 'v' || e.key === 'V') && clipboard.length > 0 && !showPopup) {
        e.preventDefault();
        e.stopPropagation();

        const updates = new Map();
        // Don't sort clipboard - preserve the visual order as copied

        // If multi-selection exists, paste to selected positions
        if (selectedPositions.size > 0) {
          // Convert selected positions to array and sort by visual position (same as copy)
          const selectedWithCoords = [];
          selectedPositions.forEach((key) => {
            const boxElement = boxRefs.current[key];
            if (boxElement) {
              const rect = boxElement.getBoundingClientRect();
              selectedWithCoords.push({
                key,
                top: rect.top,
                left: rect.left
              });
            }
          });

          // Sort by visual position: top to bottom, then left to right
          selectedWithCoords.sort((a, b) => {
            const topDiff = a.top - b.top;
            if (Math.abs(topDiff) < 5) {
              return a.left - b.left;
            }
            return topDiff;
          });

          const selectedArray = selectedWithCoords.map(item => item.key);

          // If we have the same number of copied heroes as selected positions, paste 1-to-1
          if (clipboard.length === selectedArray.length) {
            selectedArray.forEach((key, index) => {
              const [scenarioId, position] = key.split('_');
              const scenario = scenarios.find(s => String(s.id) === String(scenarioId));

              if (scenario) {
                if (!updates.has(scenarioId)) {
                  updates.set(scenarioId, scenario.picks ? [...scenario.picks] : Array(24).fill(null));
                }
                const picks = updates.get(scenarioId);
                const posIndex = parseInt(position) - 1;
                // Paste in the same visual order as copied, preserving null for blanks
                picks[posIndex] = clipboard[index].pick ? { ...clipboard[index].pick } : null;
              }
            });
            const pastedCount = clipboard.filter(d => d.pick).length;
            const blankedCount = clipboard.length - pastedCount;
            console.log(`Pasted ${pastedCount} hero(es)${blankedCount > 0 ? ` and ${blankedCount} blank(s)` : ''} 1-to-1 to selected positions`);
          } else {
            // Otherwise, repeat the clipboard pattern to fill all selected positions
            selectedArray.forEach((key, index) => {
              const [scenarioId, position] = key.split('_');
              const scenario = scenarios.find(s => String(s.id) === String(scenarioId));

              if (scenario) {
                if (!updates.has(scenarioId)) {
                  updates.set(scenarioId, scenario.picks ? [...scenario.picks] : Array(24).fill(null));
                }
                const picks = updates.get(scenarioId);
                const posIndex = parseInt(position) - 1;
                // Use modulo to cycle through clipboard items, preserving null for blanks
                const clipboardIndex = index % clipboard.length;
                picks[posIndex] = clipboard[clipboardIndex].pick ? { ...clipboard[clipboardIndex].pick } : null;
              }
            });
            const pastedCount = clipboard.filter(d => d.pick).length;
            console.log(`Pasted ${pastedCount} hero(es) (with blanks) cyclically to ${selectedArray.length} positions`);
          }
        } else if (focusedBox) {
          // If no multi-selection, paste using the same relative position pattern
          const scenario = scenarios[focusedBox.scenarioIndex];
          if (scenario) {
            const scenarioId = scenario.id;
            const picks = scenario.picks ? [...scenario.picks] : Array(24).fill(null);

            // Paste each clipboard item to its relative position from the focused box
            clipboard.forEach((clipboardItem) => {
              // Apply the same offset pattern from when it was copied
              const targetPosition = focusedBox.position + clipboardItem.offset;

              // Only paste within valid range (1-24)
              if (targetPosition >= 1 && targetPosition <= 24) {
                const posIndex = targetPosition - 1;
                picks[posIndex] = clipboardItem.pick ? { ...clipboardItem.pick } : null;
              }
            });

            updates.set(scenarioId, picks);
            const pastedCount = clipboard.filter(d => d.pick).length;
            const blankedCount = clipboard.length - pastedCount;
            const pattern = clipboard.map(d => d.offset).join(', ');
            console.log(`Pasted ${pastedCount} hero(es)${blankedCount > 0 ? ` and ${blankedCount} blank(s)` : ''} starting from position ${focusedBox.position} with pattern [${pattern}]`);
          }
        }

        // Apply all updates
        updates.forEach((picks, scenarioIdKey) => {
          const scenarioId = typeof scenarioIdKey === 'string' && !isNaN(scenarioIdKey)
            ? Number(scenarioIdKey)
            : scenarioIdKey;
          updateScenario(pickMode, scenarioId, { picks });
        });

        return;
      }

      // Handle Delete (Backspace or Delete)
      if ((e.key === 'Backspace' || e.key === 'Delete') && selectedPositions.size > 0 && !showPopup) {
        e.preventDefault();
        e.stopPropagation();

        // Collect all updates to batch them for the CURRENT pick mode only
        const updates = new Map();

        selectedPositions.forEach((key) => {
          const [scenarioId, position] = key.split('_');

          // Find the scenario in the CURRENT pick mode's scenarios
          const scenario = scenarios.find(s => String(s.id) === String(scenarioId));

          if (scenario) {
            if (!updates.has(scenarioId)) {
              // Initialize picks array if it doesn't exist
              updates.set(scenarioId, scenario.picks ? [...scenario.picks] : Array(24).fill(null));
            }
            const picks = updates.get(scenarioId);
            const posIndex = parseInt(position) - 1;
            picks[posIndex] = null;
          }
        });

        // Apply all updates - batch them through a single update per scenario
        // Convert string keys to numbers and apply updates
        updates.forEach((picks, scenarioIdKey) => {
          const scenarioId = typeof scenarioIdKey === 'string' && !isNaN(scenarioIdKey)
            ? Number(scenarioIdKey)
            : scenarioIdKey;
          updateScenario(pickMode, scenarioId, { picks });
        });

        // Clear selection
        setSelectedPositions(new Set());
      }

      // Handle Delete on forehead multi-selection
      if ((e.key === 'Backspace' || e.key === 'Delete') && foreheadSelection.size > 0 && !showPopup) {
        e.preventDefault();
        e.stopPropagation();
        setForeheadPicks(prev => {
          const next = [...prev];
          foreheadSelection.forEach(idx => { next[idx] = null; });
          return next;
        });
        setForeheadConnectors(prev => prev.filter(c => !foreheadSelection.has(c.from) && !foreheadSelection.has(c.to)));
        setForeheadSelection(new Set());
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedPositions, foreheadSelection, scenarios, pickMode, showPopup, clipboard, undo, redo, focusedBox, updateScenario, setForeheadPicks]);

  // Define which position numbers belong to Radiant and Dire
  // Updated for Patch 4.0: Left column is now 1,2,5,8,10,11,14,15,18,19,21,23
  const radiantPositions = [...game.board.radiantBans, ...game.board.radiantPicks].sort((a, b) => a - b);

  // Define which positions are ban boxes
  const banPositions = [...game.board.radiantBans, ...game.board.direBans].sort((a, b) => a - b);

  // Create pick box for a given position and scenario
  const renderPickBox = React.useCallback((scenarioIndex, position, skipRef = false) => {
    const scenario = scenarios[scenarioIndex];
    if (!scenario) return null;

    const scenarioId = scenario.id;
    const isRadiant = radiantPositions.includes(position);
    const team = isRadiant ? 'Radiant' : 'Dire';
    const pick = scenario.picks ? scenario.picks[position - 1] : null;
    const isFilled = Boolean(pick?.hero);
    const isBanBox = banPositions.includes(position);
    const isSelected = activeScenarioIndex === scenarioIndex && selectedBox === position;
    const isActive = isSelected && showPopup;
    const isMultiSelected = selectedPositions.has(`${scenarioId}_${position}`);
    const isFocused = focusedBox.scenarioIndex === scenarioIndex && focusedBox.position === position;

    const BoxComponent = isBanBox ? BanBox : PickBox;

    // Determine the appropriate size based on the position
    const isLargeBox = DraftState.getLargeBoxSequences().includes(position.toString());
    const imageSize = isLargeBox ? 'large' : 'medium';

    return (
      <BoxComponent
        key={`${scenarioId}_${position}`}
        $position={position}
        $team={team}
        $isSimulatorView={isSimulatorView}
        data-position={position}
        data-team={team}
        data-scenario-id={scenarioId}
        data-connector-target="true"
        tabIndex={isSimulatorView ? -1 : 0}
        className={`${!isSimulatorView && isSelected ? 'selected' : ''} ${isFilled ? 'filled' : ''} ${!isSimulatorView && isActive ? 'active' : ''} ${!isSimulatorView && isMultiSelected ? 'multi-selected' : ''} ${!isSimulatorView && isFocused ? 'focused' : ''}`}
        onClick={isSimulatorView ? undefined : (e) => {
          // Check for Cmd+Click to duplicate and open hero selector
          if (handleCmdClick(e, scenarioId, position, scenarioIndex)) {
            return;
          }
          // Normal click behavior - clear search first
          setHeroSearch('');
          if (searchInputRef.current) {
            searchInputRef.current.value = '';
          }
          handleBoxClick(scenarioIndex, position);
        }}
        onContextMenu={isSimulatorView ? undefined : (e) => handleContextMenu(e, scenarioId, position)}
        onFocus={isSimulatorView ? undefined : () => setFocusedBox({ scenarioIndex, position })}
        onTouchStart={isSimulatorView ? undefined : (e) => handleBoxTouchStart(e, scenarioId, position, isFilled)}
        onTouchMove={isSimulatorView ? undefined : handleBoxTouchMove}
        onTouchEnd={isSimulatorView ? undefined : cancelLongPress}
        onTouchCancel={isSimulatorView ? undefined : cancelLongPress}
        ref={skipRef ? undefined : (el => boxRefs.current[`${scenarioId}_${position}`] = el)}
        style={{
          ...(!isSimulatorView && isActive && {
            boxShadow: `0 0 12px 2px ${isRadiant ? 'rgba(146, 255, 146, 0.7)' : 'rgba(255, 107, 107, 0.7)'}`
          }),
          ...(!isSimulatorView && isFocused && !isActive && {
            outline: `2px solid ${isRadiant ? 'rgba(146, 255, 146, 0.9)' : 'rgba(255, 107, 107, 0.9)'}`,
            outlineOffset: '2px'
          })
        }}
      >
        <PositionLabel $isBan={isBanBox}>{position}</PositionLabel>
        <HeroContainer>
          {pick?.hero && (
            <HeroImage
              key={`${pick.hero.id}_${position}_${scenarioId}`}
              hero={pick.hero}
              size={imageSize}
              $grayscale={isBanBox}
            />
          )}
        </HeroContainer>
        {pick?.isBanned && <BanIndicator>X</BanIndicator>}
        {isBanBox && <BanCross>✕</BanCross>}
        {/* Time delta badge for live GSI picks — only for the currently-tracked live match */}
        {isFilled && scenario.fromMatchPull && scenario.gsiMatchId && scenario.gsiMatchId === lastSeenMatchIdRef.current && (() => {
          const ts = livePickTimestamps[position];
          if (!ts) return null;
          const order = livePickOrderRef.current;
          const idx = order.indexOf(position);
          if (idx < 0) return null;
          // First pick: delta from when match was detected
          // Subsequent picks: delta from previous pick
          const prevTs = idx === 0
            ? matchDetectedAtRef.current
            : livePickTimestamps[order[idx - 1]];
          if (!prevTs) return null;
          const secs = Math.round((ts - prevTs) / 1000);
          return <LivePickTimeBadge>{secs}s</LivePickTimeBadge>;
        })()}
        {/* Counter-relation connector handles on all 4 sides */}
        {isFilled && !isReadOnly && (() => {
          const isDragSource = connectorDrag && connectorDrag.scenarioId === scenarioId && connectorDrag.fromPosition === position;
          const cls = isDragSource ? 'dragging' : '';
          const onDown = (e) => handleConnectorHandleMouseDown(e, scenarioId, position);
          return (
            <>
              <ConnectorHandle data-export-exclude="" className={`top ${cls}`}    onMouseDown={onDown} title="Drag to mark counter target" />
              <ConnectorHandle data-export-exclude="" className={`right ${cls}`}  onMouseDown={onDown} title="Drag to mark counter target" />
              <ConnectorHandle data-export-exclude="" className={`bottom ${cls}`} onMouseDown={onDown} title="Drag to mark counter target" />
              <ConnectorHandle data-export-exclude="" className={`left ${cls}`}   onMouseDown={onDown} title="Drag to mark counter target" />
            </>
          );
        })()}
        {/* Show match badge if this hero matches a pulled match hero (and not from the pulled match scenario itself) */}
        {(() => {
          const hasHero = pick?.hero;
          const hasPulledHeroes = pulledMatchHeroes && pulledMatchHeroes.length > 0;

          // Hero ID can be either:
          // 1. Numeric (from OpenDota match data): 66, 53, etc.
          // 2. String (from local hero selection): "chen", "sand_king", etc.
          // We need to normalize both for comparison

          let currentHeroId = hasHero ? pick.hero.id : null;
          let normalizedHeroId = null;

          if (currentHeroId) {
            if (typeof currentHeroId === 'number') {
              // Already numeric (from pulled match)
              normalizedHeroId = currentHeroId;
            } else if (typeof currentHeroId === 'string') {
              // String ID from local selection
              // First try numeric conversion (in case it's a numeric string like "66")
              const numericId = Number(currentHeroId);
              if (!isNaN(numericId)) {
                normalizedHeroId = numericId;
              } else {
                // Text ID like "chen" - look up in mapping
                normalizedHeroId = HERO_ID_MAP[currentHeroId.toLowerCase()] || currentHeroId;
              }
            }
          }

          // Find ALL occurrences of this hero in pulled match data
          // pulledMatchHeroes is now an array of {heroId, position, matchId} objects
          // Can have multiple entries for the same hero from different matches
          let matchedHeroes = [];
          if (normalizedHeroId && typeof normalizedHeroId === 'number' && hasPulledHeroes) {
            matchedHeroes = pulledMatchHeroes.filter(item => Number(item.heroId) === normalizedHeroId);
          }

          const hasMatches = matchedHeroes.length > 0;
          const shouldShowBadges = hasMatches && !scenario.fromMatchPull;

          // For pulled match scenarios: only show border if this hero also exists in a PLANNED scenario
          // For planned scenarios: always show border if matched
          let shouldShowBorder = false;
          if (hasMatches) {
            if (scenario.fromMatchPull) {
              // In a pulled match: check if this hero exists in any non-pulled scenario
              // We need to check all scenarios for this hero
              const heroExistsInPlannedScenarios = scenarios.some(s =>
                !s.fromMatchPull && s.picks && s.picks.some(p => {
                  if (!p?.hero) return false;
                  const otherId = typeof p.hero.id === 'number' ? p.hero.id : HERO_ID_MAP[p.hero.id?.toLowerCase()];
                  return otherId === normalizedHeroId;
                })
              );
              shouldShowBorder = heroExistsInPlannedScenarios;
            } else {
              // In a planned scenario: always show border if matched
              shouldShowBorder = true;
            }
          }



          // Determine border color based on whether hero appears in picks, bans, or both
          let borderType = 'purple'; // default
          if (hasMatches && matchedHeroes.length > 0) {
            const appearsInPicks = matchedHeroes.some(match => {
              const pos = match.position;
              return (pos >= 8 && pos <= 9) || (pos >= 13 && pos <= 18) || (pos >= 23 && pos <= 24);
            });
            const appearsInBans = matchedHeroes.some(match => {
              const pos = match.position;
              return (pos >= 1 && pos <= 7) || (pos >= 10 && pos <= 12) || (pos >= 19 && pos <= 22);
            });

            if (appearsInPicks && appearsInBans) {
              borderType = 'purple'; // Both picked and banned
            } else if (appearsInPicks) {
              borderType = 'green'; // Only picked
            } else if (appearsInBans) {
              borderType = 'red'; // Only banned
            }
          }

          return (
            <>
              {showOpenDotaHighlights && shouldShowBadges && (
                <MatchBadgesContainer>
                  {matchedHeroes.map((match, index) => {
                    // Badge color based on the POSITION's color rules:
                    // 1-7: RED (bans), 8-9: GREEN (picks), 10-12: RED (bans),
                    // 13-18: GREEN (picks), 19-22: RED (bans), 23-24: GREEN (picks)
                    const matchPos = match.position;
                    const isGreenPosition = (matchPos >= 8 && matchPos <= 9) ||
                                           (matchPos >= 13 && matchPos <= 18) ||
                                           (matchPos >= 23 && matchPos <= 24);
                    const isSamePosition = match.position === position;
                    return (
                      <MatchBadge
                        key={`${match.matchId}-${match.position}-${index}`}
                        $isSamePosition={isGreenPosition}
                        title={isSamePosition
                          ? `✓ Match ${index + 1}: Same position! (${match.position}) - ${isGreenPosition ? 'Pick' : 'Ban'}`
                          : `Match ${index + 1}: Different position, was at ${match.position} - ${isGreenPosition ? 'Pick' : 'Ban'}`}
                      >
                        {match.position}
                      </MatchBadge>
                    );
                  })}
                </MatchBadgesContainer>
              )}
              {showOpenDotaHighlights && shouldShowBorder && <MatchedHeroBorder $borderType={borderType} />}
            </>
          );
        })()}
      </BoxComponent>
    );
  }, [scenarios, activeScenarioIndex, selectedBox, showPopup, handleBoxClick, boxRefs, selectedPositions, focusedBox, isSimulatorView, pulledMatchHeroes, showOpenDotaHighlights, livePickTimestamps]);

  // Gallery mode: auto-zoom body to fill the viewport height after render
  useEffect(() => {
    if (!isGalleryView) return;
    const applyZoom = () => {
      try {
        const bodyH = document.documentElement.scrollHeight;
        if (bodyH > 100) {
          const scale = Math.min(window.innerHeight / bodyH, 3);
          document.body.style.zoom = String(scale.toFixed(4));
          document.body.style.overflow = 'hidden';
        }
      } catch (e) {}
    };
    const t = setTimeout(applyZoom, 400);
    return () => clearTimeout(t);
  }, [isGalleryView]);

  return (
    <MainContainer
      $embed={isEmbedView}
      $gallery={isGalleryView}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      {/* Hide info inputs in View Only mode, Simulator view, Marketing Demo, or Embed view */}
      {!isSimulatorView && !isMarketingDemo && !isEmbedView && !(isSharedView && sharedPermission === 'view') && (
        <InfoInputRow>
          <InfoInput
            placeholder="Team Name"
            value={teamName}
            onChange={e => setTeamName(e.target.value)}
          />
          <InfoInput
            placeholder="Tournament Name"
            value={tournament}
            onChange={(e) => setTournament(e.target.value)}
          />
          <InfoInput
            type="text"
            value={stage}
            onChange={(e) => setStage(e.target.value)}
            placeholder="Stage Name"
          />
          <InfoDateInput
            value={draftDate}
            onChange={e => setDraftDate(e.target.value)}
          />
        </InfoInputRow>
      )}
      {/* Hide entire button row in simulator view or gallery view. In embed view
          we keep the row but render it as a fixed vertical sidebar. `hideToolbar`
          (Scout Simulator) hides it entirely. */}
      {!isSimulatorView && !isGalleryView && !hideToolbar && (
        <TopButtonRow $embed={isEmbedView}>
          {/* Always show 1st/2nd Pick toggle */}
          {/* Undo / Redo — icon only, always visible */}
          <AddScenarioButton
            onClick={undo}
            disabled={!canUndo}
            title="Undo (Cmd+Z)"
            style={{
              background: canUndo ? '#555' : '#333',
              backgroundImage: 'none',
              minWidth: 'unset',
              padding: '0 14px',
              opacity: canUndo ? 1 : 0.4,
              cursor: canUndo ? 'pointer' : 'default',
            }}
          >↩</AddScenarioButton>
          <AddScenarioButton
            onClick={redo}
            disabled={!canRedo}
            title="Redo (Cmd+Y)"
            style={{
              background: canRedo ? '#555' : '#333',
              backgroundImage: 'none',
              minWidth: 'unset',
              padding: '0 14px',
              opacity: canRedo ? 1 : 0.4,
              cursor: canRedo ? 'pointer' : 'default',
            }}
          >↪</AddScenarioButton>

          {!isEmbedView && (
          <ToggleSwitchContainer>
            <ToggleSwitch>
              <ToggleOption
                $active={pickMode === 'first'}
                onClick={() => setPickMode('first')}
              >
                1st Pick
              </ToggleOption>
              <ToggleOption
                $active={pickMode === 'second'}
                onClick={() => setPickMode('second')}
              >
                2nd Pick
              </ToggleOption>
            </ToggleSwitch>
          </ToggleSwitchContainer>
          )}

        {/* Only show these buttons if NOT in View Only shared mode */}
        {!(isSharedView && sharedPermission === 'view') && (
          <>
            {/* Show Add Scenario only if not restricted by showButtons prop */}
            {(!showButtons || showButtons.addScenario) && !(maxScenariosDemo && scenarios.length >= maxScenariosDemo) && (
              <AddScenarioButton onClick={handleAddScenario}>
                <span style={{ fontSize: '18px', marginRight: '5px' }}>+</span> Add Scenario
              </AddScenarioButton>
            )}
            {!showButtons && !isEmbedView && (
              <>
                {!isEmbedView && (
                <NewDraftButton onClick={handleNewDraft}>
                  <span style={{ fontSize: '18px', marginRight: '5px' }}>📄</span> New Draft
                </NewDraftButton>
                )}
                {/* View Records - only for Premium users (Firebase-saved drafts) */}
                {UserTierService.hasFeatureAccess(userProfile, 'canSeeViewRecords') && (
                <RecordViewerButton onClick={() => navigate('/records')}>
                  <span style={{ fontSize: '18px', marginRight: '5px' }}>📁</span> View Records
                </RecordViewerButton>
                )}
                {/* Scout Simulator - draft vs a real team from historical pro drafts */}
                <RecordViewerButton onClick={() => navigate('/scout-sim')}>
                  <span style={{ fontSize: '18px', marginRight: '5px' }}>🔭</span> Scout Sim
                </RecordViewerButton>
                {/* Opponent hero pool from ygbuff watch-out highlights.
                    The popover is a SIBLING of the button, not a child —
                    nesting a <select>/<button> inside this <button> is
                    invalid HTML (a button can't contain interactive
                    descendants) and made clicks inside the popover
                    unreliable (React logs a hydration warning for it). */}
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <OppPoolButton onClick={() => setOppPoolOpen(v => !v)}>
                    <span style={{ fontSize: '16px', marginRight: '5px' }}>⚠️</span> Opp Pool
                  </OppPoolButton>
                  {oppPoolOpen && (
                    <OppPoolPopover onClick={e => e.stopPropagation()}>
                      <span className="pool-hint">Load watch-out heroes from ygbuff IntPlayer tab</span>
                      {oppTeamsError ? (
                        <>
                          <button className="load-btn" onClick={fetchOppTeams}>⚠ Failed — Retry</button>
                          <span className="pool-hint" style={{color:'#fca5a5', wordBreak:'break-all'}}>{oppTeamsError}</span>
                        </>
                      ) : oppTeamsLoading ? (
                        <select disabled><option>Loading teams…</option></select>
                      ) : (
                        <select value={oppPoolTeam} onChange={e => setOppPoolTeam(e.target.value)}>
                          {oppPoolTeams.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      )}
                      <button className="load-btn" disabled={oppPoolLoading || !oppPoolTeam || oppTeamsLoading || !!oppTeamsError} onClick={loadOppPool}>
                        {oppPoolLoading ? 'Loading…' : 'Load Pool → Forehead'}
                      </button>
                    </OppPoolPopover>
                  )}
                </div>
              </>
            )}
          </>
        )}
        {/* Hide Save Draft button in marketing mode */}
        {!showButtons && !isEmbedView && (
          <ExportJpegButton onClick={handleExportBothPickModes} disabled={isExportingJpeg}>
            <span style={{ marginRight: '4px' }}>📸</span> {isExportingJpeg ? 'Exporting…' : 'Export JPEG'}
          </ExportJpegButton>
        )}
        {!showButtons && (
          <SaveDraftButton onClick={handleSaveDraft}>
            Save Draft
          </SaveDraftButton>
        )}
        </TopButtonRow>
      )}
      {/* Hero-pool row (15 boxes by default, grows for Opp Pool/manual) between the button row and the draft boards */}
      {!isSimulatorView && !isMarketingDemo && !isEmbedView && !(isSharedView && sharedPermission === 'view') && (
        <ForeheadRow
          ref={foreheadRowRef}
          style={(bracketRects.length > 0 || foreheadConnectors.length > 0) ? {
            // Both the Opp Pool bracket labels (sit ~52px above the boxes)
            // and the connector arc (rises ~70px above the boxes) render
            // above the boxes' natural top — reserve room via padding (not
            // margin: html-to-image's foreignObject capture silently fails
            // to paint ANY of this row's content when the captured root
            // node has a margin — verified empirically — while padding,
            // being part of the node's own box/clientHeight, works fine).
            paddingTop: `${Math.max(bracketRects.length > 0 ? 52 : 0, foreheadConnectors.length > 0 ? 76 : 0)}px`,
            // When Opp Pool groups exist, the row may wrap to 2+ lines and a
            // bracket can land on row 1+ — the default row-gap is too tight
            // to fit its label. Reserve room unconditionally (not only once
            // a wrapped bracket is actually measured) so the wider gap is
            // already in place before bracketRects reads box positions off
            // it — toggling this FROM the measurement would race the DOM.
            rowGap: oppPoolGroups.length > 0 ? '52px' : undefined,
          } : undefined}
        >
          {bracketRects.map((r, i) => (
            <ForeheadBracket
              key={`bracket-${i}`}
              data-bracket-start={r.start}
              data-bracket-end={r.end}
              data-bracket-group={r.groupIndex}
              data-bracket-group-start={r.groupStart}
              data-bracket-group-end={r.groupEnd}
              style={{
                left: r.left,
                width: r.width,
                top: `${r.rowTop - 16}px`,
              }}
            >
              <span>{r.player}{r.position ? ` (${r.position})` : ''}</span>
            </ForeheadBracket>
          ))}
          {foreheadPicks.map((pick, idx) => {
            const isSelectedHere = isForeheadMode && showPopup && selectedBox === idx + 1;
            const gsiBanned = pick?.hero?.name && gsiBannedNames.has(pick.hero.name.toLowerCase());
            const manuallyCrossed = manualCrosses.has(idx);
            const isCrossed = gsiBanned || manuallyCrossed;
            const isForeheadSelected = foreheadSelection.has(idx);
            const isFilled = !!pick?.hero;
            const isDragSource = foreheadConnectorDrag && foreheadConnectorDrag.fromIdx === idx;
            // Neon border if any arrow pointing TO this box came from a banned/crossed source
            const isNeonTarget = foreheadConnectors.some(c => {
              if (c.to !== idx) return false;
              const srcPick = foreheadPicks[c.from];
              const srcGsiBanned = srcPick?.hero?.name && gsiBannedNames.has(srcPick.hero.name.toLowerCase());
              const srcCrossed = srcGsiBanned || manualCrosses.has(c.from);
              return srcCrossed;
            });
            return (
              <PickBox
                key={`forehead-${idx}`}
                ref={el => foreheadBoxRefs.current[idx] = el}
                $position={'8'}
                $team={'Radiant'}
                data-forehead-target="true"
                data-forehead-idx={idx}
                className={`${isFilled ? 'filled' : ''} ${isSelectedHere ? 'selected' : ''} ${isForeheadSelected ? 'multi-selected' : ''}`}
                onClick={(e) => handleForeheadBoxClick(idx, e)}
                onContextMenu={(e) => handleForeheadContextMenu(e, idx)}
                title={pick?.hero ? `${pick.hero.name}${isCrossed ? ' — BANNED' : ''} (Cmd/Alt+click to toggle cross)` : `Slot ${idx + 1} (Cmd/Alt+click to toggle cross)`}
                style={{ position: 'relative', ...(isNeonTarget && { boxShadow: '0 0 8px 3px #00ff88, 0 0 18px 5px rgba(0,255,136,0.5)', border: '2px solid #00ff88' }) }}
              >
                {pick?.hero && (
                  <HeroContainer>
                    <HeroImage hero={pick.hero} $grayscale={isCrossed} />
                  </HeroContainer>
                )}
                {pick?.label && !isCrossed && <ForeheadLabel $winRate={pick.winRate}>{pick.label}</ForeheadLabel>}
                {isCrossed && <BanCrossOverlay />}
                {isFilled && !isReadOnly && (
                  <ConnectorHandle
                    className={`top ${isDragSource ? 'dragging' : ''}`}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setForeheadConnectorDrag({ fromIdx: idx, mouseX: e.clientX, mouseY: e.clientY });
                    }}
                    title="Drag to draw relation arrow"
                  />
                )}
              </PickBox>
            );
          })}
          <button
            type="button"
            data-export-exclude=""
            onClick={clearForeheadCrosses}
            title="Refresh — clear all ban crosses"
            style={{ marginLeft: '8px', padding: '5px 8px', background: '#333', color: '#fff', border: '1px solid #555', borderRadius: '6px', cursor: 'pointer', fontSize: '16px', lineHeight: 1 }}
          >↺</button>
          <button
            type="button"
            data-export-exclude=""
            onClick={() => { setForeheadPicks(Array(FOREHEAD_MIN_SLOTS).fill(null)); setForeheadConnectors([]); setOppPoolGroups([]); }}
            title="Clear all — remove all heroes and arrows"
            style={{ marginLeft: '4px', padding: '5px 8px', background: '#4a1515', color: '#fff', border: '1px solid #7a2020', borderRadius: '6px', cursor: 'pointer', fontSize: '15px', lineHeight: 1 }}
          >✕</button>
          <button
            type="button"
            data-export-exclude=""
            onClick={toggleForeheadExpanded}
            title={isForeheadExpanded ? 'Collapse to default slots' : `Show ${FOREHEAD_EXPAND_STEP} more slots`}
            style={{ marginLeft: '4px', padding: '5px 8px', background: '#333', color: '#fff', border: '1px solid #555', borderRadius: '6px', cursor: 'pointer', fontSize: '15px', lineHeight: 1 }}
          >{isForeheadExpanded ? '▲' : '▼'}</button>
          {foreheadConnectors.length > 0 && (
            <button
              type="button"
              data-export-exclude=""
              onClick={() => setForeheadConnectors([])}
              title="Clear arrows — remove all relation arrows"
              style={{ marginLeft: '4px', padding: '5px 8px', background: '#1b5e20', color: '#fff', border: '1px solid #2e7d32', borderRadius: '6px', cursor: 'pointer', fontSize: '15px', lineHeight: 1 }}
            >⤬</button>
          )}
          {/* SVG overlay for forehead connector arrows. Positioned relative to
              ForeheadRow itself (not the viewport) — a position:fixed overlay
              anchored to raw getBoundingClientRect() coordinates displays
              fine in normal browsing, but is meaningless once this row is
              captured in isolation for JPEG export (html-to-image renders
              the row into its own offscreen box, with no viewport for
              "fixed" to anchor to), so the arrows silently vanished from the
              exported image. */}
          {(() => {
            const arcLift = 70; // px the arc rises above the box tops
            const rowEl = foreheadRowRef.current;
            const rowRect = rowEl ? rowEl.getBoundingClientRect() : null;
            const getTopCenter = (idx) => {
              const el = foreheadBoxRefs.current[idx];
              if (!el || !rowRect) return null;
              const r = el.getBoundingClientRect();
              return { x: r.left + r.width / 2 - rowRect.left, y: r.top - rowRect.top };
            };
            const markerId = 'fh-arrow';
            const hasContent = foreheadConnectors.length > 0 || !!foreheadConnectorDrag;
            if (!hasContent || !rowRect) return null;
            let dragPath = null;
            if (foreheadConnectorDrag) {
              const from = getTopCenter(foreheadConnectorDrag.fromIdx);
              if (from) {
                const tx = foreheadConnectorDrag.mouseX - rowRect.left;
                const ty = foreheadConnectorDrag.mouseY - rowRect.top;
                const mx = (from.x + tx) / 2;
                const cy = Math.min(from.y, ty) - arcLift;
                dragPath = `M ${from.x} ${from.y} Q ${mx} ${cy} ${tx} ${ty}`;
              }
            }
            return (
              <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 200, overflow: 'visible' }}>
                <defs>
                  <marker id={markerId} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
                    <path d="M 0 0 L 10 5 L 0 10 z" fill="#00ff88" />
                  </marker>
                </defs>
                {foreheadConnectors.map((c, i) => {
                  const a = getTopCenter(c.from);
                  const b = getTopCenter(c.to);
                  if (!a || !b) return null;
                  const mx = (a.x + b.x) / 2;
                  const cy = Math.min(a.y, b.y) - arcLift;
                  return (
                    <React.Fragment key={i}>
                      <path
                        d={`M ${a.x} ${a.y} Q ${mx} ${cy} ${b.x} ${b.y}`}
                        fill="none"
                        stroke="rgba(0,255,136,0.85)"
                        strokeWidth="3"
                        strokeDasharray="8 6"
                        strokeLinecap="round"
                        markerEnd={`url(#${markerId})`}
                        style={{ pointerEvents: 'none' }}
                      />
                      <path
                        d={`M ${a.x} ${a.y} Q ${mx} ${cy} ${b.x} ${b.y}`}
                        fill="none"
                        stroke="transparent"
                        strokeWidth="20"
                        style={{ cursor: 'context-menu', pointerEvents: 'stroke' }}
                        onContextMenu={(e) => { e.preventDefault(); setForeheadConnectors(prev => prev.filter((_, j) => j !== i)); }}
                      />
                    </React.Fragment>
                  );
                })}
                {dragPath && (
                  <path
                    d={dragPath}
                    fill="none"
                    stroke="#00b4d8"
                    strokeWidth="3"
                    strokeDasharray="5 4"
                    strokeLinecap="round"
                  />
                )}
              </svg>
            );
          })()}
        </ForeheadRow>
      )}
      <ScenarioBoardsRow $gallery={isGalleryView} ref={(el) => {
        scrollContainerRef.current = el;
        draftContentRef.current = el;
      }}>
        {scenarios.map((scenario, index) => (
    <DraftBoardContainer
            key={scenario.id}
      className="draft-board"
            ref={el => scenarioRefs.current[scenario.id] = el}
            style={(() => {
              const anns = scenario.annotations || [];
              // Reserve only as much margin as the longest label on each side
              // actually needs, so the next board starts at the end of the text.
              const reserve = (side) => {
                const xs = anns.filter(a => a.side === side);
                if (!xs.length) return undefined;
                return Math.max(...xs.map(a => laneLabelWidth(a.label))) + (LANE_LABEL_OFFSET - 12 + 6);
              };
              return {
                minWidth: 280,
                maxWidth: 280,
                marginLeft: reserve('left'),
                marginRight: reserve('right'),
              };
            })()}
            $swapSides={scenario.swapSides || false}
            onDragOver={(e) => handleScenarioDragOver(e, scenario.id)}
            onDragLeave={(e) => handleScenarioDragLeave(e, scenario.id)}
            $isDragging={draggedScenarioId === scenario.id}
            $isOver={dragOverScenarioId ? (typeof dragOverScenarioId === 'object' ? dragOverScenarioId.id === scenario.id : dragOverScenarioId === scenario.id) : false}
            $justDropped={droppedScenarioId === scenario.id}
            $fromMatchPull={scenario.fromMatchPull}
            $matchColorIndex={scenario.matchColorIndex}
            $hasPracticeMatchup={Boolean(scenario.practiceMatchups && ['safe','mid','off'].some(k => scenario.practiceMatchups[k] && ([...( scenario.practiceMatchups[k].radiant||[]),...(scenario.practiceMatchups[k].dire||[])].some(Boolean))))}
            $neonHighlight={scenario.neonHighlight || false}
    >
      {/* Drag handle for reordering scenarios - hide for free tier.
          Actually unmounted during export — see the top-bar comment below. */}
      {UserTierService.hasFeatureAccess(userProfile, 'canSeeSorting') && !isExportingJpeg && (
        <DragHandle
          onDragStart={(e) => handleScenarioDragStart(e, scenario.id)}
          onDragEnd={(e) => handleScenarioDragEnd(e)}
        />
      )}

      {/* Top bar: Duplicate, Swap Sides, Delete (bin icon only).
          Actually unmounted (not just visually filtered) during export, so
          the live DOM height used to size the export canvas reflects its
          absence — filtering it from the html-to-image clone alone left the
          canvas sized for the with-buttons layout, showing as blank space
          ("chin") below the shorter, button-free captured content. */}
      {!isSimulatorView && !isExportingJpeg && (
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px', gap: '8px', flexWrap: 'wrap' }}>
          {(!showButtons || showButtons.duplicate) && UserTierService.hasFeatureAccess(userProfile, 'canDuplicate') && (
            <DuplicateScenarioButton onClick={() => duplicateScenario(pickMode, scenario.id)}>
              <span style={{ marginRight: '4px' }}>📋</span> Duplicate
            </DuplicateScenarioButton>
          )}
          <ClearButton
            onClick={() => handleSwapPulledBoard(scenario)}
            style={{
              padding: '4px 8px',
              fontSize: '16px',
              minWidth: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: (scenario.swapSides || false) ? '#2196F3' : '#333',
              border: (scenario.swapSides || false) ? '1px solid #2196F3' : '1px solid #555'
            }}
          >
            {(scenario.swapSides || false) ? '↔️' : '⇄'}
          </ClearButton>
          <ClearButton
            onClick={() => updateScenario(pickMode, scenario.id, { neonHighlight: !(scenario.neonHighlight || false) })}
            title={(scenario.neonHighlight || false) ? 'Remove neon highlight' : 'Highlight this board'}
            style={{
              padding: '4px 8px',
              fontSize: '16px',
              minWidth: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: (scenario.neonHighlight || false) ? '#ff00e5' : '#333',
              border: (scenario.neonHighlight || false) ? '1px solid #ff00e5' : '1px solid #555',
              boxShadow: (scenario.neonHighlight || false) ? '0 0 10px rgba(255, 0, 229, 0.7)' : 'none'
            }}
          >
            ✨
          </ClearButton>
          {(!showButtons || showButtons.delete) && scenarios.length > 1 && (
            <DeleteScenarioButton onClick={() => deleteScenario(pickMode, scenario.id)} title="Delete scenario">
              🗑
            </DeleteScenarioButton>
          )}
        </div>
      )}

      <TeamsContainer>
        {(() => {
          const isSwapped = scenario.swapSides || false;

          // Column rendering (from PicksContainer below):
          // - NOT swapped: Radiant left (positions 1,4,7...), Dire right (positions 2,3,5...)
          // - SWAPPED: Dire left (positions 2,3,5...), Radiant right (positions 1,4,7...)

          // Label logic:
          // - NOT swapped: Show "Dire" left, "Radiant" right (to match your requirement)
          // - SWAPPED: Show "Radiant" left, "Dire" right (opposite of columns, as requested)

          let leftLabel, rightLabel;
          if (!isSwapped) {
            // Not swapped: Radiant column left, Dire column right
            // But you want: Dire label left, Radiant label right
            leftLabel = 'Dire';
            rightLabel = 'Radiant';
          } else {
            // Swapped: Dire column left, Radiant column right
            // You want: Radiant label left, Dire label right (opposite)
            leftLabel = 'Radiant';
            rightLabel = 'Dire';
          }

          return (
            <>
              <TeamHeader $team={leftLabel}>{leftLabel}</TeamHeader>
              <TeamHeader $team={rightLabel}>{rightLabel}</TeamHeader>
            </>
          );
        })()}
      </TeamsContainer>

      <PicksContainer ref={el => { picksContainerRefs.current[scenario.id] = el; }}>
        {!(scenario.swapSides || false) ? (
          <>
            <TeamColumn $team="Radiant" $swapped={false}>
              {/* Radiant column - Patch 4.0 with side-by-side alignment */}
                    {renderPickBox(index, 1)}
                    {renderPickBox(index, 2)}
                    {renderPickBox(index, 5)}
                    <EmptySpacer $position={6} key={`spacer-L-6-${index}`} />
                    <EmptySpacer $position={7} key={`spacer-L-7-${index}`} />
                    {renderPickBox(index, 8)}
                    {renderPickBox(index, 10)}
                    {renderPickBox(index, 11)}
                    {renderPickBox(index, 14)}
                    {renderPickBox(index, 15)}
                    {renderPickBox(index, 18)}
                    {renderPickBox(index, 19)}
                    {renderPickBox(index, 21)}
                    {renderPickBox(index, 23)}
            </TeamColumn>

            <TeamColumn $team="Dire" $swapped={false}>
              {/* Dire column - Patch 4.0 with side-by-side alignment */}
                    <EmptySpacer $position={1} key={`spacer-R-1-${index}`} />
                    {renderPickBox(index, 3)}
                    {renderPickBox(index, 4)}
                    {renderPickBox(index, 6)}
                    {renderPickBox(index, 7)}
                    {renderPickBox(index, 9)}
                    <EmptySpacer $position={10} key={`spacer-R-10-${index}`} />
                    {renderPickBox(index, 12)}
                    {renderPickBox(index, 13)}
                    {renderPickBox(index, 16)}
                    {renderPickBox(index, 17)}
                    {renderPickBox(index, 20)}
                    {renderPickBox(index, 22)}
                    {renderPickBox(index, 24)}
            </TeamColumn>
          </>
        ) : (
          <>
            <TeamColumn $team="Dire" $swapped={true}>
              {/* Dire column - swapped to left - Patch 4.0 with side-by-side alignment */}
                    <EmptySpacer $position={1} key={`spacer-R-1-${index}`} />
                    {renderPickBox(index, 3)}
                    {renderPickBox(index, 4)}
                    {renderPickBox(index, 6)}
                    {renderPickBox(index, 7)}
                    {renderPickBox(index, 9)}
                    <EmptySpacer $position={10} key={`spacer-R-10-${index}`} />
                    {renderPickBox(index, 12)}
                    {renderPickBox(index, 13)}
                    {renderPickBox(index, 16)}
                    {renderPickBox(index, 17)}
                    {renderPickBox(index, 20)}
                    {renderPickBox(index, 22)}
                    {renderPickBox(index, 24)}
            </TeamColumn>

            <TeamColumn $team="Radiant" $swapped={true}>
              {/* Radiant column - swapped to right - Patch 4.0 with side-by-side alignment */}
                    {renderPickBox(index, 1)}
                    {renderPickBox(index, 2)}
                    {renderPickBox(index, 5)}
                    <EmptySpacer $position={6} key={`spacer-L-6-${index}`} />
                    <EmptySpacer $position={7} key={`spacer-L-7-${index}`} />
                    {renderPickBox(index, 8)}
                    {renderPickBox(index, 10)}
                    {renderPickBox(index, 11)}
                    {renderPickBox(index, 14)}
                    {renderPickBox(index, 15)}
                    {renderPickBox(index, 18)}
                    {renderPickBox(index, 19)}
                    {renderPickBox(index, 21)}
                    {renderPickBox(index, 23)}
            </TeamColumn>
          </>
        )}
        {/* Counter-relation connector overlay */}
        {(() => {
          // connectorTick referenced to retrigger eval after layout
          void connectorTick;
          const container = picksContainerRefs.current[scenario.id];
          if (!container) return null;
          const cRect = container.getBoundingClientRect();
          // Use a viewBox that matches the SVG's VISUAL pixel dimensions so 1 user unit = 1 visual px.
          // This makes coord math work uniformly under CSS `zoom` on mobile portrait.
          const vbW = Math.max(1, cRect.width);
          const vbH = Math.max(1, cRect.height);
          const getCenter = (pos) => {
            const el = boxRefs.current[`${scenario.id}_${pos}`];
            if (!el) return null;
            const r = el.getBoundingClientRect();
            return {
              x: r.left - cRect.left + r.width / 2,
              y: r.top - cRect.top + r.height / 2,
            };
          };
          const isSwapped = scenario.swapSides || false;
          const radiantOnLeft = !isSwapped;
          const getSourceDot = (pos) => {
            const el = boxRefs.current[`${scenario.id}_${pos}`];
            if (!el) return null;
            const r = el.getBoundingClientRect();
            const isRad = radiantPositions.includes(pos);
            const dotOnRight = isRad ? radiantOnLeft : !radiantOnLeft;
            return {
              x: (dotOnRight ? r.right : r.left) - cRect.left,
              y: r.top - cRect.top + r.height / 2,
            };
          };
          const connectors = Array.isArray(scenario.counterConnectors) ? scenario.counterConnectors : [];
          const markerId = `cc-arrow-${scenario.id}`;
          const isDraggingHere = connectorDrag && connectorDrag.scenarioId === scenario.id;
          let dragLine = null;
          if (isDraggingHere) {
            const from = getCenter(connectorDrag.fromPosition);
            if (from) {
              dragLine = {
                x1: from.x,
                y1: from.y,
                x2: connectorDrag.mouseX - cRect.left,
                y2: connectorDrag.mouseY - cRect.top,
              };
            }
          }
          return (
            <ConnectorSvg viewBox={`0 0 ${vbW} ${vbH}`} preserveAspectRatio="none">
              <defs>
                <marker id={markerId} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
                  <path d="M 0 0 L 10 5 L 0 10 z" fill="#00ff88" />
                </marker>
              </defs>
              {connectors.map((c, i) => {
                const a = getCenter(c.from);
                const b = getCenter(c.to);
                if (!a || !b) return null;
                return (
                  <path
                    key={`${c.from}-${c.to}-${i}`}
                    className="connector-path"
                    d={`M ${a.x} ${a.y} L ${b.x} ${b.y}`}
                    markerEnd={`url(#${markerId})`}
                    onClick={(e) => { e.stopPropagation(); if (!isReadOnly) removeCounterConnector(scenario.id, i); }}
                    // Inline fallback for the visual attrs the .connector-path CSS
                    // class normally supplies — html-to-image's export capture
                    // doesn't reliably resolve styled-components rules onto
                    // cloned SVG elements, silently leaving the stroke unset
                    // (only the fill-based arrowhead marker would show).
                    fill="none"
                    stroke="#00ff88"
                    strokeWidth="4"
                    strokeDasharray="8 6"
                    strokeLinecap="round"
                  />
                );
              })}
              {dragLine && (
                <path
                  className="connector-path drag-preview"
                  d={`M ${dragLine.x1} ${dragLine.y1} L ${dragLine.x2} ${dragLine.y2}`}
                />
              )}
            </ConnectorSvg>
          );
        })()}

        {/* Lane bracket annotations (per-scenario) — drag a gutter to add */}
        <LaneAnnotations
          scenarioId={scenario.id}
          annotations={scenario.annotations}
          boxRefs={boxRefs}
          readOnly={isSimulatorView || isMarketingDemo}
          toolEnabled={bracketToolEnabled}
          onChange={(next) => updateScenario(pickMode, scenario.id, { annotations: next })}
        />
      </PicksContainer>

      {/* Scenario title input - hide in simulator view, marketing demo, or embed view */}
      {!isSimulatorView && !isMarketingDemo && !isEmbedView && (
        <ScenarioInput
          type="text"
          placeholder="Enter scenario name..."
          value={scenario.title || ''}
          onChange={(e) => updateScenario(pickMode, scenario.id, { title: e.target.value })}
        />
      )}

      {/* Scenario number and actions */}
      <ScenarioTitle>
        {isEmbedView ? (
          <span>Scenario {scenarios.indexOf(scenario) + 1}</span>
        ) : (
          <>
            {pickMode === 'first' ?
              <span style={{ color: '#1de9b6' }}>1st Pick</span> :
              <span style={{ color: '#61dafb' }}>2nd Pick</span>} Scenario {scenarios.indexOf(scenario) + 1}
          </>
        )}
      </ScenarioTitle>

      {/* Bottom action buttons: Copy, Practice Match-up, Clear All, Clear Relations.
          Actually unmounted during export — see the top-bar comment above. */}
      {!isSimulatorView && !isExportingJpeg && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '8px', marginBottom: '10px', gap: '8px', flexWrap: 'wrap' }}>
          {!showButtons && !isEmbedView && UserTierService.hasFeatureAccess(userProfile, 'canSeeCopyToPickMode') && (
            <CopyScenarioButton onClick={() => handleCopyToOtherMode(scenario, pickMode)}>
              <span style={{ marginRight: '4px' }}>📑</span> Copy to {pickMode === 'first' ? '2nd' : '1st'} Pick
            </CopyScenarioButton>
          )}
          {!showButtons && UserTierService.hasFeatureAccess(userProfile, 'canSeePracticeMatchup') && (
            <PracticeMatchupButton onClick={() => {
              setPracticeScenario(scenario);
              setPracticePickMode(pickMode);
              setShowPracticeMatchup(true);
            }}>
              <span style={{ marginRight: '4px' }}>⚔️</span> Practice Match-up
            </PracticeMatchupButton>
          )}
          {(!showButtons || showButtons.clearAll) && (
            <ClearButton onClick={() => updateScenario(pickMode, scenario.id, { picks: Array(24).fill(null) })} style={{ padding: '4px 8px', fontSize: '11px', minWidth: '65px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              Clear All
            </ClearButton>
          )}
          {!showButtons && Array.isArray(scenario.counterConnectors) && scenario.counterConnectors.length > 0 && (
            <ClearButton
              onClick={() => updateScenario(pickMode, scenario.id, { counterConnectors: [] })}
              style={{ padding: '4px 8px', fontSize: '11px', minWidth: '95px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1b5e20', border: '1px solid #2e7d32' }}
              title="Remove all counter-relation arrows from this scenario"
            >
              Clear Relations
            </ClearButton>
          )}
        </div>
      )}

          </DraftBoardContainer>
        ))}
      </ScenarioBoardsRow>

      {/* Bottom button row — below draft boards */}
      {!isSimulatorView && !isMarketingDemo && !isEmbedView && !(isSharedView && sharedPermission === 'view') && !showButtons && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', flexWrap: 'wrap', padding: '12px 0' }}>
          {/* Duplicate toggle */}
          {UserTierService.hasFeatureAccess(userProfile, 'canSeeDuplicateToggle') && (
            <ToggleSwitchContainer>
              <ToggleSwitch style={{ minWidth: '140px', border: `2px solid ${preventDuplicates ? '#f44336' : '#666'}` }}>
                <ToggleOption
                  $active={preventDuplicates}
                  onClick={() => setPreventDuplicates(true)}
                  style={{ background: preventDuplicates ? '#f44336' : 'transparent', color: preventDuplicates ? '#fff' : '#aaa' }}
                >
                  Duplicate ✕
                </ToggleOption>
                <ToggleOption
                  $active={!preventDuplicates}
                  onClick={() => setPreventDuplicates(false)}
                  style={{ background: !preventDuplicates ? '#f44336' : 'transparent', color: !preventDuplicates ? '#fff' : '#aaa' }}
                >
                  Duplicate ✓
                </ToggleOption>
              </ToggleSwitch>
            </ToggleSwitchContainer>
          )}
          {/* Highlights toggle */}
          {UserTierService.hasFeatureAccess(userProfile, 'canSeeHighlightsToggle') && (
            <MobileHide>
              <ToggleSwitchContainer>
                <ToggleSwitch style={{ minWidth: '140px', border: `2px solid ${showOpenDotaHighlights ? '#4CAF50' : '#666'}` }}>
                  <ToggleOption
                    $active={showOpenDotaHighlights}
                    onClick={() => setShowOpenDotaHighlights(true)}
                    style={{ background: showOpenDotaHighlights ? '#4CAF50' : 'transparent', color: showOpenDotaHighlights ? '#fff' : '#aaa' }}
                  >
                    Highlights ✓
                  </ToggleOption>
                  <ToggleOption
                    $active={!showOpenDotaHighlights}
                    onClick={() => setShowOpenDotaHighlights(false)}
                    style={{ background: !showOpenDotaHighlights ? '#4CAF50' : 'transparent', color: !showOpenDotaHighlights ? '#fff' : '#aaa' }}
                  >
                    Highlights ✕
                  </ToggleOption>
                </ToggleSwitch>
              </ToggleSwitchContainer>
            </MobileHide>
          )}
          {/* Bracket drawing tool toggle (default off so gutters don't grab clicks) */}
          <ToggleSwitchContainer>
            <ToggleSwitch style={{ minWidth: '140px', border: `2px solid ${bracketToolEnabled ? '#ff7a59' : '#666'}` }}>
              <ToggleOption
                $active={bracketToolEnabled}
                onClick={() => setBracketToolEnabled(true)}
                style={{ background: bracketToolEnabled ? '#ff7a59' : 'transparent', color: bracketToolEnabled ? '#111' : '#aaa' }}
              >
                Bracket ✓
              </ToggleOption>
              <ToggleOption
                $active={!bracketToolEnabled}
                onClick={() => setBracketToolEnabled(false)}
                style={{ background: !bracketToolEnabled ? '#ff7a59' : 'transparent', color: !bracketToolEnabled ? '#111' : '#aaa' }}
              >
                Bracket ✕
              </ToggleOption>
            </ToggleSwitch>
          </ToggleSwitchContainer>
          {/* Copy From pick mode */}
          {UserTierService.hasFeatureAccess(userProfile, 'canSeeCopyFromPickMode') && (
            <AddScenarioButton onClick={handleCopyFromClick} style={{ background: '#4caf50', borderColor: '#4caf50' }}>
              <span style={{ fontSize: '18px', marginRight: '5px' }}>📑</span> Copy From {pickMode === 'first' ? '2nd' : '1st'} Pick
            </AddScenarioButton>
          )}
          {/* Show Both */}
          {UserTierService.hasFeatureAccess(userProfile, 'canSeeCopyFromPickMode') && (
            <MobileHide>
              <AddScenarioButton onClick={() => { setShowBothOverlay(true); setShowPracticeMatchup(false); }} style={{ background: '#7b1fa2', borderColor: '#7b1fa2' }}>
                <span style={{ fontSize: '18px', marginRight: '5px' }}>👁</span> Show Both
              </AddScenarioButton>
            </MobileHide>
          )}
          {/* Share Draft */}
          {UserTierService.hasFeatureAccess(userProfile, 'canSeeShareDraft') && (
            <ShareDraftButton onClick={handleOpenShareDialog}>
              <span style={{ fontSize: '18px', marginRight: '5px' }}>🔗</span> Share Draft
            </ShareDraftButton>
          )}
          {/* See Matchups */}
          {UserTierService.hasFeatureAccess(userProfile, 'canSeeSeeMatchups') && (
            <SeeMatchupsButton onClick={() => setShowAllMatchups(true)}>
              <span style={{ fontSize: '18px', marginRight: '5px' }}>📊</span> See Matchups
            </SeeMatchupsButton>
          )}
        </div>
      )}

      {/* Mobile landscape view toggle — only visible in landscape */}
      <MobileLandscapeToggleBar>
        <MobileLandscapeToggleSwitch>
          <MobileLandscapeToggleOption
            $active={mobileLandscapeView === 'drafter'}
            onClick={() => setMobileLandscapeView('drafter')}
          >
            Drafter POV
          </MobileLandscapeToggleOption>
          <MobileLandscapeToggleOption
            $active={mobileLandscapeView === 'board'}
            onClick={() => setMobileLandscapeView('board')}
          >
            Draft Board
          </MobileLandscapeToggleOption>
        </MobileLandscapeToggleSwitch>
      </MobileLandscapeToggleBar>

      {/* Drafter POV — bans on top, picks as 2 vertical columns, one card = 50vw */}
      <MobileDrafterOuter $active={mobileLandscapeView === 'drafter'}>
        <MobileCarouselScroll>
          {scenarios.map((scenario, sIdx) => {
            const swapped = scenario.swapSides || false;
            // Radiant bans/picks | Dire bans/picks
            const radiantBans  = game.board.radiantBans;
            const radiantPicks = game.board.radiantPicks;
            const direBans     = game.board.direBans;
            const direPicks    = game.board.direPicks;
            const usBans    = swapped ? direBans    : radiantBans;
            const usPicks   = swapped ? direPicks   : radiantPicks;
            const enemyBans = swapped ? radiantBans : direBans;
            const enemyPicks= swapped ? radiantPicks: direPicks;
            const usTeam    = swapped ? 'Dire' : 'Radiant';
            const enemyTeam = swapped ? 'Radiant' : 'Dire';
            const borderColor = swapped ? 'rgba(255,107,107,0.6)' : 'rgba(146,255,146,0.6)';

            return (
              <MobileDrafterCard key={`drafter_${scenario.id}`} $borderColor={borderColor} $swapped={swapped}>
                <MobileBoardHeader>
                  <div style={{ color: '#ccc', fontSize: '10px', fontWeight: 'bold', zIndex: 1 }}>
                    {pickMode === 'first' ? '1st Pick' : '2nd Pick'} #{sIdx + 1}
                    {scenario.title ? ` — ${scenario.title}` : ''}
                  </div>
                  <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap', alignItems: 'center', zIndex: 1 }}>
                    {UserTierService.hasFeatureAccess(userProfile, 'canDuplicate') && (
                      <ClearButton
                        onClick={() => duplicateScenario(pickMode, scenario.id)}
                        style={{ padding: '2px 5px', fontSize: '9px', minWidth: 'unset' }}
                      >📋</ClearButton>
                    )}
                    {!isEmbedView && UserTierService.hasFeatureAccess(userProfile, 'canSeeCopyToPickMode') && (
                      <ClearButton
                        onClick={() => handleCopyToOtherMode(scenario, pickMode)}
                        style={{ padding: '2px 5px', fontSize: '9px', minWidth: 'unset' }}
                      >📑{pickMode === 'first' ? '→2nd' : '→1st'}</ClearButton>
                    )}
                    <ClearButton
                      onClick={() => updateScenario(pickMode, scenario.id, { picks: Array(24).fill(null) })}
                      style={{ padding: '2px 5px', fontSize: '9px', minWidth: 'unset' }}
                    >Clear</ClearButton>
                    <ClearButton
                      onClick={() => handleSwapPulledBoard(scenario)}
                      style={{
                        padding: '2px 5px', fontSize: '11px', minWidth: 'unset',
                        background: swapped ? '#2196F3' : '#333',
                        border: swapped ? '1px solid #2196F3' : '1px solid #555',
                      }}
                    >⇄</ClearButton>
                  </div>
                </MobileBoardHeader>

                {/* Bans row: Us | Enemy */}
                <MobileBansSection>
                  <MobileBansGroup>
                    {usBans.map(pos => renderPickBox(sIdx, pos, true))}
                  </MobileBansGroup>
                  <MobileBansGroup>
                    {enemyBans.map(pos => renderPickBox(sIdx, pos, true))}
                  </MobileBansGroup>
                </MobileBansSection>

                {/* Team labels */}
                <MobileTeamsLabel>
                  <TeamHeader $team={usTeam} style={{ fontSize: '11px', width: 'auto', padding: '0 16px' }}>Us</TeamHeader>
                  <TeamHeader $team={enemyTeam} style={{ fontSize: '11px', width: 'auto', padding: '0 16px' }}>Enemy</TeamHeader>
                </MobileTeamsLabel>

                {/* Picks: 2 vertical columns */}
                <MobilePicksSection>
                  <MobilePickColumn>
                    {usPicks.map(pos => renderPickBox(sIdx, pos, true))}
                  </MobilePickColumn>
                  <MobilePickColumn>
                    {enemyPicks.map(pos => renderPickBox(sIdx, pos, true))}
                  </MobilePickColumn>
                </MobilePicksSection>
              </MobileDrafterCard>
            );
          })}
        </MobileCarouselScroll>
      </MobileDrafterOuter>

      {/* Draft Board View — compact 2-col Radiant/Dire layout, 4 per page (2×2) */}
      <MobileCarouselOuter $active={mobileLandscapeView === 'board'}>
        <MobileCarouselScroll>
          {(() => {
            const renderBoardCard = (scenario, sIdx, solo = false) => {
              const swapped = scenario.swapSides || false;
              const borderColor = swapped ? 'rgba(255,107,107,0.5)' : 'rgba(146,255,146,0.5)';
              const leftTeam  = swapped ? 'Dire' : 'Radiant';
              const rightTeam = swapped ? 'Radiant' : 'Dire';

              return (
                <MobileBoardCard key={`board_${scenario.id}`} $borderColor={borderColor} $solo={solo}>
                  <MobileBoardHeader>
                    <div style={{ color: '#ccc', fontSize: '9px', fontWeight: 'bold' }}>
                      {pickMode === 'first' ? '1st' : '2nd'} #{sIdx + 1}
                      {scenario.title ? ` ${scenario.title}` : ''}
                    </div>
                    <div style={{ display: 'flex', gap: '2px', alignItems: 'center', flexWrap: 'wrap' }}>
                      {UserTierService.hasFeatureAccess(userProfile, 'canDuplicate') && (
                        <ClearButton
                          onClick={() => duplicateScenario(pickMode, scenario.id)}
                          style={{ padding: '1px 4px', fontSize: '8px', minWidth: 'unset' }}
                        >📋</ClearButton>
                      )}
                      {!isEmbedView && UserTierService.hasFeatureAccess(userProfile, 'canSeeCopyToPickMode') && (
                        <ClearButton
                          onClick={() => handleCopyToOtherMode(scenario, pickMode)}
                          style={{ padding: '1px 4px', fontSize: '8px', minWidth: 'unset' }}
                        >📑{pickMode === 'first' ? '→2' : '→1'}</ClearButton>
                      )}
                      <ClearButton
                        onClick={() => updateScenario(pickMode, scenario.id, { picks: Array(24).fill(null) })}
                        style={{ padding: '1px 4px', fontSize: '8px', minWidth: 'unset' }}
                      >Clear</ClearButton>
                      <ClearButton
                        onClick={() => handleSwapPulledBoard(scenario)}
                        style={{
                          padding: '1px 4px', fontSize: '10px', minWidth: 'unset',
                          background: swapped ? '#2196F3' : '#333',
                          border: swapped ? '1px solid #2196F3' : '1px solid #555',
                        }}
                      >⇄</ClearButton>
                    </div>
                  </MobileBoardHeader>
                  <MobileDraftGrid>
                    <MobileSideSection>
                      <MobileSideLabel $team={leftTeam}>{leftTeam}</MobileSideLabel>
                      {/* Bans row */}
                      <MobileHeroRow>
                        {(swapped ? game.board.direBans : game.board.radiantBans).map(pos => renderPickBox(sIdx, pos, true))}
                      </MobileHeroRow>
                      {/* Picks row */}
                      <MobileHeroRow>
                        {(swapped ? game.board.direPicks : game.board.radiantPicks).map(pos => renderPickBox(sIdx, pos, true))}
                      </MobileHeroRow>
                    </MobileSideSection>
                    <MobileSideDivider />
                    <MobileSideSection>
                      <MobileSideLabel $team={rightTeam}>{rightTeam}</MobileSideLabel>
                      {/* Bans row */}
                      <MobileHeroRow>
                        {(swapped ? game.board.radiantBans : game.board.direBans).map(pos => renderPickBox(sIdx, pos, true))}
                      </MobileHeroRow>
                      {/* Picks row */}
                      <MobileHeroRow>
                        {(swapped ? game.board.radiantPicks : game.board.direPicks).map(pos => renderPickBox(sIdx, pos, true))}
                      </MobileHeroRow>
                    </MobileSideSection>
                  </MobileDraftGrid>
                </MobileBoardCard>
              );
            };

            const pages = [];
            for (let pageIdx = 0; pageIdx * 4 < scenarios.length; pageIdx++) {
              const pageItems = scenarios.slice(pageIdx * 4, pageIdx * 4 + 4);
              pages.push(
                <MobilePage key={`page_${pageIdx}`}>
                  {pageItems.map((scenario, i) => renderBoardCard(scenario, pageIdx * 4 + i, false))}
                  {pageItems.length < 4 && Array(4 - pageItems.length).fill(null).map((_, i) => (
                    <div key={`empty_${i}`} />
                  ))}
                </MobilePage>
              );
            }
            return pages;
          })()}
        </MobileCarouselScroll>
      </MobileCarouselOuter>

      {/* Hero selection popup as centered overlay */}
      {showPopup && selectedBox !== -1 && (
        <>
          {/* Semi-transparent backdrop */}
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 999
            }}
            onClick={() => setShowPopup(false)}
          />
          
          <HeroSelectPopup ref={popupRef}>
            <div style={{ 
              padding: '8px 16px',
              borderBottom: '1px solid #444',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ fontWeight: 'bold', color: '#eee' }}>
                {isForeheadMode
                  ? `Select Hero for Header Slot ${selectedBox}`
                  : `Select Hero for Position ${selectedBox} (Scenario #${activeScenarioIndex + 1}, ID: ${activeScenario?.id})`}
              </div>
              <div 
                style={{ 
                  cursor: 'pointer', 
                  fontSize: '20px',
                  color: '#888',
                  padding: '0 8px'
                }}
                onClick={() => setShowPopup(false)}
              >
                ×
              </div>
            </div>
            
            <div style={{ position: 'relative' }}>
              <SearchInput
                ref={searchInputRef}
                type="text"
                placeholder="Search heroes... (Enter: select first, ESC: close)"
                defaultValue=""
                onInput={(e) => {
                  const newValue = e.target.value;
                  console.log('🔤 SearchInput onInput:', {
                    value: newValue,
                    prevHeroSearch: heroSearch
                  });
                  setHeroSearch(newValue);
                }}
                onKeyDown={handleSearchKeyDown}
                autoFocus
              />
              <SearchIcon>🔍</SearchIcon>
            </div>
            
            <HeroGrid>
              {filteredHeroes.map((hero, index) => (
                <HeroOption
                  key={hero.id}
                  ref={el => heroRefs.current[hero.id] = el}
                  onClick={() => handleHeroSelect(hero)}
                  style={{
                    outline: focusedHeroIndex === index ? '3px solid #667eea' : 'none',
                    outlineOffset: '2px',
                    backgroundColor: focusedHeroIndex === index ? 'rgba(102, 126, 234, 0.2)' : 'transparent'
                  }}
                >
                  <HeroImage hero={hero} size="small" />
                  <div className="hero-name">{hero.name}</div>
                </HeroOption>
              ))}
              {filteredHeroes.length === 0 && (
                <div style={{ gridColumn: 'span 4', textAlign: 'center', padding: '20px', color: '#888' }}>
                  No heroes match your search
                </div>
              )}
            </HeroGrid>
            
            {((isForeheadMode && foreheadPicks[selectedBox - 1]?.hero) ||
              (!isForeheadMode && activeScenario && activeScenario.picks && activeScenario.picks[selectedBox - 1]?.hero)) && (
              <PopupControls>
                <RemoveButton onClick={removeHero}>
                  Remove Hero
                </RemoveButton>
              </PopupControls>
            )}
          </HeroSelectPopup>
        </>
      )}

      {/* Drag and drop overlay */}
      <DragOverlay $show={isDragOver}>
        <DragOverlayContent>
          <DragOverlayIcon>📄</DragOverlayIcon>
          <div>Drop to import draft</div>
        </DragOverlayContent>
      </DragOverlay>

      {/* Confirmation dialog for unsaved changes */}
      <ConfirmationDialog
        isOpen={showConfirmation}
        title="Unsaved Changes"
        message="You have unsaved changes. Would you like to save them before continuing?"
        onSave={handleConfirmSave}
        onDiscard={handleConfirmDiscard}
        onCancel={handleConfirmCancel}
        saveButtonText="Save & Continue"
        discardButtonText="Discard Changes"
        cancelButtonText="Cancel"
      />

      {/* Merge/Replace dialog for file imports */}
      <MergeReplaceDialog
        open={showMergeReplaceDialog}
        onClose={handleMergeReplaceCancel}
        onMerge={handleMergeDrop}
        onReplace={handleReplaceDrop}
        fileName={pendingFileImport?.file?.name || 'draft.json'}
        isForeheadOnly={pendingFileImport?.isForeheadOnly || false}
      />

      {/* Selection box for multi-select */}
      {isSelecting && (
        <SelectionBox
          style={{
            left: `${Math.min(selectionStart.x, selectionEnd.x)}px`,
            top: `${Math.min(selectionStart.y, selectionEnd.y)}px`,
            width: `${Math.abs(selectionEnd.x - selectionStart.x)}px`,
            height: `${Math.abs(selectionEnd.y - selectionStart.y)}px`,
          }}
        />
      )}

      {/* Canvas ripple effect on drop */}
      <CanvasRipple
        $show={ripplePosition.show}
        $x={ripplePosition.x}
        $y={ripplePosition.y}
      />

      {/* Scaling Features Dialogs */}
      <ShareLinkManager
        open={showShareDialog}
        onClose={() => setShowShareDialog(false)}
        draftId={currentDraftId}
        draftTitle={teamName || 'Untitled Draft'}
        onSaveDraft={handleSaveDraft}
      />

      {/* Practice Matchup Overlay */}
      <PracticeMatchup
        isOpen={showPracticeMatchup}
        onClose={() => {
          setShowPracticeMatchup(false);
          setPracticeScenario(null);
          setPracticePickMode(null);
        }}
        scenario={practiceScenario}
        pickMode={practicePickMode}
      />

      <AllMatchupsView
        isOpen={showAllMatchups}
        onClose={() => setShowAllMatchups(false)}
        scenariosFirstPick={scenariosFirstPick}
        scenariosSecondPick={scenariosSecondPick}
        updateScenario={updateScenario}
      />

      {/* Show Both Overlay */}
      {showBothOverlay && (
        <ScenarioSelectionOverlay onClick={() => setShowBothOverlay(false)} onWheel={(e) => e.stopPropagation()}>
          <ScenarioSelectionCloseButton onClick={() => setShowBothOverlay(false)}>✕</ScenarioSelectionCloseButton>
          <ScenarioSelectionHeader onClick={(e) => e.stopPropagation()}>
            <ScenarioSelectionTitle style={{ color: '#ce93d8' }}>All Scenarios — Both Pick Modes</ScenarioSelectionTitle>
            <ScenarioSelectionSubtitle>Press Esc or click outside to close</ScenarioSelectionSubtitle>
          </ScenarioSelectionHeader>

          <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
            {[['1st Pick', scenariosFirstPick, '#1de9b6'], ['2nd Pick', scenariosSecondPick, '#61dafb']].map(([label, scenarios, color]) => (
              <div key={label} style={{ flex: 1, minWidth: 0 }}>
                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                  <span style={{ color, fontSize: 20, fontWeight: 'bold', borderBottom: `2px solid ${color}`, paddingBottom: 4 }}>{label}</span>
                  <span style={{ color: '#888', fontSize: 14, marginLeft: 12 }}>{scenarios.length} scenario{scenarios.length !== 1 ? 's' : ''}</span>
                </div>
                {scenarios.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#555', fontSize: 14 }}>No scenarios yet.</div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'nowrap', gap: 16, overflowX: 'auto', overflowY: 'hidden', paddingBottom: 12, alignItems: 'flex-start' }}>
                    {scenarios.map((scenario, idx) => (
                      <DraftBoardContainer
                        key={scenario.id}
                        $swapSides={scenario.swapSides || false}
                        style={{ minWidth: 280, maxWidth: 300, flexShrink: 0, border: `3px solid ${color}`, cursor: 'default' }}
                        $fromMatchPull={scenario.fromMatchPull}
                        $matchColorIndex={scenario.matchColorIndex}
                      >
                        <ScenarioInput type="text" placeholder="Enter scenario name..." value={scenario.title || ''} readOnly style={{ pointerEvents: 'none' }} />
                        <ScenarioTitle>
                          <span style={{ color }}>{label}</span> Scenario {idx + 1}
                        </ScenarioTitle>
                        <TeamsContainer>
                          {!(scenario.swapSides || false) ? (
                            <>
                              <TeamHeader $team="Radiant">Radiant</TeamHeader>
                              <TeamHeader $team="Dire">Dire</TeamHeader>
                            </>
                          ) : (
                            <>
                              <TeamHeader $team="Dire">Dire</TeamHeader>
                              <TeamHeader $team="Radiant">Radiant</TeamHeader>
                            </>
                          )}
                        </TeamsContainer>
                        <PicksContainer>
                          {!(scenario.swapSides || false) ? (
                            <>
                              <TeamColumn $team="Radiant" $swapped={false}>
                                {[1, 4, 7, 8, 10, 11, 14, 15, 18, 19, 22, 23].map(position => {
                                  const pick = scenario.picks?.[position - 1];
                                  const isBanPosition = banPositions.includes(position);
                                  return (
                                    <PickBox key={position} $position={position} $isRadiant={true} style={{ pointerEvents: 'none' }}>
                                      <PositionLabel $isBan={isBanPosition}>{position}</PositionLabel>
                                      {pick?.hero ? <HeroContainer><HeroImage hero={pick.hero} $grayscale={isBanPosition} /></HeroContainer> : null}
                                      {isBanPosition && <BanCross>✕</BanCross>}
                                    </PickBox>
                                  );
                                })}
                              </TeamColumn>
                              <TeamColumn $team="Dire" $swapped={false}>
                                {[2, 3, 5, 6, 9, 12, 13, 16, 17, 20, 21, 24].map(position => {
                                  const pick = scenario.picks?.[position - 1];
                                  const isBanPosition = banPositions.includes(position);
                                  return (
                                    <PickBox key={position} $position={position} $isRadiant={false} style={{ pointerEvents: 'none' }}>
                                      <PositionLabel $isBan={isBanPosition}>{position}</PositionLabel>
                                      {pick?.hero ? <HeroContainer><HeroImage hero={pick.hero} $grayscale={isBanPosition} /></HeroContainer> : null}
                                      {isBanPosition && <BanCross>✕</BanCross>}
                                    </PickBox>
                                  );
                                })}
                              </TeamColumn>
                            </>
                          ) : (
                            <>
                              <TeamColumn $team="Dire" $swapped={true}>
                                {[2, 3, 5, 6, 9, 12, 13, 16, 17, 20, 21, 24].map(position => {
                                  const pick = scenario.picks?.[position - 1];
                                  const isBanPosition = banPositions.includes(position);
                                  return (
                                    <PickBox key={position} $position={position} $isRadiant={false} style={{ pointerEvents: 'none' }}>
                                      <PositionLabel $isBan={isBanPosition}>{position}</PositionLabel>
                                      {pick?.hero ? <HeroContainer><HeroImage hero={pick.hero} $grayscale={isBanPosition} /></HeroContainer> : null}
                                      {isBanPosition && <BanCross>✕</BanCross>}
                                    </PickBox>
                                  );
                                })}
                              </TeamColumn>
                              <TeamColumn $team="Radiant" $swapped={true}>
                                {[1, 4, 7, 8, 10, 11, 14, 15, 18, 19, 22, 23].map(position => {
                                  const pick = scenario.picks?.[position - 1];
                                  const isBanPosition = banPositions.includes(position);
                                  return (
                                    <PickBox key={position} $position={position} $isRadiant={true} style={{ pointerEvents: 'none' }}>
                                      <PositionLabel $isBan={isBanPosition}>{position}</PositionLabel>
                                      {pick?.hero ? <HeroContainer><HeroImage hero={pick.hero} $grayscale={isBanPosition} /></HeroContainer> : null}
                                      {isBanPosition && <BanCross>✕</BanCross>}
                                    </PickBox>
                                  );
                                })}
                              </TeamColumn>
                            </>
                          )}
                        </PicksContainer>
                      </DraftBoardContainer>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </ScenarioSelectionOverlay>
      )}

      {/* Scenario Selection Overlay */}
      {showScenarioSelection && (
        <ScenarioSelectionOverlay onClick={handleCancelScenarioSelection}>
          <ScenarioSelectionCloseButton onClick={handleCancelScenarioSelection}>
            ✕
          </ScenarioSelectionCloseButton>
          <ScenarioSelectionHeader onClick={(e) => e.stopPropagation()}>
            <ScenarioSelectionTitle>
              Select Scenario to Copy From
            </ScenarioSelectionTitle>
            <ScenarioSelectionSubtitle>
              Click on a scenario from {pickMode === 'first' ? '2nd Pick' : '1st Pick'} to copy its heroes
            </ScenarioSelectionSubtitle>
          </ScenarioSelectionHeader>
          <ScenarioSelectionGrid onClick={(e) => e.stopPropagation()}>
            {(pickMode === 'first' ? scenariosSecondPick : scenariosFirstPick).map((sourceScenario, idx) => (
              <DraftBoardContainer
                key={sourceScenario.id}
                $swapSides={sourceScenario.swapSides || false}
                style={{
                  cursor: 'pointer',
                  minWidth: 280,
                  maxWidth: 300,
                  border: '3px solid #4caf50'
                }}
                onClick={() => handleSelectSourceScenario(sourceScenario, pickMode === 'first' ? 'second' : 'first')}
                $fromMatchPull={sourceScenario.fromMatchPull}
                $matchColorIndex={sourceScenario.matchColorIndex}
              >
                <ScenarioInput
                  type="text"
                  placeholder="Enter scenario name..."
                  value={sourceScenario.title || ''}
                  readOnly
                  style={{ pointerEvents: 'none' }}
                />
                <ScenarioTitle>
                  {pickMode === 'first' ?
                    <span style={{ color: '#61dafb' }}>2nd Pick</span> :
                    <span style={{ color: '#1de9b6' }}>1st Pick</span>} Scenario {idx + 1}
                </ScenarioTitle>

                <TeamsContainer>
                  {!(sourceScenario.swapSides || false) ? (
                    <>
                      <TeamHeader $team="Radiant">Radiant</TeamHeader>
                      <TeamHeader $team="Dire">Dire</TeamHeader>
                    </>
                  ) : (
                    <>
                      <TeamHeader $team="Dire">Dire</TeamHeader>
                      <TeamHeader $team="Radiant">Radiant</TeamHeader>
                    </>
                  )}
                </TeamsContainer>

                <PicksContainer>
                  {!(sourceScenario.swapSides || false) ? (
                    <>
                      <TeamColumn $team="Radiant" $swapped={false}>
                        {[1, 4, 7, 8, 10, 11, 14, 15, 18, 19, 22, 23].map(position => {
                          const pick = sourceScenario.picks?.[position - 1];
                          const isBanPosition = banPositions.includes(position);
                          return (
                            <PickBox
                              key={position}
                              $position={position}
                              $isRadiant={true}
                              style={{ pointerEvents: 'none' }}
                            >
                              <PositionLabel $isBan={isBanPosition}>{position}</PositionLabel>
                              {pick?.hero ? (
                                <HeroContainer>
                                  <HeroImage hero={pick.hero} $grayscale={isBanPosition} />
                                </HeroContainer>
                              ) : null}
                              {isBanPosition && <BanCross>✕</BanCross>}
                            </PickBox>
                          );
                        })}
                      </TeamColumn>

                      <TeamColumn $team="Dire" $swapped={false}>
                        {[2, 3, 5, 6, 9, 12, 13, 16, 17, 20, 21, 24].map(position => {
                          const pick = sourceScenario.picks?.[position - 1];
                          const isBanPosition = banPositions.includes(position);
                          return (
                            <PickBox
                              key={position}
                              $position={position}
                              $isRadiant={false}
                              style={{ pointerEvents: 'none' }}
                            >
                              <PositionLabel $isBan={isBanPosition}>{position}</PositionLabel>
                              {pick?.hero ? (
                                <HeroContainer>
                                  <HeroImage hero={pick.hero} $grayscale={isBanPosition} />
                                </HeroContainer>
                              ) : null}
                              {isBanPosition && <BanCross>✕</BanCross>}
                            </PickBox>
                          );
                        })}
                      </TeamColumn>
                    </>
                  ) : (
                    <>
                      <TeamColumn $team="Dire" $swapped={true}>
                        {[2, 3, 5, 6, 9, 12, 13, 16, 17, 20, 21, 24].map(position => {
                          const pick = sourceScenario.picks?.[position - 1];
                          const isBanPosition = banPositions.includes(position);
                          return (
                            <PickBox
                              key={position}
                              $position={position}
                              $isRadiant={false}
                              style={{ pointerEvents: 'none' }}
                            >
                              <PositionLabel $isBan={isBanPosition}>{position}</PositionLabel>
                              {pick?.hero ? (
                                <HeroContainer>
                                  <HeroImage hero={pick.hero} $grayscale={isBanPosition} />
                                </HeroContainer>
                              ) : null}
                              {isBanPosition && <BanCross>✕</BanCross>}
                            </PickBox>
                          );
                        })}
                      </TeamColumn>

                      <TeamColumn $team="Radiant" $swapped={true}>
                        {[1, 4, 7, 8, 10, 11, 14, 15, 18, 19, 22, 23].map(position => {
                          const pick = sourceScenario.picks?.[position - 1];
                          const isBanPosition = banPositions.includes(position);
                          return (
                            <PickBox
                              key={position}
                              $position={position}
                              $isRadiant={true}
                              style={{ pointerEvents: 'none' }}
                            >
                              <PositionLabel $isBan={isBanPosition}>{position}</PositionLabel>
                              {pick?.hero ? (
                                <HeroContainer>
                                  <HeroImage hero={pick.hero} $grayscale={isBanPosition} />
                                </HeroContainer>
                              ) : null}
                              {isBanPosition && <BanCross>✕</BanCross>}
                            </PickBox>
                          );
                        })}
                      </TeamColumn>
                    </>
                  )}
                </PicksContainer>
              </DraftBoardContainer>
            ))}
          </ScenarioSelectionGrid>
        </ScenarioSelectionOverlay>
      )}

      {saveToast && (
        <SaveToast $type={saveToast.type}>{saveToast.message}</SaveToast>
      )}
    </MainContainer>
  );
};

// Add a simple Button component
const Button = styled.button`
  cursor: pointer;
  font-weight: bold;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.4);
  }

  &:active {
    transform: translateY(1px);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
  }
`;

export default DraftBoard; 